import React, { useEffect, useMemo, useRef, useState } from "react";
import { track as metaTrack } from "../lib/metaPixel";
import { Link } from "react-router-dom";
import api, { API, FRONTEND_BUILD_ID } from "../lib/api";
import { getToken, setToken, hasToken } from "../lib/nativeTokenStorage";
import { credentialsMode, webOnlyWithCredentials, isNative } from "../lib/nativePlatform";
import { startGoogleLogin } from "../lib/googleAuth";
import { beginPlusPurchase } from "../lib/beginPlusPurchase";
import NativePurchaseCta from "../components/NativePurchaseCta";
import { Send, ArrowLeft, Video, Loader2, Sparkles, X, Search, ChevronDown, Check, Menu, Brain } from "lucide-react";
import useMemoryHasNew from "@/lib/useMemoryHasNew";
import ConversationsSidebar from "../components/ConversationsSidebar";
import CoachRecommendationCard from "../components/CoachRecommendationCard";
import CricketGraphCard from "../components/CricketGraphCard";
import { fetchCricketGraphState } from "../lib/cricketGraph";
import CricketMemoryConfirmationCard from "../components/CricketMemoryConfirmationCard";
import SessionMemoryCard from "../components/SessionMemoryCard";
import VisualExplanation from "../components/VisualExplanation";
import InAppBrowserBanner from "../components/InAppBrowserBanner";
import ConnectCricketCard from "../components/ConnectCricketCard";
import YourCricketPanel from "../components/YourCricketPanel";
import MyCricketPanel from "../components/MyCricketPanel";
import PerformanceConfirmCard from "../components/PerformanceConfirmCard";
import { WelcomeBackReveal, trackCtaClick } from "../components/PlusConversionSurfaces";
import { fetchConnection, isSessionDismissed } from "../lib/connectCricket";
import { looksLikeSpecificCricketProblem } from "../lib/cricketIntent";
import { fetchRecordSummary } from "../lib/connectCricket";
import { fetchPendingPerformances } from "../lib/performanceCapture";
import {
  logCoachingStarted, logMeaningfulCoachingCompleted,
  logVideoReviewStarted, logVideoReviewCompleted,
} from "../lib/iosMetaEvents";
import { ReportAIResponseTrigger } from "../components/ReportAIResponse";
import { postClientEvent, postClientEventAwait, getClientEnv } from "../lib/clientEnv";
import { getBrowserId } from "../lib/browserId";
import { toast } from "sonner";
import InstallBanner from "../components/InstallBanner";
import ConsentBar from "../components/ConsentBar";
import TrialBanner from "../components/TrialBanner";
import MediaConsentModal from "../components/MediaConsentModal";
import AgeGateModal from "../components/AgeGateModal";
import PaywallModal from "../components/PaywallModal";
import ContinuityPrompt from "../components/ContinuityPrompt";
import OnboardingCard from "../components/OnboardingCard";
import AuthedHomeHero from "../components/AuthedHomeHero";
// Iter XX.285h — ClaimYourCricketBanner for anonymous users with
// stored cricket state. Lightweight, dismissible, non-interrupting.
import ClaimYourCricketBanner from "../components/ClaimYourCricketBanner";
import MobileBottomNav from "../components/MobileBottomNav";
import MoreSheet from "../components/MoreSheet";
// Iter XX.267b — subtle native haptics. Medium impact fires on the
// canonical video-upload path (pickVideo) so pressing Upload feels
// intentional across every entry point (composer button, hero card,
// legacy pill). All calls are fire-and-forget and no-op on web.
import { hapticError, hapticLight, hapticMedium, hapticSuccess } from "../lib/haptics";
import { useAuth } from "../lib/auth";
import { computeHomeSurface } from "../lib/homeSurface";
import SeasonCard from "../components/SeasonCard";
import CricketJourneyGate from "../components/CricketJourneyGate";
import CricketJourneyStarted from "../components/CricketJourneyStarted";
import VoiceComposer from "../components/VoiceComposer";
import LiveVoiceOverlay from "../components/LiveVoiceOverlay";
import VideoReviewCard from "../components/VideoReviewCard";
import ConversationOfferModal from "../components/ConversationOfferModal";
import ScorecardFlow from "../components/ScorecardFlow";
import { track as clarityTrack } from "../lib/clarity";
import { trackOnce as clarityTrackOnce } from "../lib/claritySession";
import {
  useReveal, onScorecardConfirmed, applyServerReveals, hydrateFromServer,
} from "../lib/progressiveReveal";
import AnonymousIdentityClaimModal from "../components/AnonymousIdentityClaimModal";
import { PracticeStarter } from "../components/PracticeStarter";
import { trackVoice } from "../lib/voice";
import { useFlag } from "../lib/featureFlags";
import PaidInstagramPersonaliseGate from "../components/PaidInstagramPersonaliseGate";
import SecondOpenCard from "../components/SecondOpenCard";
import MetaConsentBar from "../components/MetaConsentBar";
import {
  shouldShowMetaConsentBar, grantMetaConsent, denyMetaConsent, readMetaConsent,
  evaluateMetaConsentBarEligibility,
} from "../lib/metaConsent";
import { trackFunnel } from "../lib/activationFunnel";
import { buildActiveMission } from "../lib/missionPayload";
import ReportCapture from "../components/ReportCapture";
import { trackMetaFunnelEvent } from "../lib/metaFunnelEvents";
import {
  shouldShowPaidInstagramGate,
  consumeStashedPersonaContext,
} from "../lib/paidInstagramGate";
import ParentEntryGate from "../components/ParentEntryGate";
import {
  shouldShowParentEntryGate,
  consumeStashedParentContext,
  consumePendingIntent,
  peekPendingIntent,
} from "../lib/parentEntryGate";
import QuestionEngineCard from "../components/QuestionEngineCard";
import { shouldShowPce, markPceDone } from "../lib/progressiveCommitmentEngine";
import {
  PlusLaunchBanner,
  PostReportUpgradeCard,
  usePlusUpgradeSurfaces,
} from "../components/PlusUpgradeSurfaces";
import {
  COUNTRY_BY_CODE,
  ORDERED_COUNTRIES,
  searchCountries,
  guessDefaultCountry,
  phoneMetaFor,
} from "../lib/countries";

const STORAGE_KEY = "gully_web_session_id";
const UTM_STORAGE_KEY = "gully_utm";
// Iter XX.33 — canonical pending-upload registry. Keyed by
// upload_trace_id so the same physical upload survives page refresh,
// tab close, session upgrade, and identity clarification.
const PENDING_UPLOADS_KEY = "gully_pending_uploads";

// Iter XX.58 — Module-scoped mission-complete opener. Set on mount when
// the URL contains `?mission_complete=1&opener=...`, consumed (read but
// not cleared) by both `startSession()` and `hydrate()` so React 18's
// StrictMode double-mount cannot cause the opener to disappear. Cleared
// the first time the player sends a real message, i.e. it has served
// its purpose.
let _pendingMissionOpener = null;

// Iter XX.90 — Goal-oriented default opener. Fallback used only when
// /api/web/history AND /api/web/session-reserve both fail to return an
// opening_message (extremely rare — offline / cold start). Kept in
// lockstep with backend/web_chat.py to avoid mismatched fresh-thread
// copy between server-first render and client fallback.
const DEFAULT_OPENING_MESSAGE =
  "Before your next match… what would you most like to improve?";

// B+D simplification (2026-10-02) — single quiet composer placeholder.
// Replaces the rotating XX.90 examples. The input itself teaches how
// to use Gully; no examples needed.
const COMPOSER_PLACEHOLDERS = [
  "What\u2019s happening in your cricket?",
];
const COMPOSER_PLACEHOLDER_INTERVAL_MS = 3500;

// Iter XX.92 — Scripted intro sequence. Gully initiates the conversation
// with a four-line intro (~2.7s total) that reinforces our positioning:
// we own MEMORY of the player's journey, not just answers. Runs ONCE
// per BROWSER — completion persists in localStorage under
// `g_intro_v1_shown` so returning users (whose anonymous chat history
// may not always re-hydrate) never see a canned intro again. Any
// hydrated history / mission-complete opener / onboarding
// interstitial suppresses the intro entirely. User typing OR chip
// tap cancels the sequence immediately — messages already shown
// stay, remaining ones are dropped, and their input flows through
// normally. See the intro effect near mount for scheduling; render
// happens alongside `messages` so the (append-only) audit log never
// records the scripted lines as real assistant turns (they live in
// a separate `introMessages` state).
// Iter XX.92 → XX.106 — Scripted intro sequence retired.
// The pre-chat "I listen. I remember. I coach." explanation screen
// was proven to be pure friction between landing and the first real
// coaching moment. XX.106 removes it entirely; the identity screen
// on the ParentEntryGate now carries the ONE moment of intent
// (recognition, not explanation), and chat opens straight into the
// backend `opening_message` coaching opener. The intro sequencer
// below is kept as dormant scaffolding so a future scripted-intro
// experiment can reuse it; when INTRO_SCRIPT is empty the entire
// sequencer short-circuits (see the intro effect near mount).
const INTRO_SHOWN_KEY = "g_intro_v1_shown";
const INTRO_SCRIPT = [];
const INTRO_CADENCE = [];

function _readPendingUploads() {
  try {
    const raw = localStorage.getItem(PENDING_UPLOADS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch { return {}; }
}
function _writePendingUploads(obj) {
  try {
    localStorage.setItem(PENDING_UPLOADS_KEY, JSON.stringify(obj || {}));
  } catch { /* private mode / quota — non-fatal */ }
}
function _savePendingUpload(record) {
  if (!record || !record.upload_trace_id) return;
  const all = _readPendingUploads();
  all[record.upload_trace_id] = { ...(all[record.upload_trace_id] || {}), ...record };
  _writePendingUploads(all);
}
function _patchPendingUpload(traceId, patch) {
  if (!traceId) return;
  const all = _readPendingUploads();
  if (!all[traceId]) return;
  all[traceId] = { ...all[traceId], ...patch };
  _writePendingUploads(all);
}
function _clearPendingUpload(traceId) {
  if (!traceId) return;
  const all = _readPendingUploads();
  if (all[traceId]) { delete all[traceId]; _writePendingUploads(all); }
}

function loadSession() {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}
function saveSession(id) {
  try { localStorage.setItem(STORAGE_KEY, id); } catch { /* localStorage disabled */ }
}

// Iter XX.77.1 — R3-lite handoff. The R3 onboarding page POSTs the
// first coaching turn to /web/chat itself and stashes the outcome
// here so Chat.jsx can paint it instantly on mount — no loading
// spinner, no "opening_message" from /web/session, no perceived
// handoff. The stash is session-scoped and consumed exactly once.
const R3_SEED_KEY = "r3_seed_reply";
const R3_SEED_MAX_AGE_MS = 5 * 60 * 1000;  // 5 minutes
function _consumeR3Seed(expectedSessionId) {
  try {
    const raw = sessionStorage.getItem(R3_SEED_KEY);
    if (!raw) return null;
    // Delete BEFORE parsing so a malformed payload can never re-fire
    // (defensive against poisoned/StrictMode double-consume).
    sessionStorage.removeItem(R3_SEED_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.session_id || parsed.session_id !== expectedSessionId) {
      // Stash belongs to a different session — discard silently.
      return null;
    }
    if (parsed.ts && (Date.now() - parsed.ts) > R3_SEED_MAX_AGE_MS) {
      return null;   // stale, older than 5 min
    }
    return parsed;
  } catch { return null; }
}

// Iter 71 — UTM capture. Reads utm_source / utm_campaign / utm_medium /
// utm_content / utm_term (+ document.referrer) from the current URL,
// merges with anything previously cached in localStorage (so the FIRST
// landing wins — the initial campaign that brought the user in), and
// returns a payload safe to POST to /web/session. Empty object if
// nothing is present at all.
// Exported for unit tests (no runtime callers import it).
export function _readAndCacheUtm() {
  const UTM_KEYS = ["utm_source", "utm_campaign", "utm_medium", "utm_content", "utm_term"];
  let cached = {};
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (raw) cached = JSON.parse(raw) || {};
  } catch { /* corrupt cache — ignore, treat as empty */ }
  const params = new URLSearchParams(window.location.search || "");
  const fromUrl = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) fromUrl[k] = String(v).slice(0, 120);
  }
  // Only capture referrer if we have UTMs (avoids leaking arbitrary
  // referrers when the user just typed the URL directly).
  if (Object.keys(fromUrl).length > 0 && document.referrer) {
    try {
      fromUrl.referrer = String(document.referrer).slice(0, 120);
    } catch { /* referrer read blocked (rare) — skip */ }
  }
  // FIRST-touch attribution: cached values win over URL. If nothing was
  // cached, cache now for future page reloads.
  if (Object.keys(fromUrl).length > 0 && Object.keys(cached).length === 0) {
    try { localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl)); }
    catch { /* localStorage disabled — payload still sent this once */ }
  }
  // Iter XX.285c — merge via the shared pure helper (first-touch wins;
  // stored-attribution backfill fills only missing keys). See
  // webAttribution.mergeSessionUtm for the full contract.
  try {
    // eslint-disable-next-line global-require
    const { mergeSessionUtm, readWebAttribution } = require("../lib/webAttribution");
    return mergeSessionUtm({ cached, fromUrl, storedAttr: readWebAttribution() });
  } catch { /* fail-soft — fall through to the un-enriched merge */ }
  return Object.keys(cached).length > 0 ? cached : fromUrl;
}

// Iter 33 — humanise a chip pick into a user-visible message. Used by the
// recovery card so the chat history reads naturally instead of showing
// "[CLARIFY:...]" token strings.
const _MULTI_PLAYER_CHIP_LABELS = {
  batting: "I'm the batter",
  bowling: "I'm the bowler",
  keeping: "I'm the wicketkeeper",
  describe: "I'll describe where I am",
};
const _NON_CRICKET_CHIP_LABELS = {
  batting: "Batting",
  bowling: "Bowling",
  fielding: "Fielding",
  training: "Training drill",
  gym: "Gym / fitness",
  match_footage: "Match footage",
  scorecard: "Scorecard",
  injury_concern: "Injury concern",
};
function _humanReadableChipPick(kind, chipId, freeform) {
  const labels = kind === "non_cricket"
    ? _NON_CRICKET_CHIP_LABELS
    : _MULTI_PLAYER_CHIP_LABELS;
  const label = labels[chipId] || chipId;
  const text = (freeform || "").trim();
  return text ? `${label} — ${text}` : label;
}

// ---- Iter 58 — Canonical video-failure card builder ----------------------
// Prefers the backend's Iter 53 contract fields (`user_facing_message`,
// `next_step`, `category`, `is_user_retryable`) over the legacy reason-string
// inference. Falls back to a reason-mapped headline/body only when the
// canonical fields are missing (older backend response shapes).
//
// Founder rule (Iter 58): when `is_user_retryable === false` we MUST NOT
// render a retry button AND we MUST NOT use the "your clip is still here"
// copy — both are misleading for terminal failures like NORMALIZATION_FAILED
// / FILE_TOO_LARGE / NON_CRICKET / MULTI_PLAYER_AFTER_RECOVERY.
const _CATEGORY_HEADLINES = {
  file_too_large:       "Video too large",
  duration_too_long:    "Clip too long",
  unsupported_codec:    "Video format issue",
  upload_interrupted:   "Upload didn't complete",
  processing_timeout:   "Review timed out",
  model_error:          "Reviewer hit an error",
  storage_error:        "Couldn't save your clip",
  non_cricket:          "That doesn't look like cricket",
  multi_player:         "Multiple players in clip",
  unknown_error:        "Couldn't review this clip",
};

function _headlineForCategory(category, rejectionReason) {
  if (category && _CATEGORY_HEADLINES[category]) return _CATEGORY_HEADLINES[category];
  // Reason-specific overrides for the few cases where the category headline
  // doesn't land cleanly.
  const rej = String(rejectionReason || "").toUpperCase();
  if (rej === "NORMALIZATION_FAILED") return "Couldn't read that clip";
  if (rej === "GEMINI_REJECTED")      return "Reviewer hit an error";
  return "Couldn't review this clip";
}

function buildFailureCard(data) {
  // Backend's canonical contract — prefer this when available.
  const userMsg = (data && typeof data.user_facing_message === "string") ? data.user_facing_message : "";
  const nextStep = (data && typeof data.next_step === "string") ? data.next_step : "";
  const category = data && data.category ? String(data.category) : null;
  const rejectionReason = data && data.rejection_reason ? String(data.rejection_reason) : null;
  const canRetry = (data && typeof data.is_user_retryable === "boolean")
    ? data.is_user_retryable
    : null;

  if (userMsg && canRetry !== null) {
    // Canonical path. Trust the backend's retryable flag verbatim.
    const headline = _headlineForCategory(category, rejectionReason);
    const body = nextStep ? `${userMsg}\n\n${nextStep}` : userMsg;
    return { headline, body, canRetry };
  }

  // Legacy fallback — older response shape without the canonical fields.
  // Be conservative: assume retry is OK so users aren't stuck if the
  // backend forgot the flag, but DON'T use the "still here" copy.
  const reason = String((data && data.normalized_reason) || "").toLowerCase();
  const rej = String((data && data.rejection_reason) || "").toUpperCase();
  if (data && data.gemini_called === false) {
    return {
      headline: "Upload couldn't be processed",
      body: "Your video reached Gully, but we couldn't prepare it for review. Try uploading the original camera file as a short 5-15 second clip.",
      canRetry: true,
    };
  }
  if (reason === "fielding_only" || reason === "keeping_only" || reason === "wicketkeeping_only") {
    return {
      headline: "Can only review batting & bowling for now",
      body: "I can see you're on the field — but I can only break down batting and bowling actions right now. Try a clip of yourself batting or bowling.",
      canRetry: false,
    };
  }
  if (reason === "normalization_failed" || rej === "NORMALIZATION_FAILED") {
    return {
      headline: "Couldn't read that clip",
      body: "Send a different clip — shorter (5-15 sec) and recorded directly from your phone's camera. Long or large videos sometimes can't be processed.",
      canRetry: false,
    };
  }
  if (reason === "gemini_timeout" || rej === "PROCESSING_TIMEOUT" || rej === "GEMINI_TIMEOUT") {
    return {
      headline: "Review timed out",
      body: "That clip took longer than we allow to review. Try a shorter version (under 30 seconds) and I'll take another look.",
      canRetry: true,
    };
  }
  const replyFallback = (data && typeof data.reply === "string") ? data.reply : "";
  return {
    headline: "Couldn't review this clip",
    body: replyFallback || "I couldn't process that clip cleanly. If it keeps failing, try a shorter version.",
    canRetry: true,
  };
}


// ---- Client diagnostics for upload telemetry -----------------------------
// Iter 53 — delegate to the canonical detector in lib/clientEnv.js so
// `in_app_browser` is captured as its own field (instagram | facebook |
// android_webview | ...) and admin can filter on it directly.
function collectClientDiag() {
  if (typeof navigator === "undefined") return {};
  try {
    const env = getClientEnv();
    return {
      device_type: env.device_type,
      os: env.os,
      browser: env.browser,
      in_app_browser: env.in_app_browser,  // explicit field — backend Form param
    };
  } catch {
    // Fallback: minimal UA bucketing if the import is missing.
    const ua = (navigator.userAgent || "").toLowerCase();
    let device_type = "desktop";
    if (/ipad|tablet/.test(ua)) device_type = "tablet";
    else if (/mobi|iphone|android.*mobile/.test(ua)) device_type = "mobile";
    let os = "other";
    if (/iphone|ipad|ipod/.test(ua)) os = "ios";
    else if (/android/.test(ua)) os = "android";
    return { device_type, os, browser: "other", in_app_browser: null };
  }
}

// Probe a selected video for duration + dimensions before upload. Returns
// {} if anything goes wrong — never blocks the upload path.
function probeVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      let done = false;
      const finish = (out) => {
        if (done) return;
        done = true;
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        resolve(out);
      };
      v.onloadedmetadata = () => {
        finish({
          duration_s: Math.round((v.duration || 0) * 10) / 10 || null,
          resolution_w: v.videoWidth || null,
          resolution_h: v.videoHeight || null,
          codec: (file.type || "").split("/")[1] || null,
        });
      };
      v.onerror = () => finish({});
      // Hard timeout — some Android WebViews never fire loadedmetadata.
      setTimeout(() => finish({}), 2500);
      v.src = url;
    } catch {
      resolve({});
    }
  });
}

// Iter XX.73 — Improvement Loop entry-point openers. When the player
// arrives via /chat?entry=<kind> from LandingLoop OR taps the
// corresponding chip in the composer, we send this natural opener as
// the first message and stamp `entry_point` on the /web/chat body so
// the backend persists it on the session.
//
// Video Analysis is deliberately NOT here — Video begins with the
// clip, not a chat prompt. Both the tile and the chip open the video
// picker directly.
const ENTRY_POINT_OPENERS = {
  match:    "I played a match — want to talk it through.",
  practice: "Just finished a practice session.",
  question: "Got a cricket question for you.",
};

const ENTRY_POINT_CHIPS = [
  { id: "match",    label: "Match Reflection" },
  { id: "practice", label: "Practice Reflection" },
  { id: "video",    label: "Video Analysis" },
  { id: "question", label: "Cricket Question" },
];

// Iter XX.97 — Client-side save-intent detector.
//
// Mirrors backend/parent_memory.SAVE_INTENT_PATTERNS closely enough
// that the journey FSM never swallows a "save this" while waiting
// for a dream answer. Regex-only, deterministic, false-positive
// safe — the backend re-checks with the authoritative list before
// firing the extraction task.
const _SAVE_INTENT_RE = /\b(?:save\s+(?:this|that|it|these|for\s+later)|(?:please\s+)?remember\s+(?:this|that|it|these|him|her|them|about\s+\w+)|don'?t\s+forget\s+(?:this|that|it|about|these)|keep\s+this\s+in\s+mind|note\s+(?:this|that|it|these)\s+down|make\s+a\s+note\s+of\s+(?:this|that|it)|(?:save|store)\s+to\s+(?:my|the)?\s*(?:cricket\s+)?memory|cricket\s+memory\b.{0,20}\b(?:save|update|store|remember))\b/i;
// Shared constant referenced by send()'s FSM guard AND
// handleJourneyUserInput's bypass path. Prevents a typo from
// silently reintroducing the send↔handleJourneyUserInput recursion
// bug caught in iteration 106 testing.
const SAVE_INTENT_BYPASS = "save_intent_bypass";
// Iter XX.102 — Sources that MUST bypass the journey FSM guard in
// send(). Any onboarding-interrupted coaching topic being resumed
// (dream / parent / role / age / level / future onboarding) is
// tagged with one of these sources. The guard's setJourneyState
// state update is async and the setTimeout callback captures a
// stale `send` closure — so relying purely on `journeyState !==
// "NONE"` re-routes the resume into handleJourneyUserInput and
// clobbers the original topic. Explicit bypass by source is the
// authoritative signal.
const RESUME_AFTER_ONBOARDING = "resume_after_onboarding";
const FSM_GUARD_BYPASS_SOURCES = new Set([
  SAVE_INTENT_BYPASS,
  RESUME_AFTER_ONBOARDING,
]);
function _looksLikeSaveIntent(text) {
  if (!text || typeof text !== "string") return false;
  return _SAVE_INTENT_RE.test(text.trim());
}

// Iter XX.242a — Returning-user Cricket Graph card. Renders ONLY when
// the server reports state != "first_time" AND the graph has real
// content. First-time users see nothing here; the existing simple
// chat entry is preserved. Both card CTAs simply dismiss the card —
// they never leave the chat surface, so continuity is felt without
// blocking the composer.
function ReturningUserCricketGraph() {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const state = await fetchCricketGraphState();
      if (!cancelled) setVisible(state && state !== "first_time");
    })();
    return () => { cancelled = true; };
  }, []);
  const [dismissed, setDismissed] = React.useState(false);
  if (!visible || dismissed) return null;
  const focusComposer = () => {
    setDismissed(true);
    try {
      const el = document.querySelector('textarea, input[type="text"]');
      if (el && typeof el.focus === "function") el.focus();
    } catch { /* noop */ }
    try {
      import("../lib/api").then((m) => {
        m.default.post("/web/track", {
          session_id: null,
          event: "chat_launched_from_returning_home",
        }).catch(() => {});
      });
    } catch { /* noop */ }
  };
  return (
    <div data-testid="chat-returning-user-cricket-graph" className="mb-4">
      <CricketGraphCard onOpenChat={focusComposer} />
    </div>
  );
}

export default function Chat() {
  const [sessionId, setSessionId] = useState(null);
  // Iter XX.280 — Journey-start instrumentation (founder-approved
  // 2026-10-01). Narrow: 4 client events (ui_ready, first_interaction,
  // message_ack_received, session_end) posted to the existing
  // /web/client-error sink. No content capture, no new PII — only
  // session_id + browser_id + timestamp + stage name.
  const _journeyUiReadyFiredRef = useRef(false);
  const _journeyFirstInteractionFiredRef = useRef(false);
  // Fires once when the chat composer is interactive (sessionId
  // resolved after pre-warm). Distinguishes prefetch/bot loads
  // (no ui_ready) from real human page views.
  useEffect(() => {
    if (!sessionId || _journeyUiReadyFiredRef.current) return;
    _journeyUiReadyFiredRef.current = true;
    try {
      let _bid = null;
      try { _bid = getBrowserId(); } catch { /* noop */ }
      postClientEvent(API, sessionId, "ui_ready",
        _bid ? { browser_id: _bid } : {});
    } catch { /* non-fatal — telemetry must never break chat */ }
  }, [sessionId]);
  // session_end beacon — navigator.sendBeacon on pagehide, best-effort.
  // Carries session_id + timestamp so the loop can compute trustworthy
  // durations. Missing beacons = "unknown", never "bounced".
  useEffect(() => {
    if (!sessionId) return;
    const _sid = sessionId;
    const _onHide = () => {
      try {
        let _bid = null;
        try { _bid = getBrowserId(); } catch { /* noop */ }
        const _body = JSON.stringify({
          session_id: _sid, kind: "session_end",
          browser_id: _bid, ts: new Date().toISOString(),
        });
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
          navigator.sendBeacon(`${API}/web/client-error`, _body);
        }
      } catch { /* non-fatal */ }
    };
    window.addEventListener("pagehide", _onHide);
    return () => { try { window.removeEventListener("pagehide", _onHide); } catch {} };
  }, [sessionId]);
  // Iter XX.199 — "Gully remembers me" second-open card. Server-decided.
  // `null` = not yet asked, or don't show. When populated it renders
  // <SecondOpenCard/> above the composer inside the message column.
  const [secondOpen, setSecondOpen] = useState(null);
  // Iter XX.200 — Meta App Events consent bar (Android-only, once).
  // `null` = decision not read yet, `true` = show, `false` = decided.
  const [metaConsentVisible, setMetaConsentVisible] = useState(false);
  const memoryHasNew = useMemoryHasNew();
  // Iter XX.74 — Journey nav pill visibility. Only shown when the
  // player has identity AND has at least one captured commitment.
  // The pill briefly pulses for a few seconds right after a fresh
  // commitment is captured (`journeyPulse` toggled by the /web/chat
  // response signal).
  const [journeyReady, setJourneyReady] = useState(false);
  const [journeyPulse, setJourneyPulse] = useState(false);
  // Iter XX.73 — Improvement Loop flag. When ON, we swap the legacy
  // starter chips for the 4-tile entry-point picker (Match / Practice
  // / Video / Question), auto-send the appropriate opener when we
  // arrive via /chat?entry=<kind>, and stamp `entry_point` on the
  // first /web/chat request so the backend can persist it.
  const improvementLoopOn = useFlag("new_improvement_loop_enabled");
  // Iter XX.75 — Practice with Gully (Phase 0). Founder-only flag.
  // When ON, a Practice pill appears next to the entry-point row that
  // opens a small modal for a bowling-only Rhythm coaching read.
  const practiceOn = useFlag("practice_with_gully_enabled");
  const [practiceOpen, setPracticeOpen] = useState(false);
  // Iter XX.89 — Paid Instagram Personalisation Gate. Flag-gated
  // eligibility computed ONCE per mount so a mid-session localStorage
  // flip doesn't yank the gate away while the player is tapping. The
  // gate persona/goal is stashed via markGateCompleted() so the FIRST
  // /web/chat call in this session can consume it (see send()).
  const paidIgGateFlagOn = useFlag("paid_instagram_personalisation_gate_enabled");
  const [showPaidIgGate, setShowPaidIgGate] = useState(false);
  useEffect(() => {
    if (!paidIgGateFlagOn) return;
    // Iter XX.160 — Landing→chat handoff bypass. Founder rule:
    // "Nothing between landing and the first coaching moment." When
    // the visitor arrived from the activation landing (any chip or
    // typed intent), their intent is already stashed and Chat.jsx
    // fires the first /web/chat POST on paint — the personalise
    // gate would interrupt that. Direct /chat visitors (no
    // `from=activation`) still see the gate exactly as before.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("from") === "activation") return;
    } catch { /* noop */ }
    // Deliberate: recompute eligibility ONLY when the flag flips on.
    // shouldShowPaidInstagramGate() reads localStorage + persisted
    // UTMs and is O(1) — safe to call synchronously during render
    // frames without a debouncer.
    if (shouldShowPaidInstagramGate()) setShowPaidIgGate(true);
  }, [paidIgGateFlagOn]);

  // Iter XX.129 — Progressive Commitment Engine gate. Flag-gated
  // eligibility: mount-time only, never mid-session so a rollout flip
  // doesn't yank the engine away while the visitor is tapping. The
  // engine's tap-context is attached to the FIRST /web/chat POST via
  // `pceHandoffPendingRef` (see send()). Signed-in returning users
  // (localStorage.gully_token present) bypass entirely — they've
  // committed already.
  const pceFlagOn = useFlag("progressive_commitment_engine_enabled");
  // Iter XX.130 — Interaction Decision Engine flag. When ON, the
  // engine decides when to show the PCE (mid-conversation or at
  // start), and the strict one-shot `g_pce_v1_done` guard is
  // retired. When OFF, the classic one-shot XX.129 behaviour holds.
  const ideFlagOn = useFlag("interaction_decision_engine_enabled");
  // Iter XX.131 — Plus users skip the opening PCE overlay entirely.
  // The engine can still raise a mid-coaching micro-commitment via
  // /web/chat's next_interaction payload; that path is honoured
  // separately below.
  const isPlusActive = useFlag("is_plus_active");
  // Iter XX.273 — Continue This Journey · post-purchase correlation.
  // When the user lands back on /chat with `?journey=continue`
  // AND Plus is active, fire the correlated purchase-success event
  // ONCE per session. Session-deduped by trackFunnel. Never fires
  // on repeat renders or on /chat without the marker.
  useEffect(() => {
    try {
      const usp = new URLSearchParams(window.location.search);
      if (usp.get("journey") === "continue" && isPlusActive) {
        try {
          trackFunnel("journey_offer_purchase_success", {
            surface: "arc_lock_402",
          });
        } catch { /* fire-and-forget */ }
        // Clean the URL so a refresh doesn't loop the event (though
        // trackFunnel is session-deduped anyway).
        try {
          usp.delete("journey");
          const search = usp.toString();
          const next = window.location.pathname + (search ? `?${search}` : "");
          window.history.replaceState({}, "", next);
        } catch { /* noop */ }
      }
    } catch { /* noop */ }
  }, [isPlusActive]);
  // Iter XX.270 — "Build Your Gully" narrative (Phase 1). Additive,
  // visibility-only flag. When OFF (default), Home / paywall /
  // Memory renders exactly as XX.269.
  const gullyNarrativeOn = useFlag("gully_narrative_enabled");
  // Iter XX.272 — Gully Challenge #001. Additive; the surface only
  // renders when both this flag AND the challenge doc's `enabled`
  // gate permit exposure (server enforces the second gate).
  const gullyChallengeOn = useFlag("gully_challenge_v1_enabled");
  const [showPce, setShowPce] = useState(false);
  const pceHandoffPendingRef = useRef(false);
  const [pceHandoffPlaceholder, setPceHandoffPlaceholder] = useState(null);
  // Iter XX.130 — High-intent Plus signal. Any of these force the
  // engine into `introduce_plus` mode instantly on the next
  // /web/chat POST:
  //   ?intent=plus  ?from=plus_cta  localStorage.g_high_intent_plus
  const highIntentPlusRef = useRef(false);
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("intent") === "plus"
          || params.get("from") === "plus_cta"
          || localStorage.getItem("g_high_intent_plus") === "1") {
        highIntentPlusRef.current = true;
      }
    } catch { /* noop */ }
  }, []);
  // Iter XX.133 — the opening PCE overlay effect lives further down
  // (after `showParentEntryGate`, `pendingIntentRef` and
  // `hasFirstMessage` are declared) to avoid a temporal-dead-zone
  // ReferenceError. See "// Iter XX.133 opening PCE effect" below.

  // Iter XX.94 — Parent Entry gate. XX.96 update: no longer shown on
  // /chat mount. Instead, the gate is triggered at the "commitment
  // moment" — either on the InstantChatLanding chip/Send (which
  // stashes the pending intent + persists the relationship BEFORE
  // navigating here) or from Chat.jsx's own send() intercept for
  // direct /chat visitors who bypass the landing. This preserves
  // the founder's rule: parent context is captured only after the
  // visitor has demonstrated intent, never as a cold opener.
  const [showParentEntryGate, setShowParentEntryGate] = useState(false);
  // Iter XX.96 — Pending intent captured either by the landing's
  // Parent Entry gate handoff (sessionStorage.g_pending_intent_v1)
  // OR by Chat.jsx's own send() intercept when the gate fires here.
  // The ref survives re-renders and is consumed once — either by
  // the XX.92 intro completion path (landing handoff) or by the
  // gate's onComplete callback (direct /chat visitors).
  const pendingIntentRef = useRef(null);
  const pendingIntentFiredRef = useRef(false);
  // Iter XX.258 — Gully Initiates V1. Set true when a proactive
  // initiation is currently un-acknowledged (either shown live or
  // loaded from history without a subsequent user reply). The next
  // outbound user message fires `/initiate/ack` with `replied`.
  const gullyInitiatePendingRef = useRef(false);
  const gullyInitiateCheckedRef = useRef(false);
  // Iter XX.21 — ChatGPT-style multi-thread sidebar. Toggled by the
  // hamburger in the header. Switching threads updates localStorage +
  // re-hydrates from /web/history for the new session_id.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter XX.126 — Coaching-arc lock. Set to true after the backend
  // returns HTTP 402 with `paywall=coaching_arc_used`. Disables the
  // composer so the user sees a boundary, not a broken input.
  const [arcLocked, setArcLocked] = useState(false);
  // Post-paywall ghosting repair (2026-10-02) — synchronous mirror of
  // arcLocked for async closures (402 handler, send guard). React state
  // updates are async; the ref is updated synchronously alongside every
  // setArcLocked call so rapid double-sends and 402 races read the truth.
  const arcLockedRef = useRef(false);
  // Post-paywall ghosting repair (2026-10-02) — set when Plus entitlement
  // clears the lock; consumed on the next successful send to fire
  // `conversation_resumed` exactly once.
  const _resumedAfterLockRef = useRef(false);
  // Post-paywall ghosting repair (2026-10-02) — ensures `locked_ui_displayed`
  // fires once per lock episode, not on every re-render while locked.
  const _lockEpisodeFiredRef = useRef(false);
  // Post-paywall ghosting repair (2026-10-02) — fire `locked_ui_displayed`
  // when the explicit locked composer becomes visible. Once per lock episode;
  // resets when the lock clears so a future re-lock fires again.
  useEffect(() => {
    if (arcLocked && !_lockEpisodeFiredRef.current) {
      _lockEpisodeFiredRef.current = true;
      try { trackFunnel("locked_ui_displayed", { surface: "chat_composer" }); } catch { /* fire-and-forget */ }
    } else if (!arcLocked) {
      _lockEpisodeFiredRef.current = false;
    }
  }, [arcLocked]);
  // Iter XX.90 — Rotating placeholder examples. Cycle through
  // COMPOSER_PLACEHOLDERS every ~3.5s while the composer is empty AND
  // unfocused. As soon as the player focuses or types, rotation stops
  // and stays stopped — we never want a moving placeholder distracting
  // an actively engaged player. `focused` resets to false on blur so
  // rotation can resume if the player walks away without sending.
  const [composerFocused, setComposerFocused] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  useEffect(() => {
    if (composerFocused || input) return; // frozen while engaged
    const id = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % COMPOSER_PLACEHOLDERS.length);
    }, COMPOSER_PLACEHOLDER_INTERVAL_MS);
    return () => clearInterval(id);
  }, [composerFocused, input]);
  // 2026-02-25 — starter-chip flag. Set to true the first time the player
  // sends a real message; once set, the chip row never re-renders this
  // session. We also flip it when a video is picked (chip 5 path), since
  // an upload is a clear "session has started" signal too.
  const [hasFirstMessage, setHasFirstMessage] = useState(false);
  // Iter XX.265b — Canonical Home-surface state.
  // `homePinned` is toggled by the "← Gully" header affordance so an
  // established player can return to the polished Home during an
  // active conversation without erasing / hiding transcript history.
  // The derivation lives in /lib/homeSurface.js and is the SINGLE
  // source of truth for whether `<AuthedHomeHero>` is currently the
  // top surface. `hasFirstMessage` is preserved because other legacy
  // effects still key off it; it is NOT used for Home gating any more.
  const [homePinned, setHomePinned] = useState(false);
  // Iter XX.268 — current-mission return loop. Fetched once on mount
  // from GET /api/home/current_mission. Anonymous / no-active-mission
  // users get `null` and the Home hero renders exactly as it did in
  // XX.265b / XX.266 / XX.267 (no card shown). Reuses existing
  // mission_engine / current_focus infrastructure — no new mission
  // model or business logic on this side either.
  const [activeMission, setActiveMission] = useState(null);
  // Iter XX.285h — REMEMBERED moment for B+D continuity.
  // Fetched from GET /api/home/remembered. Null when no valid moment,
  // anonymous, or read fails. Never blocks rendering.
  const [rememberedMoment, setRememberedMoment] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/home/remembered");
        if (cancelled) return;
        if (data && data.available && data.display_text) {
          setRememberedMoment({
            tier: data.tier,
            kind: data.kind,
            text: data.text,
            display_text: data.display_text,
            at: data.at,
          });
        }
        // else: leave as null → RememberedMoment renders nothing
      } catch { /* silent — Home falls back to no-moment unchanged */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Iter XX.265b — Authenticated Home Hero: read the current user so
  // we can greet by first name. `useAuth` returns { user, loading, ...};
  // both fields are safe to destructure on every render.
  // (Declared here — above the claim-banner effect — because that
  // effect's dependency array reads `authedUser?.user_id` during render;
  // declaring it later is a temporal-dead-zone crash (P0 2026-10-03).)
  const { user: authedUser } = useAuth();
  // Iter XX.285h — "Claim your cricket" banner for anonymous users
  // with meaningful stored state. Rules:
  //   - Only for anonymous (no authedUser)
  //   - Only when /home/anonymous-state returns has_state=true
  //   - Never during an active coaching conversation (messages exist)
  //   - Dismissible (session-only; reappears next visit if state remains)
  //   - Sign-in preserves state via existing link_session_to_user migration
  const [showClaimBanner, setShowClaimBanner] = useState(false);
  const [claimBannerDismissed, setClaimBannerDismissed] = useState(false);
  // Track if there's an active conversation (don't interrupt coaching)
  const hasActiveConversation = (() => {
    try {
      // messages is the chat transcript state; non-empty = active conversation
      return Array.isArray(messages) && messages.length > 0;
    } catch { return false; }
  })();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only for anonymous users
      if (authedUser?.user_id) {
        if (!cancelled) setShowClaimBanner(false);
        return;
      }
      // Don't interrupt an active coaching conversation
      if (hasActiveConversation) {
        if (!cancelled) setShowClaimBanner(false);
        return;
      }
      // Don't re-show if dismissed this session
      if (claimBannerDismissed) {
        if (!cancelled) setShowClaimBanner(false);
        return;
      }
      try {
        // Get browser/session identifiers for the anonymous check
        let browserId = null;
        let sessionId = null;
        try {
          browserId = localStorage.getItem("gully_browser_id");
          sessionId = sessionId || null; // sessionId state is available below
        } catch { /* noop */ }
        const params = new URLSearchParams();
        if (browserId) params.set("browser_id", browserId);
        // Note: sessionId from state may not be available at this point
        // in the component; the backend also checks headers as fallback
        const { data } = await api.get(`/home/anonymous-state?${params.toString()}`);
        if (cancelled) return;
        setShowClaimBanner(!!(data && data.has_state));
      } catch { /* silent — banner stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [authedUser?.user_id, hasActiveConversation, claimBannerDismissed]);
  // First Loop — one-tap report capture visibility. Opened by the
  // mission card's "I tried it" tap (manual taps always work;
  // `shouldAutoSurface` in ReportCapture guards only future
  // proactive surfacing, never this path).
  const [showReportCapture, setShowReportCapture] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/home/current_mission");
        if (cancelled) return;
        // `buildActiveMission` is the single mapping — every loop
        // presentation field the backend supplies must pass through
        // here (this was the XX.276 wiring gap).
        setActiveMission(buildActiveMission(data));
      } catch { /* silent — Home falls back to no-card unchanged */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Iter XX.270 — "Build Your Gully" narrative signals (Phase 1).
  // Read-only aggregate over `/api/journey/me` + `/api/web/knows`.
  // Never a new endpoint, never a new collection. `signalsCount`
  // is a strict AND-gate over persisted signals so the returning-
  // user narrative line ("Your Gully is taking shape.") only
  // renders when there is genuinely something to be shaping.
  const [narrativeSignalsCount, setNarrativeSignalsCount] = useState(0);
  useEffect(() => {
    if (!gullyNarrativeOn) return;
    let cancelled = false;
    (async () => {
      try {
        // Parallel — both are cached, cheap, and already used
        // elsewhere in the app.
        const [journeyRes, knowsRes] = await Promise.all([
          api.get("/journey/me").catch(() => ({ data: null })),
          (async () => {
            let bid = null;
            try {
              // eslint-disable-next-line global-require
              bid = (require("../lib/browserId").getBrowserId)();
            } catch { /* noop */ }
            let sid = null;
            try {
              sid = localStorage.getItem("gully_web_session_id");
            } catch { /* noop */ }
            if (!sid) return { data: null };
            return api
              .get("/web/knows", { params: { session_id: sid, browser_id: bid } })
              .catch(() => ({ data: null }));
          })(),
        ]);
        if (cancelled) return;
        const journey = journeyRes?.data || {};
        const knows = knowsRes?.data || {};
        const snapshotPresent = Boolean(
          (journey?.journey_snapshot && String(journey.journey_snapshot).trim())
          || (knows?.patterns || knows?.strengths || knows?.stats),
        );
        const hasSnapshot = Boolean(
          (journey?.journey_snapshot && String(journey.journey_snapshot).trim().length > 0),
        );
        let count = 0;
        if (hasSnapshot) count += 1;
        if (Array.isArray(knows?.patterns) && knows.patterns.length >= 1) count += 1;
        if (Array.isArray(knows?.strengths) && knows.strengths.length >= 1) count += 1;
        if (Number(knows?.stats?.video_reviews) >= 1) count += 1;
        // activeMission comes from the other effect above. Add
        // it if it has already resolved by the time we finish.
        // The AND-gate in `AuthedHomeHero` re-checks so a mission
        // arriving late still lifts the count on its own render.
        setNarrativeSignalsCount(count);
        void snapshotPresent; // kept for future telemetry if we
                              // decide to distinguish empty vs
                              // 1-signal cohorts. Not exposed
                              // yet — Phase 1 gates on count.
      } catch {
        setNarrativeSignalsCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [gullyNarrativeOn]);
  // Iter XX.272 — Challenge state + contextual home banner. Fetched
  // once when the flag is ON. Never rendered as a permanent ad —
  // `shouldShowHomeChallengeBanner` gates on user status + Plus +
  // registration window. Pulse remains the primary discovery
  // surface for everyone else.
  const [challengeState, setChallengeState] = useState(null);
  useEffect(() => {
    if (!gullyChallengeOn) return;
    let cancelled = false;
    (async () => {
      try {
        const { fetchCurrentChallenge } = await import("../lib/challenge");
        const s = await fetchCurrentChallenge();
        if (!cancelled) setChallengeState(s);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [gullyChallengeOn]);
  const challengeBanner = useMemo(() => {
    if (!gullyChallengeOn) return null;
    if (!challengeState || challengeState.available === false) return null;
    // Same predicate as shouldShowHomeChallengeBanner — kept inline
    // to avoid a second import in the hot Chat.jsx path.
    const status = challengeState.user_status;
    const showParticipating = status === "participating";
    const showInvited       = status === "invited";
    const showPlusEligible  = status === "not_joined"
      && isPlusActive
      && challengeState.registration_closes_at
      && Date.now() < Date.parse(challengeState.registration_closes_at);
    if (!showParticipating && !showInvited && !showPlusEligible) return null;
    if (showParticipating) {
      const d = challengeState.days_elapsed;
      const total = challengeState.total_days || 7;
      return {
        challenge_id: challengeState.challenge_id,
        eyebrow: d ? `Gully Challenge · Day ${d}/${total}` : "Gully Challenge",
        line: challengeState.user_focus
          ? `Current focus: ${challengeState.user_focus}`
          : "Pick your one thing.",
      };
    }
    if (showInvited) {
      return {
        challenge_id: challengeState.challenge_id,
        eyebrow: "You're invited",
        line: "Join the first Gully Challenge — 7 days. One thing.",
      };
    }
    return {
      challenge_id: challengeState.challenge_id,
      eyebrow: "Gully Challenge",
      line: "7 days. One thing in your cricket.",
    };
  }, [gullyChallengeOn, challengeState, isPlusActive]);
  // Iter XX.267a — Consume hash hints from PrimarySurfaceNav. Pulse
  // and Memory bottom-nav taps navigate to `/chat#home` (Home tab) or
  // `/chat#chat` (centre Gully tab). We interpret the hash exactly
  // once on mount and clear it so the URL stays clean. Preserves the
  // transcript — no new session is created.
  useEffect(() => {
    let h = "";
    try { h = (window.location.hash || "").toLowerCase(); }
    catch { /* noop */ }
    if (!h) return;
    if (h.includes("home")) {
      setHomePinned(true);
    } else if (h.includes("chat")) {
      setHomePinned(false);
    }
    try {
      // Strip the hash without a full navigation.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch { /* noop */ }
     
  }, []);
  // Iter XX.266 — More sheet visibility.
  const [moreOpen, setMoreOpen] = useState(false);
  // Iter XX.133 opening PCE effect. Declared here (rather than
  // alongside the other pce* state) because it references
  // `showParentEntryGate`, `pendingIntentRef` and `hasFirstMessage`
  // — all declared above this line — so referencing them earlier
  // would hit the const TDZ.
  //
  // Iter XX.172.1 (Feb 2026) — Opening PCE overlay RETIRED.
  //
  // Founder invariant: NO NEW USER SHOULD COMPLETE A PROBLEM-
  // CLASSIFICATION QUESTIONNAIRE BEFORE GULLY HAS DELIVERED ITS
  // FIRST REAL COACHING VALUE. The chip flow (XX.172 CONTROL and
  // FOCUSED arms) already bypassed via `from=activation`; the only
  // remaining cohort that hit the recognition card was direct
  // `/chat` visitors. That interstitial is now gone.
  //
  // Deliberately preserved:
  //   · `/api/pce/*` endpoints (server flag + question bank stay ON).
  //   · `question_engine_config.json` question tree.
  //   · IDE-driven mid-conversation micro-commitments via
  //     /web/chat's `next_interaction` payload — the "earned
  //     question" path.
  //   · `RECOGNITION_LINE_1` / `RECOGNITION_LINE_2` constants
  //     (still imported by mid-conversation surfaces).
  //   · `<QuestionEngineCard>` mount at L4112 — dead-code guarded
  //     by `showPce` which is now never set to true. Left in place
  //     so a founder-driven re-enable is a one-line revert.
  //
  // The effect is retained (skeleton only) so any future re-enable
  // slots into the same execution point. `showPce` state remains
  // in scope for downstream JSX which reads it defensively.
  useEffect(() => {
    // XX.172.1 — opening PCE overlay retired. No-op by design.
    // Do not restore `setShowPce(true)` here without an explicit
    // founder decision to reopen the recognition-card cohort.
    void pceFlagOn;
    void isPlusActive;
    void showParentEntryGate;
    void hasFirstMessage;
  }, [pceFlagOn, ideFlagOn, isPlusActive, showParentEntryGate, hasFirstMessage]);

  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  // Iter XX.124 — Latest lifecycle phase reported by /api/video/status
  // once the upload byte-transfer has completed. Drives the coach-voice
  // progress label. Null while transferring; string once reviewing.
  const [uploadPhase, setUploadPhase] = useState(null);
  const [uploadTraceId, setUploadTraceId] = useState(null);

  // Iter XX.124 — After byte-transfer completes (pct === 100) cycle
  // the phase label based on elapsed time so the user sees coaching
  // progress even before the backend job publishes its lifecycle
  // state. Deliberately mood-language, not specific claims.
  //   0-4s   → "getting it ready" (normalizing/stored)
  //   4-10s  → "watching each delivery" (reviewing)
  //   10s+   → "putting my coaching together" (formatting)
  useEffect(() => {
    if (!uploading) { setUploadPhase(null); return; }
    if (uploadPct < 100) { setUploadPhase(null); return; }
    const t0 = Date.now();
    setUploadPhase("stored");
    const tick = () => {
      const el = Date.now() - t0;
      if (el < 4000) setUploadPhase("stored");
      else if (el < 10000) setUploadPhase("reviewing");
      else setUploadPhase("formatting");
    };
    tick();
    const id = setInterval(tick, 1500);
    return () => clearInterval(id);
  }, [uploading, uploadPct]);
  const [needsIdentity, setNeedsIdentity] = useState(false);
  const [identityShown, setIdentityShown] = useState(false);
  const [linked, setLinked] = useState(false);
  // Iter XX.26 — older-conversations prompt. Set right after the
  // AuthProvider's post-login claim returns `sessions_skipped_older>0`.
  // Shown once per browser+login and dismissible.
  const [olderPrompt, setOlderPrompt] = useState(null);
  const [olderPromptBusy, setOlderPromptBusy] = useState(false);

  // Iter XX.58 — Player Journey (Dream → Next Level → Today's Mission).
  // Modal FSM: while `journeyState !== "NONE"` the normal chat pipeline
  // is fully suspended. No memory retrieval, no /web/chat calls, no
  // answer to intercepted user text. All user input is routed to the
  // journey handler until the flow completes.
  //   journey       = /api/journey/me payload (null until loaded)
  //   journeyState  = "NONE" | "WAITING_FOR_DREAM"
  //                 | "WAITING_FOR_NEXT_LEVEL_CONFIRMATION"
  //                 | "WAITING_FOR_MISSION_CONFIRMATION"
  //   journeyProposal = { next_level, why_this_matters, mission } — the
  //                     latest LLM proposal awaiting player confirmation.
  //   journeyEditMode = null | "next_level" | "mission" — when the player
  //                     tapped "Change it" and needs to type a replacement.
  //   journeyIntent   = the raw first user message we intercepted, used to
  //                     ground the Next Level proposal. Never replayed.
  const [journey, setJourney] = useState(null);
  const [journeyState, setJourneyState] = useState("NONE");
  const [journeyProposal, setJourneyProposal] = useState(null);
  const [journeyEditMode, setJourneyEditMode] = useState(null);
  const journeyIntentRef = useRef(null);
  // P0-B — cricket-intent carve-out. When the session's first message
  // carries a sufficiently specific cricket problem, the dream gate is
  // bypassed so coaching runs first. This ref remembers to collect the
  // dream AFTER the first real coaching reply lands (deferred, not
  // removed). See send() and the reply handler below.
  const deferredDreamRef = useRef(false);
  // Iter XX.92 — Scripted intro state. `introRunning` is true while
  // the sequence is scheduling messages; `introVisibleCount` is the
  // number of intro lines currently rendered (0 → 4); `introTyping`
  // is true when Gully's typing indicator should show; `introTimers`
  // holds the setTimeout handles so an interruption can cancel them
  // atomically. All four values reset together on interruption /
  // completion so no dangling timer ever fires late.
  const [introRunning, setIntroRunning] = useState(false);
  const [introVisibleCount, setIntroVisibleCount] = useState(0);
  const [introTyping, setIntroTyping] = useState(false);
  const introTimersRef = useRef([]);
  // Iter XX.96 — Hydrate the pending intent stashed by the landing's
  // Parent Entry gate handoff. Iter XX.107 (hotfix): use the
  // non-destructive `peekPendingIntent()` here — the destructive
  // `consumePendingIntent()` was clearing sessionStorage during
  // React 18 StrictMode's first (ephemeral) mount, so the real
  // second mount found empty storage and dropped the intent. The
  // flush effect below performs the actual `consumePendingIntent()`
  // right before firing send(), which is the atomic point where we
  // truly commit to replaying the message.
  useEffect(() => {
    try {
      const intent = peekPendingIntent();
      if (intent && intent.text) {
        pendingIntentRef.current = intent;
        pendingIntentFiredRef.current = false;
        // Iter XX.149 — Instant-navigate landing→chat handoff.
        // Paint the user's message optimistically + turn on the typing
        // dots the moment we discover the intent. This is the
        // perceived-latency win: the visitor sees their own message
        // and Gully's typing state within a paint frame of the tap,
        // instead of waiting for /web/session (~200 ms) + /web/chat
        // (~2-6 s LLM) to resolve. The user bubble is deduped inside
        // `send()` (below) via a role+content match, so no double
        // paint on the eventual send() replay.
        // Iter XX.265b — Tag the optimistic paint as system-seeded so
        // the Home-surface derivation does NOT count it as meaningful
        // user activity. The real send() firing right after (via the
        // pending-intent flush effect) either de-dupes and CLEARS this
        // flag (upgrading the row to meaningful), or appends a fresh
        // meaningful row. Either way `hasMeaningfulUserContent` flips
        // true only at the send() boundary — never from this paint.
        setMessages((m) => (
          m.length ? m : [{ role: "user", content: intent.text, _system_seeded: true }]
        ));
        setHasFirstMessage(true);
        setBusy(true);
        try {
          if (typeof intent.t_tap === "number") {
            const t_now = (typeof performance !== "undefined" && performance.now)
              ? performance.now() : Date.now();
            // eslint-disable-next-line no-console
            console.info("[gully.latency] chat.mount.painted-user-bubble", {
              ms_since_tap: Math.round(t_now - intent.t_tap),
              source: intent.source,
            });
          }
        } catch { /* noop */ }
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const introEmittedInterruptRef = useRef(false);
  const introSuppressOpenerRef = useRef(false);
  // Iter XX.96 — Flag set by _cancelIntro (any reason) so the
  // pending-intent flush effect can observe "intro is done" without
  // depending on a specific counter. Also nudges `introFinishedTick`
  // to trigger the effect re-run. A ref alone won't re-render.
  // Iter XX.92 → XX.106 — With INTRO_SCRIPT retired the scripted
  // intro no longer exists. `introFinishedRef` defaults to TRUE so
  // downstream effects that gated on "wait for the intro to end"
  // (notably the XX.96 pending-intent flush at ~L1337) fire on
  // mount. If a future iteration repopulates INTRO_SCRIPT, the
  // intro scheduler flips this back to false while running and
  // sets it true via `_cancelIntro("completed")`.
  const introFinishedRef = useRef(INTRO_SCRIPT.length === 0);
  const [introFinishedTick, setIntroFinishedTick] = useState(0);

  // Iter XX.92 — Intro cancellation. Cancels any pending timers, hides
  // the typing indicator, and marks the intro finished. Called by the
  // interruption path (user typed OR chip tapped OR upload started)
  // and by the sequencer itself when it reaches message 4. Idempotent.
  function _cancelIntro(reason /* "completed" | "interrupted_typed" | ... */) {
    try {
      introTimersRef.current.forEach((t) => clearTimeout(t));
    } catch { /* noop */ }
    introTimersRef.current = [];
    setIntroTyping(false);
    setIntroRunning(false);
    // Iter XX.92 — Persist "intro already shown" so subsequent visits
    // by this browser never replay the intro (bug #2 from iteration_104:
    // returning anonymous users where hydrate skipped were re-seeing
    // the whole intro every mount). Even a mid-intro interruption
    // counts as "seen" — the visitor has already experienced Gully
    // introducing itself, replaying would feel canned.
    try { localStorage.setItem(INTRO_SHOWN_KEY, "1"); } catch { /* noop */ }
    // Analytics — dedupe per session. `intro_completed` / `intro_interrupted`.
    try {
      if (reason === "completed") {
        postClientEvent(API, sessionId, "intro_completed", {});
        try { clarityTrack("intro_completed", {}); } catch { /* noop */ }
      } else if (reason && reason.startsWith("interrupted") && !introEmittedInterruptRef.current) {
        introEmittedInterruptRef.current = true;
        postClientEvent(API, sessionId, "intro_interrupted", {
          at_message: introVisibleCount, reason,
        });
        try { clarityTrack("intro_interrupted", {
          at_message: introVisibleCount, reason,
        }); } catch { /* noop */ }
      }
    } catch { /* noop */ }
    // Iter XX.96 — Auto-fire the pending intent captured at the
    // landing's commitment moment. We DON'T fire directly here —
    // send() bails silently while `journey === null`, which is
    // common right after mount. Instead, we flip a state flag and
    // let the pending-intent effect (which watches journey +
    // sessionId + busy) fire when conditions are safe.
    if (!introFinishedRef.current) {
      introFinishedRef.current = true;
      setIntroFinishedTick((n) => n + 1);
    }
  }
  // Iter XX.89 — Paid Instagram persona gate. Tracks whether the
  // stashed persona_context has already been consumed and attached to
  // an outbound /web/chat POST. Used INSTEAD of `hasFirstMessage`
  // because the dream-interstitial branch (Iter XX.58 / XX.86) sets
  // hasFirstMessage=true before the visitor's first real /web/chat
  // POST fires — which would swallow the payload for the entire
  // target cohort (anonymous ad clicks who don't yet have a dream).
  const personaContextConsumedRef = useRef(false);
  // Iter XX.94 — Parent Entry gate. Same one-shot semantics as
  // personaContextConsumedRef above.
  const parentContextConsumedRef = useRef(false);
  const journeyBusyRef = useRef(false);   // guard against double-taps
  const journeyResumeFiredRef = useRef(false);   // proposal fired once per mount

  // Iter XX.59 — Voice MVP state.
  //   voiceSessionActive : the player has used the mic this session at
  //     least once → assistant replies are eligible for TTS autoplay.
  //   voiceMuted         : device-scoped mute preference.
  //   ttsStatus          : "idle" | "loading" | "playing" | "error".
  const [voiceSessionActive, setVoiceSessionActive] = useState(false);
  const [voiceMuted, setVoiceMuted] = useState(() => {
    try { return localStorage.getItem("gully_voice_muted") === "1"; }
    catch { return false; }
  });
  const [ttsStatus, setTtsStatus] = useState("idle");
  const ttsAudioRef = useRef(null);
  const lastSpokenIndexRef = useRef(-1);
  // Timing markers for the "stop → first audio" experiment metric.
  const voiceTurnRef = useRef(null);

  // Iter XX.60 — Realtime Voice entry + overlay ---------------------
  //   realtimeEnabled : founder-only feature flag (from /api/realtime/config)
  //   showLiveOverlay : mount the LiveVoiceOverlay full-screen
  //   entryFiredRef   : fire realtime_entry_viewed exactly once per mount
  const [realtimeEnabled, setRealtimeEnabled] = useState(false);
  const [showLiveOverlay, setShowLiveOverlay] = useState(false);
  const entryFiredRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/realtime/config");
        if (!cancelled) setRealtimeEnabled(!!data?.enabled);
      } catch { if (!cancelled) setRealtimeEnabled(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter XX.72.5 — Chain from /upgrade → identity → Stripe.
  // When Upgrade.jsx sees the user is anonymous, it redirects to
  // /chat?upgrade=1. We open the identity capture immediately so
  // there's no dead-end. On successful link, onLinked (paywall path)
  // routes back to /upgrade?from=paywall&autostart=1.
  useEffect(() => {
    let cancelled = false;
    const qs = new URLSearchParams(window.location.search || "");
    if (qs.get("upgrade") !== "1") return;
    (async () => {
      // Only trigger if the user is still anonymous. If they're
      // already authed, bounce straight back to /upgrade so it can
      // auto-start checkout.
      let _authed = false;
      try { _authed = hasToken(); } catch { /* noop */ }
      if (_authed) {
        window.location.replace("/upgrade?from=paywall&autostart=1");
        return;
      }
      if (cancelled) return;
      setIdentityForPaywall(true);
      try { localStorage.setItem("gully_last_identity_trigger", "upgrade_page"); } catch { /* noop */ }
      api.post("/web/track", {
        session_id: null, event: "plus_identity_required",
        payload: { entry: "upgrade_page_bounce" },
      }).catch(() => {});
      setNeedsIdentity(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter XX.72 — Universal Plus Paywall gate on /chat page-mount.
  // Iter XX.72.4 — Extracted as `recheckEntitlement` so we can re-run
  // it on identity-sheet close, identity-linked callback, window
  // focus (Stripe return), and after IdentitySheet completion. Deny-
  // by-default: if the endpoint fails to load we leave the paywall
  // where it was (never optimistically unlock).
  const recheckEntitlement = React.useCallback(async () => {
    try {
      // Iter XX.205 — pass session_id so the response can carry
      // `your_cricket` for the personalised paywall hero.
      const _sid = (typeof window !== "undefined"
        && localStorage.getItem("gully_web_session_id")) || "";
      const _url = _sid
        ? `/web/entitlement/status?session_id=${encodeURIComponent(_sid)}`
        : "/web/entitlement/status";
      const { data } = await api.get(_url);
      if (data?.paywall_active && !data.entitled) {
        setPaywall((prev) => prev || {
          used: 0, limit: 0,
          reason:      data.reason || "E_NO_ENTITLEMENT",
          surface:     "universal_plus_paywall",
          message:     "You’ve started building a picture of your cricket. Keep Gully learning it with Plus.",
          upgrade_url: data.upgrade_url || "/upgrade?from=universal_paywall",
          plan:        data.plan,
        });
      } else if (data?.paywall_active && data.entitled) {
        // Genuinely entitled now → clear the paywall.
        setPaywall(null);
        // Post-paywall ghosting repair (2026-10-02) — Plus entitlement also
        // clears the arc lock. Conversation history and draft text are
        // preserved (we never clear messages or input here). The next
        // successful send fires `conversation_resumed`.
        if (arcLockedRef.current) {
          arcLockedRef.current = false;
          try { setArcLocked(false); } catch { /* noop */ }
          _resumedAfterLockRef.current = true;
        }
      }
      // If !paywall_active (flag OFF) → legacy behaviour, do nothing.
    } catch { /* fail-closed: keep whatever paywall state we had */ }
  }, []);

  // Iter XX.72 — call on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Iter XX.205 — pass session_id so response carries your_cricket.
        const _sid = (typeof window !== "undefined"
          && localStorage.getItem("gully_web_session_id")) || "";
        const _url = _sid
          ? `/web/entitlement/status?session_id=${encodeURIComponent(_sid)}`
          : "/web/entitlement/status";
        const { data } = await api.get(_url);
        if (cancelled) return;
        if (data?.paywall_active && !data.entitled) {
          setPaywall({
            used: 0, limit: 0,
            reason:      data.reason || "E_NO_ENTITLEMENT",
            surface:     "universal_plus_paywall",
            message:     "You’ve started building a picture of your cricket. Keep Gully learning it with Plus.",
            upgrade_url: data.upgrade_url || "/upgrade?from=universal_paywall",
            plan:        data.plan,
          });
        }
      } catch { /* fail-open — /web/chat 402 is the belt-and-braces gate */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter XX.72.4 — Re-check entitlement whenever the tab regains
  // focus or is re-shown (covers Stripe return, back-nav, refresh
  // after checkout, iOS bfcache restore).
  useEffect(() => {
    const onFocus = () => { recheckEntitlement(); };
    const onPageShow = () => { recheckEntitlement(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [recheckEntitlement]);
  useEffect(() => {
    if (!realtimeEnabled || entryFiredRef.current) return;
    entryFiredRef.current = true;
    trackVoice("realtime_entry_viewed", {});
  }, [realtimeEnabled]);
  const [returnPanel, setReturnPanel] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [showMediaConsent, setShowMediaConsent] = useState(false);
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [paywall, setPaywall] = useState(null);   // { used, limit } | null
  // When true, identity capture is part of the paywall→checkout flow; after a
  // successful /identity call, we auto-navigate to /upgrade?from=paywall so
  // the user doesn't have to click twice. Cleared on identity success or any
  // modal close.
  const [identityForPaywall, setIdentityForPaywall] = useState(false);
  // Iter XX.12 — Cricket Journey Gate. `gateRequired` locks the composer
  // and shows the un-dismissable modal once the engagement score has
  // crossed the threshold on an anonymous session. `gateForcing` is
  // true when the identity sheet is being driven by the gate (so we
  // hide its close button + change the copy tone). `showJourneyStarted`
  // triggers the 4.5s celebration overlay right after linking.
  const [gateRequired, setGateRequired] = useState(false);
  const [gateForcing, setGateForcing] = useState(false);
  const [showJourneyStarted, setShowJourneyStarted] = useState(false);
  // Iter XX.93 — Deferred telemetry emitter for the Cricket Journey
  // Started transition. Populated the moment the overlay is triggered
  // and flushed once the overlay's onFinished fires — that way the
  // single event carries all three founder-requested deltas (auth
  // complete → transition shown → first paint → hydrated) without
  // stitching multiple events together downstream.
  const _pendingTransitionTelemetryRef = useRef(null);
  const [composerH, setComposerH] = useState(0);
  // Iter XX.4 — Gully Plus upgrade surfaces (launch banner + post-report card).
  const { surfaces: plusSurfaces, dismissBanner: dismissPlusBanner, markPostReportShown: markPlusPostReportShown } =
    usePlusUpgradeSurfaces(sessionId);
  const [postReportSeenIndex, setPostReportSeenIndex] = useState(null);
  // Iter XX.71 — Conversation Plus Offer modal state. Populated ONLY
  // when the /web/chat response payload carries `conversation_offer`.
  // Rendered AFTER the assistant message is appended to the thread so
  // the coaching reply is never interrupted.
  const [conversationOffer, setConversationOffer] = useState(null);
  // Iter XX.71.3 — Anonymous Identity Claim prompt state. Shown once
  // per anonymous identity BEFORE the Plus offer is evaluated. Bridges
  // the anon user to a claimed identity (sign-in).
  const [identityClaimPrompt, setIdentityClaimPrompt] = useState(null);
  // Iter XX.6 — Season Card modal state.
  const [seasonOpen, setSeasonOpen] = useState(false);
  // Iter 43.1 / Iter XX.88 — pre-upload expectation-setting helper.
  // Conversation First Mode: the tips no longer auto-show on chat mount.
  // They surface ONLY when the player initiates the upload flow (taps the
  // upload button, or the "Video tips" link). Founder brief: instructions
  // belong inside the upload flow, not competing with the first coaching
  // exchange. Copy is unchanged — this is a timing change only.
  const [showUploadTips, setShowUploadTips] = useState(false);
  // Iter XX.83 — Post-first-message video discoverability. A small,
  // dismissable inline card that appears once per session after the
  // user has sent their first message. Preserves the conversation-
  // first philosophy while making video users feel they're in the
  // right place. Founder brief: "This should appear as a contextual
  // suggestion, not as a large CTA or separate landing-page feature."
  const [showVideoSuggestion, setShowVideoSuggestion] = useState(() => {
    try { return sessionStorage.getItem("g_video_suggestion_dismissed") !== "1"; }
    catch { return true; }
  });
  // Iter XX.88 — Conversation First Mode. Per-turn signal (distinct
  // from the persistent `showVideoUpload` reveal) that flips to true
  // ONLY when Gully's most-recent reply contextually invited a clip.
  // This is what gates the video suggestion card so signed-in users
  // (whose upload reveal defaults to visible) don't see the card
  // ambient on chat mount — it appears only when Gully asks for
  // video, as founder intended.
  const [videoSignaledInLastTurn, setVideoSignaledInLastTurn] = useState(false);
  // Iter 58 — removed the legacy 25MB "large clip" warning. The backend
  // accepts up to 100MB (UPLOAD_MAX_BYTES) so any inline 25MB threshold
  // here was misleading users into thinking we'd reject larger files.
  // The frontend's only hard gate is now the 100MB check in onVideoPicked.
  // Iter 44.1 — Continuity Capture state. Server-side eligibility
  // (anonymous-only, ≥3 successful reviews OR 1+ review + meaningful chat).
  // Refreshed after every chat reply and every successful upload. 7-day
  // cooldown enforced server-side; a dismissal in this render hides it
  // until the next page load (server still suppresses it for 7 days).
  const [continuityEligible, setContinuityEligible] = useState(false);
  const [continuityDismissedInSession, setContinuityDismissedInSession] = useState(false);

  // Iter 62 — First-time onboarding card state. `null` = not yet
  // resolved (don't render anything). Object with `eligible` +
  // `options` after the /onboarding/status call returns. Once a
  // returning-user signal is detected server-side, this stays
  // ineligible permanently for the session.
  const [onboarding, setOnboarding] = useState(null);
  // We emit `onboarding_shown` exactly ONCE per session, even if the
  // card re-renders.
  const onboardingShownRef = useRef(false);

  const videoRef = useRef(null);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  const textRef = useRef(null);
  // Iter 59 — count consecutive fallback-path failures (the non-canonical
  // 5xx/non-JSON branch). After ≥2 fallback failures in a session, we
  // stop offering retry and instead direct the user to upload a new
  // shorter clip. Resets on every successful upload OR page reload.
  const fallbackFailCountRef = useRef(0);

  // --- Init session ---------------------------------------------------------
  //
  // Iter XX.107 (hotfix) — Guard against React 18 StrictMode dev
  // double-invocation. Without this ref, the init effect fires
  // twice: two startSession() POSTs race, two hydrate() calls
  // race, and setMessages([]) inside those async paths clobbers
  // the user bubble that the pending-intent flush effect just
  // pushed via send(). Result: "yorkers in death overs" was
  // stranded silently. The guard makes init strictly one-shot
  // per mounted-and-committed component.
  const initSessionRanRef = useRef(false);
  useEffect(() => {
    if (initSessionRanRef.current) return;
    initSessionRanRef.current = true;
    const existing = loadSession();
    if (existing) {
      setSessionId(existing);
      // Iter XX.77.1 — R3-lite handoff. If we arrived from the
      // R3 onboarding flow with a seed reply that matches THIS
      // session, paint it instantly and skip the "opening_message"
      // greeting. The transition must feel like coaching already
      // began — never "Onboarding complete → Chat started".
      const seed = _consumeR3Seed(existing);
      if (seed) {
        // Optimistic paint — user's Step-4 answer, then Gully's
        // forensic first reply. Marks hasFirstMessage=true so the
        // starter chip row / opener effects are all suppressed.
        //
        // Iter XX.265b — Tag BOTH rows as system-seeded so the Home
        // surface derivation treats them as onboarding continuation,
        // not a fresh meaningful conversation. If the player never
        // types the next turn, the polished Home remains reachable.
        setMessages([
          { role: "user", content: seed.user_message, _system_seeded: true },
          {
            role: "assistant",
            content: seed.assistant_reply,
            attachments: seed.assistant_attachments || [],
            _r3_seeded: true,
            _system_seeded: true,
          },
        ]);
        setHasFirstMessage(true);
        // Surface any post-turn signals that /web/chat returned
        // (conversation_offer, identity_claim_prompt, journey pulse).
        if (seed.conversation_offer) {
          try { setConversationOffer(seed.conversation_offer); }
          catch { /* setter not always in scope on first render */ }
        }
        if (seed.identity_claim_prompt) {
          try { setIdentityClaimPrompt(seed.identity_claim_prompt); }
          catch { /* noop */ }
        }
        if (seed.journey_added) {
          try { setJourneyPulse(true); } catch { /* noop */ }
        }
        // Silent background reconcile — /web/history is the ground
        // truth. If it returns matching rows we replace the optimistic
        // paint; if there's a mismatch (network glitch, mid-air edit)
        // the server view wins.
        hydrate(existing);
      } else {
        hydrate(existing);
      }
    } else {
      startSession();
    }
    // Iter XX.26 — surface the "we found N older conversations" prompt
    // if AuthProvider's post-login claim run stashed one for us. Only
    // fires when there's actually something to add.
    try {
      // eslint-disable-next-line global-require
      const _authMod = require("../lib/auth");
      const p = _authMod.readPendingOlderPrompt && _authMod.readPendingOlderPrompt();
      if (p && p.sessions_skipped_older > 0) setOlderPrompt(p);
    } catch { /* noop */ }
    // Iter 54 cleanup — the legacy first-visit OnboardingFlow modal
    // was retired. The chat IS the onboarding now; the welcome
    // message + chip row + identity prompt cover the same ground.
  }, []);

  // Iter XX.199 — "Gully remembers me". Fetch the server-decided
  // second-open payload once we have a session id AND the message
  // column is empty. All decisions (show / don't show, sentence,
  // continue target) are server-side. Card is lifetime-once per
  // identity; the server enforces that. `messages.length === 0`
  // is intentional: as soon as the player sends anything the
  // moment is over and we don't interrupt the flow.
  useEffect(() => {
    if (!sessionId) return;
    if (secondOpen !== null) return;                // already resolved
    if (messages.length > 0) return;                // mid-conversation
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/web/second-open-context", {
          params: { session_id: sessionId },
        });
        if (cancelled) return;
        if (data && data.show) {
          setSecondOpen({
            salutation: data.salutation || "Welcome back.",
            sentence:   data.sentence   || "",
            sentenceTier:         data.sentence_tier   || "none",
            continueSessionId:    data.continue_session_id || null,
            identity:             data.identity || null,
          });
          // Fire-and-forget: server-side lifetime-once flag.
          try {
            api.post("/web/second-open-context/mark-shown", null, {
              params: { session_id: sessionId },
            }).catch(() => {});
          } catch { /* noop */ }
          try {
            trackFunnel("second_open_experience_viewed", {
              client_platform: (typeof navigator !== "undefined"
                                  && /Android/i.test(navigator.userAgent))
                                 ? "android"
                                 : (typeof navigator !== "undefined"
                                      && /iPad|iPhone|iPod/i.test(navigator.userAgent))
                                    ? "ios" : "web",
              is_authenticated: !!(data.identity
                                    && data.identity.startsWith("user:")),
              sentence_tier: data.sentence_tier || "none",
              has_previous_session_id: !!data.continue_session_id,
            });
          } catch { /* analytics never blocks UX */ }
        } else {
          setSecondOpen(false);  // resolved-negative — never re-ask
        }
      } catch {
        if (!cancelled) setSecondOpen(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, messages.length, secondOpen]);

  // Iter XX.201 — Meta measurement consent bar (Android + iOS, one-time).
  // DECOUPLED from TOS/consentStatus per founder spec (P0 architecture):
  // reads only the local device decision. Consent gating preserved —
  // the bar shows iff isMetaConsentNative() && readMetaConsent() === null.
  //
  // Iter XX.212 — Physical-iPhone race fix. On very cold iOS launches
  // WKWebView starts running our JS bundle a few dozen ms BEFORE the
  // Capacitor bridge attaches. The previous synchronous read locked
  // `metaConsentVisible` OFF for the whole session on that race. Now
  // we AWAIT the bridge (same bounded-poll discipline as
  // `replayMetaConsentOnBoot`) before setting state, and re-run the
  // evaluation whenever TOS acceptance transitions to true (so tapping
  // "OK, let's go" reveals the Meta bar in the same paint cycle).
  useEffect(() => {
    const tosAccepted = !!(consentStatus && consentStatus.tos_accepted);
    if (!tosAccepted) {
      // TOS not yet accepted → nothing to evaluate. Keep the bar hidden
      // so it never stacks above the ConsentBar on the same paint.
      setMetaConsentVisible(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { visible } = await evaluateMetaConsentBarEligibility({
          tosAccepted: true,
        });
        if (!cancelled) setMetaConsentVisible(!!visible);
      } catch {
        if (!cancelled) setMetaConsentVisible(false);
      }
    })();
    return () => { cancelled = true; };
  }, [consentStatus]);

  // Iter XX.58 — Load /api/journey/me once so the FSM knows where to
  // resume. Refresh scenarios: player closed the tab mid-onboarding →
  // we detect the partial state on the server and resume the modal.
  //
  // Iter XX.86 — Hydration-race fix. `journey === null` means the load
  // hasn't resolved yet. The composer's send handler MUST wait for
  // this to complete before it can decide whether to trigger the
  // dream interstitial. `journey === {}` (empty object) means the
  // load finished but nothing is stored (or the caller is anonymous
  // / signed-out — /journey/me returned 401). Neither shape ever
  // re-triggers the dream flow when `.dream` is already present.
  const journeyLoadedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/journey/me");
        if (cancelled) return;
        setJourney(data || {});
        // Recover partial state on refresh:
        //   dream but no next_level → resume at Next Level confirmation
        //   dream + next_level but never had a mission → resume at Mission
        if (data?.dream && !data?.next_level) {
          setJourneyState("WAITING_FOR_NEXT_LEVEL_CONFIRMATION");
        } else if (data?.dream && data?.next_level
                    && !data?.today_mission
                    && (data?.mission_history || []).length === 0) {
          setJourneyState("WAITING_FOR_MISSION_CONFIRMATION");
        }
      } catch {
        if (!cancelled) setJourney({});   // logged-out or error — FSM stays NONE
      } finally {
        if (!cancelled) journeyLoadedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter XX.58 — Resume the modal after a refresh. If we entered a
  // *_CONFIRMATION state via journey/me and no proposal card is in the
  // thread yet, fire the appropriate proposer exactly once.
  useEffect(() => {
    if (journeyResumeFiredRef.current) return;
    if (journeyState === "WAITING_FOR_NEXT_LEVEL_CONFIRMATION" && !journeyProposal) {
      journeyResumeFiredRef.current = true;
      _proposeAndShowNextLevel();
    } else if (journeyState === "WAITING_FOR_MISSION_CONFIRMATION" && !journeyProposal) {
      journeyResumeFiredRef.current = true;
      _proposeAndShowMission();
    }
  }, [journeyState]);

  // Iter XX.58 — "Mark Complete" opener helper. Uses a module-scoped
  // variable so React 18 StrictMode double-mount cannot lose it.
  function _consumePendingMissionOpener() {
    return _pendingMissionOpener || null;
  }
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search || "");
      if (p.get("mission_complete") === "1") {
        _pendingMissionOpener = p.get("opener") || "Nice. Tell me how it went.";
        p.delete("mission_complete");
        p.delete("opener");
        const qs = p.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      }
    } catch { /* noop */ }
  }, []);

  // Iter XX.74 — Fetch journey existence when the flag is ON. Endpoint
  // is auth-required (401 for anonymous) so we silently swallow errors
  // — anonymous or first-time players simply don't see the nav pill
  // until they earn their first commitment. Re-runs on `linked` so
  // identity capture immediately unlocks the pill without a refresh.
  useEffect(() => {
    if (!improvementLoopOn) return;
    let cancelled = false;
    api.get("/journey/status")
      .then((r) => {
        if (!cancelled) setJourneyReady(Boolean(r?.data?.has_journey));
      })
      .catch(() => { /* anonymous / no-journey → hide pill */ });
    return () => { cancelled = true; };
  }, [improvementLoopOn, linked]);

  // Iter XX.73 — Entry-point auto-send. When the LandingLoop tile
  // deep-links to /chat?entry=<kind>, we open the coaching journey
  // with the natural opener for that kind. Runs ONCE per URL, guarded
  // by a ref so React StrictMode double-mount doesn't send twice.
  // Only fires when the flag is ON — flag-OFF preview leaves the
  // URL untouched.
  //
  // Iter XX.73.1 — Video is an exception. Video Analysis begins with
  // the clip, not a chat prompt. When entry=video (or pick=1 present),
  // we open the picker directly and skip the text auto-send.
  const _entryAutosendRef = useRef(false);
  useEffect(() => {
    if (!improvementLoopOn) return;
    if (!sessionId) return;
    if (_entryAutosendRef.current) return;
    if (hasFirstMessage) return;
    try {
      const p = new URLSearchParams(window.location.search || "");
      const entry = p.get("entry");
      const shouldPick = p.get("pick") === "1" || entry === "video";
      if (!entry && !shouldPick) return;
      _entryAutosendRef.current = true;
      // Strip the params so a refresh doesn't re-fire.
      p.delete("entry");
      p.delete("source");
      p.delete("pick");
      const qs = p.toString();
      window.history.replaceState(
        {}, "", window.location.pathname + (qs ? `?${qs}` : ""),
      );
      // Video branch — open the picker, do NOT send an opener message.
      if (shouldPick) {
        setTimeout(() => {
          try { pickVideo?.(); } catch { /* noop */ }
        }, 120);
        return;
      }
      const opener = ENTRY_POINT_OPENERS[entry];
      if (!opener) return;
      setTimeout(() => {
        send(opener, { source: "landing_loop_entry", entry_point: entry });
      }, 120);
    } catch { /* noop */ }
     
  }, [improvementLoopOn, sessionId, hasFirstMessage]);

  // Iter XX.83 — Activation "Review my video" deep-link. When the
  // landing chip is tapped, we navigate here with `?open=video` and
  // open the video picker directly. Runs independently of the
  // improvement-loop flag so the activation chip works for every
  // cohort.
  const _openVideoOnceRef = useRef(false);
  useEffect(() => {
    if (_openVideoOnceRef.current) return;
    if (!sessionId) return;
    try {
      const p = new URLSearchParams(window.location.search || "");
      const openTarget = p.get("open");
      if (openTarget !== "video") return;
      _openVideoOnceRef.current = true;
      p.delete("open");
      const qs = p.toString();
      window.history.replaceState(
        {}, "", window.location.pathname + (qs ? `?${qs}` : ""),
      );
      setTimeout(() => {
        try { pickVideo?.(); } catch { /* noop */ }
      }, 150);
    } catch { /* noop */ }
     
  }, [sessionId]);

  // Iter XX.70 — Post-payment return-to reveal. On mount, if the URL
  // is `?return_to=video:VID_ID`, we mark the corresponding assistant
  // message with `auto_reveal_on_mount=true` so its VideoReviewCard
  // staggers the reveal of the newly-unlocked cards. When the seamless-
  // continuation flag is ON, we then call /synthesis to append a
  // natural continuation message from Gully. Payment is invisible —
  // no welcome, no toast, no celebration UI.
  useEffect(() => {
    if (!sessionId || !messages || !messages.length) return;
    try {
      const p = new URLSearchParams(window.location.search || "");
      const returnTo = p.get("return_to");
      if (!returnTo || !returnTo.startsWith("video:")) return;
      const revealVid = returnTo.slice("video:".length);
      // Only trigger once per URL — clear the param before doing anything.
      p.delete("return_to");
      p.delete("session_id");
      const qs = p.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      // Mark the target message for auto-reveal.
      setMessages((arr) => arr.map((m) => (
        (m.video_analysis_id === revealVid && m.role === "assistant")
          ? { ...m, auto_reveal_on_mount: true }
          : m
      )));
      // Kick off the synthesis message (server decides if the flag is
      // ON; if OFF it returns ok:false and we do nothing).
      (async () => {
        try {
          const { data } = await api.post(
            `/video-review/${encodeURIComponent(revealVid)}/synthesis`,
          );
          if (data && data.ok && data.message) {
            // Iter XX.70.1 — Capture the user's scroll intent BEFORE
            // we append. If they've scrolled up to read the review
            // itself, we don't yank them to the bottom — just append
            // the message. If they're at (or near) the bottom, we
            // smooth-scroll the new message into view so they never
            // miss the natural continuation.
            let atBottom = true;
            try {
              const scrollEl = document.scrollingElement || document.documentElement;
              const distanceFromBottom =
                scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
              atBottom = distanceFromBottom < 120;
            } catch { /* fall back to atBottom=true */ }
            setMessages((arr) => [...arr, {
              role: "assistant",
              content: data.message,
              kind: "video_reveal_synthesis",
              video_analysis_id: revealVid,
              _autoscroll_target: atBottom,
            }]);
            if (atBottom) {
              // Two rAFs so the message has painted before we scroll.
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  try {
                    const el = document.querySelector(
                      `[data-testid="video-reveal-synthesis-anchor-${revealVid}"]`,
                    );
                    if (el && el.scrollIntoView) {
                      el.scrollIntoView({ behavior: "smooth", block: "end" });
                    } else {
                      window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: "smooth",
                      });
                    }
                  } catch { /* noop */ }
                });
              });
            }
          }
        } catch { /* non-fatal — reveal still shows the newly-unlocked cards */ }
      })();
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, messages.length]);

  // Iter 62 — First-time onboarding eligibility. Fetched whenever
  // sessionId resolves. Server-side rules: skip if the user has any
  // prior conversations / videos / reports / aged session. We emit
  // `onboarding_shown` exactly once per session.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API}/web/onboarding/status?session_id=${encodeURIComponent(sessionId)}`,
          { credentials: credentialsMode() },
        );
        if (!r.ok || cancelled) return;
        const data = await r.json();
        setOnboarding(data || { eligible: false });
        // Fire `onboarding_shown` ONLY if we're actually going to
        // show the card (eligible AND no first message yet).
        if (data?.eligible && !hasFirstMessage && !onboardingShownRef.current) {
          onboardingShownRef.current = true;
          try {
            await fetch(`${API}/web/onboarding/event`, {
              method: "POST",
              credentials: credentialsMode(),
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ session_id: sessionId, event: "shown" }),
            });
          } catch { /* analytics-only; non-fatal */ }
        }
      } catch {
        // Non-fatal — chat still works without onboarding.
        setOnboarding({ eligible: false });
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, hasFirstMessage]);

  // Iter XX.92 — Scripted intro scheduler. Kicks in ONLY when the
  // fresh-empty-session branch of hydrate() flagged `introSuppressOpenerRef`.
  // Watches downstream state (onboarding eligibility, journey block,
  // paid-IG gate, first-message flag) so any of those flipping ON before
  // the intro starts short-circuits it — the visitor never sees the intro
  // AND we render the default opener instead so the chat is not empty.
  useEffect(() => {
    if (!introSuppressOpenerRef.current) return;
    if (introRunning) return;
    if (introVisibleCount > 0) return;   // already started (or finished)
    if (hasFirstMessage) return;
    if (messages.length > 0) return;
    // Iter XX.142 — Activation arrivals SKIP the scripted intro so
    // the seeded user-turn + Gully-reply paint immediately. Founder
    // rule: nothing between landing and the first coaching moment.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("from") === "activation") {
        try { localStorage.setItem(INTRO_SHOWN_KEY, "1"); } catch { /* noop */ }
        introSuppressOpenerRef.current = false;
        introFinishedRef.current = true;
        setIntroFinishedTick((n) => n + 1);
        return;
      }
    } catch { /* noop */ }
    if (onboarding === null) return;     // still loading — wait
    // Iter XX.92 — Onboarding bails the intro ONLY when the
    // OnboardingCard is actually going to render (i.e. options[] is
    // populated). Anonymous fresh visitors get `eligible: true` with
    // an empty options[] (see /api/web/onboarding/status), and they
    // ARE the target cohort for the intro. Aligned with the render
    // gate at line ~3852 so behaviour is symmetric.
    const willRenderOnboardingCard = Boolean(
      onboarding?.eligible
      && Array.isArray(onboarding?.options)
      && onboarding.options.length > 0
    );
    if (willRenderOnboardingCard) {
      // Onboarding wins — render the default opener silently and
      // release the suppress flag so we never schedule the intro.
      introSuppressOpenerRef.current = false;
      setMessages([{ role: "assistant", content: DEFAULT_OPENING_MESSAGE }]);
      return;
    }
    if (journeyState && journeyState !== "NONE") return;
    if (showPaidIgGate) return;
    // Iter XX.94 — Parent Entry gate preempts the intro. The gate
    // must complete first so the scripted intro's tone (and every
    // subsequent turn) is oriented toward the correct relationship.
    if (showParentEntryGate) return;
    // Iter XX.106 — scripted intro retired. When the script is
    // empty, mark the intro as "already shown" (durably) AND
    // signal to downstream effects (particularly the pending-
    // intent flush) that the intro-completion phase has passed.
    // Without this signal, XX.96's pending-intent handoff never
    // fires and the user's landing message is dropped — regressed
    // in XX.107 pre-deploy smoke.
    if (INTRO_SCRIPT.length === 0) {
      try { localStorage.setItem(INTRO_SHOWN_KEY, "1"); } catch { /* noop */ }
      introFinishedRef.current = true;
      setIntroFinishedTick((n) => n + 1);
      return;
    }
    // Everything is green — start the sequence.
    introSuppressOpenerRef.current = false;
    setIntroRunning(true);
    setIntroVisibleCount(0);
    setIntroTyping(false);
    introEmittedInterruptRef.current = false;
    try {
      postClientEvent(API, sessionId, "intro_started", {});
      try { clarityTrack("intro_started", {}); } catch { /* noop */ }
    } catch { /* noop */ }
    const timers = [];
    INTRO_CADENCE.forEach((tick, idx) => {
      // Typing indicator ON at this step.
      timers.push(setTimeout(() => setIntroTyping(true), tick.typing_at));
      // Message revealed — indicator OFF, count++.
      timers.push(setTimeout(() => {
        setIntroTyping(false);
        setIntroVisibleCount((n) => Math.max(n, idx + 1));
        if (idx === INTRO_CADENCE.length - 1) {
          // Final message shown — close the sequence.
          _cancelIntro("completed");
        }
      }, tick.message_at));
    });
    introTimersRef.current = timers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboarding, hasFirstMessage, messages.length, journeyState, showPaidIgGate, showParentEntryGate, sessionId]);

  // Iter XX.92 — cleanup on unmount so no timer fires after the
  // Chat page has been left. Clears both the intro sequencer AND the
  // interruption ref so returning to /chat starts fresh.
  useEffect(() => {
    return () => {
      try { introTimersRef.current.forEach((t) => clearTimeout(t)); } catch { /* noop */ }
      introTimersRef.current = [];
    };
  }, []);

  // Iter XX.96 — Pending-intent flusher. Fires the stashed original
  // message from the landing's Parent Entry gate handoff ONLY when
  // every downstream guard is satisfied:
  //   · intro sequence has ended (introFinishedRef set by _cancelIntro)
  //   · /api/journey/me has resolved (`journey !== null`) — send()
  //     silently bails otherwise, which is exactly the race that
  //     stranded messages during commit-moment testing.
  //   · sessionId + not-busy + not-in-journey-FSM.
  // Guarded by pendingIntentFiredRef so it can NEVER double-send.
  useEffect(() => {
    if (!introFinishedRef.current) return;
    if (pendingIntentFiredRef.current) return;
    const intent = pendingIntentRef.current;
    if (!intent) return;
    if (!sessionId) return;
    if (journey === null) return;
    // Iter XX.149 — Instant-navigate landing→chat handoff.
    // We deliberately set busy=true optimistically in the mount
    // effect to show typing dots the moment the user lands on
    // /chat. Since we're firing the very send() that owns this
    // busy state, the historic `if (busy) return;` guard is
    // deliberately dropped on the pending-intent path.
    if (journeyState !== "NONE") return;
    if (showParentEntryGate) return;
    pendingIntentFiredRef.current = true;
    pendingIntentRef.current = null;
    // Iter XX.107 (hotfix) — Fire send() synchronously. The prior
    // setTimeout(400) delay was cosmetic (space for intro line to
    // settle) but React 18 StrictMode dev double-mount would cancel
    // the timeout via cleanup, then the fresh mount's flush effect
    // would bail because pendingIntentFiredRef is preserved across
    // the simulated remount — stranding "yorkers in death overs"
    // in the preview smoke. Since INTRO_SCRIPT is empty in XX.106,
    // there is no intro to wait for; fire immediately. Storage is
    // consumed here (atomic with fire) so a page refresh cannot
    // replay the message.
    try { consumePendingIntent(); } catch { /* noop */ }
    try {
      send(intent.text, {
        source: intent.source || "activation_pending_resume",
        _fromPendingIntent: true,
        _t_tap: intent.t_tap || null,
        // Iter XX.172 — forward experiment metadata on the first
        // /web/chat POST. Never enters the LLM prompt — audit /
        // funnel storage only.
        entry_experiment: intent.entry_experiment || null,
        entry_problem:    intent.entry_problem    || null,
        entry_situation:  intent.entry_situation  || null,
        // Iter XX.254 — deterministic starter hook. Forwarded to
        // /web/chat below so the backend can trigger the format-
        // clarification override when the visitor tapped the
        // landing chip that declares an explicit starter_id.
        starter_id:       intent.starter_id       || null,
      });
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introFinishedTick, journey, sessionId, busy, journeyState, showParentEntryGate]);

  // Iter XX.97 — Cricket Memory save poller. When the most recent
  // assistant message carries a `cricket_memory_confirmation` with
  // status="saving", poll the backend every 900ms (up to ~15s) until
  // status flips to "saved" (render the full confirmation card) or
  // "failed" (swap to the graceful fallback copy). On any error we
  // land in the fallback branch — never leaves the card stuck in
  // "saving" forever.
  useEffect(() => {
    if (!sessionId) return;
    const last = messages.length ? messages[messages.length - 1] : null;
    if (!last || last.role !== "assistant") return;
    const conf = last.cricket_memory_confirmation;
    if (!conf || conf.status !== "saving") return;
    let cancelled = false;
    let attempts = 0;
    const MAX = 18;  // 18 * 900ms = ~16s upper bound
    const _poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(
          "/web/memory/parent_child_profile",
          { params: { session_id: sessionId } },
        );
        if (cancelled) return;
        if (data && (data.status === "saved" || data.status === "failed")) {
          setMessages((arr) => {
            if (!arr.length) return arr;
            const copy = arr.slice();
            for (let i = copy.length - 1; i >= 0; i--) {
              const m = copy[i];
              if (m.role !== "assistant") continue;
              const c = m.cricket_memory_confirmation;
              if (!c || c.status !== "saving") continue;
              if (c.save_id && data.save_id && c.save_id !== data.save_id) {
                // A newer save superseded this one — leave the stale
                // saving card in place; the newer message will pick up
                // its own poll cycle.
                break;
              }
              copy[i] = {
                ...m,
                content: data.status === "saved"
                  ? "Saved to your Cricket Memory. Here's what I've got:"
                  : ("Got it — I'll remember. (Some details couldn't be "
                     + "structured just now — feel free to share them "
                     + "again when it's convenient.)"),
                cricket_memory_confirmation: {
                  status:        data.status,
                  save_id:       data.save_id || c.save_id,
                  newly_saved:   data.newly_saved || [],
                  already_known: data.already_known || [],
                  profile:       data.profile || {},
                  saved_at:      data.saved_at || null,
                },
              };
              break;
            }
            return copy;
          });
          return;
        }
      } catch { /* transient — keep polling */ }
      if (attempts >= MAX) {
        // Timeout — degrade to graceful fallback so the card doesn't
        // remain stuck in "saving" indefinitely.
        setMessages((arr) => {
          if (!arr.length) return arr;
          const copy = arr.slice();
          for (let i = copy.length - 1; i >= 0; i--) {
            const m = copy[i];
            if (m.role !== "assistant") continue;
            const c = m.cricket_memory_confirmation;
            if (!c || c.status !== "saving") continue;
            copy[i] = {
              ...m,
              content: ("Got it — I'll remember. (Some details couldn't "
                        + "be structured just now — feel free to share "
                        + "them again when it's convenient.)"),
              cricket_memory_confirmation: {
                ...c,
                status: "failed",
                reason: "poll_timeout",
              },
            };
            break;
          }
          return copy;
        });
        return;
      }
      setTimeout(_poll, 900);
    };
    const t = setTimeout(_poll, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, sessionId]);

  // Iter XX.79 — Clarity funnel events driven off `messages` count.
  //   · `conversation_started`               → first user message
  //   · `first_meaningful_conversation_completed` → 3rd user message
  //     (defines "meaningful" as ≥3 user turns — a real back-and-forth,
  //      not just a hi-and-bye). Sufficient signal for the founder's
  //      current review needs; can be tightened later.
  const _clarityConvStartedRef = useRef(false);
  const _clarityMeaningfulRef = useRef(false);
  const _funnelFirstReplyRef = useRef(false);
  const _funnelSecondMsgRef = useRef(false);
  useEffect(() => {
    const userTurns = messages.filter((m) => m.role === "user").length;
    const asstTurns = messages.filter((m) => m.role === "assistant").length;
    if (!_clarityConvStartedRef.current && userTurns >= 1) {
      _clarityConvStartedRef.current = true;
      // Iter XX.87 — kept for backward-compat dashboards.
      clarityTrackOnce("conversation_started",
                        { path: "/chat" }, "conversation_started");
    }
    // Iter XX.142 — Activation funnel:
    //   · first_gully_reply_painted — Gully's seed reply is on-screen
    //   · second_user_message_sent  — visitor sent turn 2
    //   · first_coaching_moment     — 3rd user turn (real back-and-forth)
    // Iter XX.172 — Read the persisted experiment arm ONCE per
    // sessionful of turns so downstream funnel events carry the same
    // `entry_experiment` that fired `primary_cta_clicked`. Fail-safe
    // to null: control arm still tags cleanly via the landing page.
    let _entry_experiment = null;
    try {
      // eslint-disable-next-line global-require
      _entry_experiment = require("../lib/entryExperiment").readPersistedEntryVariant();
    } catch { /* noop */ }
    try {
      if (!_funnelFirstReplyRef.current && userTurns >= 1 && asstTurns >= 1) {
        _funnelFirstReplyRef.current = true;
        // Lazy-load to keep bundle small on non-chat routes.
        import("../lib/activationFunnel").then(({ trackFunnel }) => {
          trackFunnel("first_gully_reply_painted", {
            session_id: sessionId,
            ...(_entry_experiment ? { entry_experiment: _entry_experiment } : {}),
          });
        }).catch(() => { /* noop */ });
      }
      if (!_funnelSecondMsgRef.current && userTurns >= 2) {
        _funnelSecondMsgRef.current = true;
        import("../lib/activationFunnel").then(({ trackFunnel }) => {
          trackFunnel("second_user_message_sent", {
            session_id: sessionId,
            ...(_entry_experiment ? { entry_experiment: _entry_experiment } : {}),
          });
        }).catch(() => { /* noop */ });
      }
    } catch { /* noop */ }
    if (!_clarityMeaningfulRef.current && userTurns >= 3) {
      _clarityMeaningfulRef.current = true;
      clarityTrackOnce("first_meaningful_conversation_completed",
                        { user_turns: userTurns },
                        "first_meaningful_conversation_completed");
      try {
        import("../lib/activationFunnel").then(({ trackFunnel }) => {
          trackFunnel("first_coaching_moment", {
            session_id: sessionId, user_turns: userTurns,
            ...(_entry_experiment ? { entry_experiment: _entry_experiment } : {}),
          });
        }).catch(() => { /* noop */ });
      } catch { /* noop */ }
    }
    // Iter XX.82 — Turn-based reveal removed. Progressive reveal is
    // now context-aware: server emits `reveals: [stages]` on every
    // /web/chat response, and `applyServerReveals()` merges into
    // localStorage. See progressiveReveal.js for the full contract.
  }, [messages, sessionId]);

  // ------------------------------------------------------------------
  // Iter XX.87 — chat_opened + composer focus + first_message_sent.
  //
  //   chat_opened                  — once, when /chat is interactive
  //                                   (sessionId available, DOM painted)
  //   first_message_composer_focused — once, first focus of the textarea
  //   first_message_sent           — once, when the FIRST valid user
  //                                   message is ACCEPTED for sending
  //                                   (before /web/chat resolves — so
  //                                    empty/rejected/failed sends
  //                                    never fire it)
  // All dedup'd per session.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId) return;
    clarityTrackOnce("chat_opened", { path: "/chat" });
  }, [sessionId]);

  const _composerFocusFiredRef = useRef(false);
  const _handleComposerFocusOnce = () => {
    if (_composerFocusFiredRef.current) return;
    _composerFocusFiredRef.current = true;
    clarityTrackOnce("first_message_composer_focused", { path: "/chat" });
  };

  // Iter XX.82 — Hydrate revealed stages from the server on mount so
  // the cumulative unlock set survives reloads / device switches.
  // Server is the source of truth; local state is a cache.
  useEffect(() => {
    hydrateFromServer();
  }, []);

  // Iter XX.88 — Conversation First Mode. Derived count of user turns
  // used to gate secondary surfaces (Today's Report chip). We hide them
  // through the first coaching exchange (user sends msg → assistant
  // reply) and start surfacing once the player continues the
  // conversation (≥ 2 user messages).
  const userMessageCount = useMemo(
    () => messages.filter((m) => m.role === "user").length,
    [messages],
  );

  // Iter XX.81 — Reveal gates. Signed-in returning users already
  // "know" the product, so their gates default open. Anonymous
  // first-timers see gates driven purely by the local reveal state.
  // Chat.jsx doesn't hold an auth context — we detect signed-in
  // status via nativeTokenStorage (localStorage on web, Keychain /
  // EncryptedSharedPreferences on native — XX.177).
  const revealDefault = (() => {
    try { return hasToken(); }
    catch { return false; }
  })();
  const showMemory      = useReveal("memory",      { defaultVisible: revealDefault });
  const showSeason      = useReveal("season",      { defaultVisible: revealDefault });
  const showEarn        = useReveal("earn",        { defaultVisible: revealDefault });
  const showPlusBanner  = useReveal("plus_banner", { defaultVisible: revealDefault });
  // Iter XX.160.1 — Video-intent entry detection. When the visitor
  // arrived via the landing chip `/chat?open=video`, we (a) force
  // the composer's upload button visible even for a zero-turn
  // visitor, and (b) render a prominent inline upload hero card so
  // they have an obvious click target if the auto-open pickVideo()
  // was blocked by the browser gesture policy (Chrome/Safari
  // routinely block programmatic .click() on file inputs that
  // happen after a page navigation because the user gesture from
  // the landing tap doesn't carry across the route change). The
  // hero card DISAPPEARS the moment an upload starts, a user
  // message is sent, or an assistant coaching turn has painted —
  // it is a first-frame nudge, not a persistent surface.
  const [_videoEntryActive, _setVideoEntryActive] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search || "");
      return p.get("open") === "video";
    } catch { return false; }
  });
  const showVideoUpload = useReveal("video", {
    defaultVisible: revealDefault || _videoEntryActive,
  });

  // Iter XX.82 — Session-count-based reveals (home_screen / earn)
  // removed with the turn-based reveal system. Those stages are
  // now driven by contextual signals from the server, not a mount
  // counter.

  // --- Iter XX.33 — pending-upload rehydration ----------------------------
  // On mount, read localStorage.gully_pending_uploads and re-check the
  // status of each entry via GET /api/video/status/{trace_id}. This is
  // the fix for "client timeout / tab close / refresh during processing
  // hides the review that DID complete on the server."
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const rehydrate = async () => {
      const pending = _readPendingUploads();
      const entries = Object.entries(pending);
      if (entries.length === 0) return;
      const now = Date.now();
      for (const [traceId, rec] of entries) {
        try {
          // Sweep records older than 60 minutes — the backend TTL for
          // both video_review_jobs and pending_clarification is 60m.
          const started = Date.parse(rec.started_at || "");
          if (started && (now - started) > 60 * 60 * 1000) {
            _clearPendingUpload(traceId);
            continue;
          }
          const { data } = await api.get(
            `/web/video/status/${encodeURIComponent(traceId)}`,
            { params: { session_id: sessionId } },
          );
          if (cancelled) return;
          if (!data || !data.status) continue;
          // Terminal — show the assistant message and clear the entry.
          if (data.status === "completed") {
            if (data.assistant_message) {
              setMessages((m) => {
                // Avoid duplicating if the message already exists.
                const exists = m.some(
                  (x) => x.video_analysis_id && x.video_analysis_id === data.video_analysis_id,
                );
                if (exists) return m;
                return [...m, {
                  role: "assistant",
                  kind: "video_review",
                  content: data.assistant_message,
                  video_analysis_id: data.video_analysis_id,
                  upload_trace_id: traceId,
                  server_recovered: true,
                }];
              });
            }
            _clearPendingUpload(traceId);
          } else if (data.status === "failed") {
            if (data.user_facing_message) {
              setMessages((m) => [...m, {
                role: "assistant",
                kind: "video_review_failed",
                content: (data.user_facing_message +
                          (data.user_action_required
                             ? `\n\n${data.user_action_required}` : "")),
                trace_ref: data.video_analysis_id || traceId,
                can_retry: !!data.retryable,
                upload_trace_id: traceId,
              }]);
            }
            _clearPendingUpload(traceId);
          } else if (data.status === "awaiting_identity") {
            setMessages((m) => [...m, {
              role: "assistant",
              kind: "awaiting_identity",
              content:
                data.user_facing_message ||
                "I received the video. I just need to know which player you are before I show the review.",
              clarification_kind: "awaiting_identity",
              clarification_chips: [
                { id: "bowler",  label: "🎳 Bowler" },
                { id: "batter",  label: "🏏 Batter" },
                { id: "keeper",  label: "🧤 Keeper" },
                { id: "fielder", label: "🛡️ Fielder" },
              ],
            }]);
          }
          // Non-terminal (received/validating/normalizing/analysing)
          // — leave in localStorage; a subsequent poll will pick it up.
        } catch {
          // 404 (no artifact) or network error — leave the entry alone
          // and try again on the next mount.
        }
      }
    };
    rehydrate();
    return () => { cancelled = true; };
  }, [sessionId]);

  // --- Auto-scroll on new content ------------------------------------------
  useEffect(() => {
    // Use rAF + a tiny timeout so the scroll happens AFTER layout settles
    // (mobile keyboards reflow asynchronously).
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  }, [messages, busy, uploading]);

  // --- Visual-viewport-driven layout (Android Chrome, Instagram/FB WebView)
  // We set the OUTER container's height from the SHORTEST of:
  //   - window.visualViewport.height
  //   - window.innerHeight
  // because Instagram/FB Android WebView often reports a stale vv.height
  // that does NOT shrink with the keyboard, while window.innerHeight does.
  // Taking the min handles both: standalone Chrome/Safari (vv shrinks) AND
  // Instagram WebView (innerHeight shrinks).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    const root = document.documentElement;

    function applyVVHeight() {
      const innerH = window.innerHeight;
      const vvH = vv ? vv.height : innerH;
      const effective = Math.min(innerH, vvH);
      // vv.offsetTop is the y-coordinate of the visual viewport relative to
      // the layout viewport. In iOS Safari this is non-zero whenever the
      // address bar is visible — anchoring the chat at top:0 then leaves a
      // gap below it equal to offsetTop. We must offset the chat down by
      // exactly that much so the chat fills the *visible* region.
      const top = vv ? Math.max(0, vv.offsetTop) : 0;
      root.style.setProperty("--vv-h", `${effective}px`);
      root.style.setProperty("--vv-top", `${top}px`);
    }

    function onChange() {
      applyVVHeight();
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    }

    applyVVHeight();
    if (vv) {
      vv.addEventListener("resize", onChange);
      vv.addEventListener("scroll", onChange);
    }
    window.addEventListener("resize", onChange);

    // Lock body scroll while the chat is mounted — prevents ancestor
    // scrolling from pushing the composer below the soft keyboard in
    // Android WebView (the actual reported bug).
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overscrollBehavior = "none";

    return () => {
      if (vv) {
        vv.removeEventListener("resize", onChange);
        vv.removeEventListener("scroll", onChange);
      }
      window.removeEventListener("resize", onChange);
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
    };
  }, []);

  // --- Track composer height (used only for "is something visible above
  // it" calculations; layout no longer needs it).
  useEffect(() => {
    function measure() {
      if (composerRef.current) setComposerH(composerRef.current.offsetHeight);
    }
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && composerRef.current) ro.observe(composerRef.current);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [returnPanel, consentStatus, linked]);

  async function startSession() {
    try {
      // Iter 71 — Capture UTM params from the landing URL on first
      // session creation so downstream events can attribute SMS/social
      // campaign conversions. Cached in localStorage for the tab's
      // lifetime (session_id also lives there) so a page reload before
      // the session materialises doesn't lose the attribution.
      const utmPayload = _readAndCacheUtm();
      // Iter XX.26 — send our persistent browser_id so the (later,
      // lazy-created) web_sessions row is stamped with it. Enables
      // cross-tab claiming after sign-in.
      let _bid = null;
      try {
        // eslint-disable-next-line global-require
        _bid = (require("../lib/browserId").getBrowserId)();
      } catch { /* noop */ }
      // Iter XX.149 — Landing→chat handoff: if the landing page
      // stashed a pending intent with its landing_page_id, forward
      // it to /web/session so attribution / audience routing stays
      // intact. Peek (non-destructive) — the intent is consumed
      // atomically by the pending-intent flusher when it fires
      // send(). Pure-ASCII no-op for direct /chat visitors.
      let _landing_page_id = null;
      try {
        const _intent = peekPendingIntent();
        if (_intent && _intent.landing_page_id) {
          _landing_page_id = _intent.landing_page_id;
        }
      } catch { /* noop */ }
      const _t_start = (typeof performance !== "undefined" && performance.now)
        ? performance.now() : Date.now();
      const { data } = await api.post("/web/session", {
        ...utmPayload,
        browser_id: _bid,
        ...(_landing_page_id ? { landing_page_id: _landing_page_id } : {}),
      });
      try {
        const _t_end = (typeof performance !== "undefined" && performance.now)
          ? performance.now() : Date.now();
        // eslint-disable-next-line no-console
        console.info("[gully.latency] chat.session-created", {
          ms: Math.round(_t_end - _t_start),
          landing_page_id: _landing_page_id,
        });
      } catch { /* noop */ }
      setSessionId(data.session_id);
      saveSession(data.session_id);
      // Iter XX.149 — Preserve optimistic user bubble painted from
      // the landing→chat handoff. If we've already painted the
      // visitor's message (pending-intent path), keep it and prepend
      // any opener the server returned. Otherwise use the historic
      // behaviour (opener OR intro-suppression empty state).
      const _hasOptimisticUserBubble = !!pendingIntentRef.current;
      const mcOpener = _consumePendingMissionOpener();
      if (mcOpener) {
        // Mission-complete opener present — render as before, skip
        // the Iter XX.92 intro.
        if (_hasOptimisticUserBubble) {
          setMessages((prev) => [
            { role: "assistant", content: data.opening_message },
            { role: "assistant", content: mcOpener },
            ...prev,
          ]);
        } else {
          setMessages([
            { role: "assistant", content: data.opening_message },
            { role: "assistant", content: mcOpener },
          ]);
        }
      } else {
        // Iter XX.92 — Fresh new session (localStorage was empty).
        // Suppress the server opener so the intro scheduler effect
        // can play the 4-line sequence. The scheduler will fall back
        // to the server opener if it's blocked (onboarding-eligible,
        // journey non-NONE, paid-IG gate).
        // If this browser has ALREADY seen the intro on a prior
        // visit (`g_intro_v1_shown`), skip straight to the default
        // opener — the intro is a once-per-visitor experience.
        let _introAlreadyShown = false;
        try { _introAlreadyShown = localStorage.getItem(INTRO_SHOWN_KEY) === "1"; }
        catch { /* noop */ }
        if (_hasOptimisticUserBubble) {
          // Coach voice: the visitor already sees their own message.
          // Skip the opener/intro entirely — the reply that lands
          // after /web/chat resolves IS the opening moment.
          introSuppressOpenerRef.current = true;
        } else if (_introAlreadyShown) {
          setMessages([{ role: "assistant", content: data.opening_message }]);
        } else {
          introSuppressOpenerRef.current = true;
          setMessages([]);
        }
      }
      fetchConsentStatus(data.session_id);
    } catch (e) {
      toast.error("Couldn't start your Gully session. Try refreshing.");
      // Iter XX.149 — If we painted an optimistic user bubble +
      // typing dots from the landing→chat handoff, clear the dots
      // and surface a coach-voice inline error so the visitor is
      // never stuck staring at typing dots forever when the session
      // POST failed. The bubble stays so their message isn't lost.
      if (pendingIntentRef.current) {
        try {
          setBusy(false);
          setMessages((m) => [...m, {
            role: "assistant",
            content: "Couldn't reach me just now — check your connection and tap send again. Your message is right above.",
          }]);
        } catch { /* noop */ }
      }
    }
  }

  // Iter XX.21 — Switch to a different conversation thread. Updates
  // localStorage + re-hydrates messages. Long-term memory (player
  // identity, journey, reports, patterns) lives on user_id server-side
  // and follows the player across threads — nothing to reset here.
  async function handleSwitchSession(newSid, isNew) {
    if (!newSid || newSid === sessionId) { setSidebarOpen(false); return; }
    try {
      setSessionId(newSid);
      saveSession(newSid);
      setSidebarOpen(false);
      if (isNew) {
        // Fresh thread — clear the message view and show the opener.
        setMessages([{ role: "assistant", content: DEFAULT_OPENING_MESSAGE }]);
        // Reset transient UI state that shouldn't leak between threads.
        setInput("");
      } else {
        // Existing thread — pull its history.
        await hydrate(newSid);
      }
      // Re-check consent status on the new thread.
      fetchConsentStatus(newSid);
    } catch {
      toast.error("Couldn't switch conversation. Try again.");
    }
  }

  async function hydrate(sid) {
    try {
      // Iter XX.107 (hotfix) — /web/me can 404 for a pre-warmed
      // session that has no chat turns yet (activation-landing
      // pre-warm path). Treat 404 here as "not linked, keep
      // going" — do NOT let it cascade into the outer catch that
      // fires startSession() and wipes the messages that the
      // pending-intent flush just painted.
      try {
        const me = await api.get("/web/me", { params: { session_id: sid } });
        setLinked(!!me.data?.is_linked);
      } catch {
        setLinked(false);
      }
      const h = await api.get("/web/history", { params: { session_id: sid } });
      const rows = (h.data?.messages || []).map((m) => {
        // Iter 42.1 — extract billing CTA from the saved attachment so it
        // renders as a button when the message is re-loaded from history.
        const billingAtt = (m.attachments || []).find(
          (a) => a && a.kind === "billing_cta" && a.cta_url
        );
        return {
          role: m.role, content: m.content,
          attachments: m.attachments || [],
          billing_cta: billingAtt ? {
            intent: billingAtt.intent,
            label: billingAtt.cta_label,
            url: billingAtt.cta_url,
            // Iter XX.223 — carry conversion_context_id + cta_variant
            // through the frontend so the tap handler can fire
            // `plus_conversion_cta_clicked` on the same funnel id.
            conversion_context_id: billingAtt.conversion_context_id || null,
            cta_variant: billingAtt.cta_variant || billingAtt.intent,
          } : null,
          // Iter XX.85 — Coach recommendation card persistence.
          // Server stores the block on the assistant web_messages row
          // (see web_chat.py "coach_recommendation persistence"). We
          // simply thread it back so the card re-renders on reload.
          coach_recommendation: (m.coach_recommendation
            && m.coach_recommendation.text) ? m.coach_recommendation : null,
          // Iter XX.97 — Cricket Memory confirmation card persistence.
          cricket_memory_confirmation: m.cricket_memory_confirmation || null,
          // Iter XX.111 — Session memory card persistence. Server
          // persists `session_memory` on the assistant web_messages
          // row for the turn the arc closed; re-thread it so the
          // card survives a page reload.
          session_memory: (m.session_memory
            && m.session_memory.observation) ? m.session_memory : null,
          // Iter 69 — session boundary scalars used by <SessionDivider>.
          // Only user messages carry these server-side; assistant rows
          // will have them undefined which is fine — the divider logic
          // treats undefined as "same session".
          session_index: m.session_index,
          is_new_session_start: !!m.is_new_session_start,
          day_bucket: m.day_bucket || "",
          created_at: m.created_at || null,
          // Iter XX.258 — Gully Initiates V1. Server persists these on
          // proactive-return assistant rows so refresh does not
          // duplicate and the ack lifecycle can key off them.
          source_kind: m.source_kind || null,
          gully_initiate: m.gully_initiate || null,
        };
      });
      if (rows.length === 0) {
        const mcOpener = _consumePendingMissionOpener();
        if (mcOpener) {
          // Mission-complete opener present — skip the Iter XX.92
          // intro and render the mission-flavoured opener as before.
          setMessages([
            { role: "assistant", content: DEFAULT_OPENING_MESSAGE },
            { role: "assistant", content: mcOpener },
          ]);
        } else {
          // Iter XX.92 — Fresh empty session with no mission opener.
          // Suppress DEFAULT_OPENING_MESSAGE (M4 of the intro delivers
          // the equivalent question) and let the intro scheduler
          // effect run instead. If the scheduler is prevented from
          // firing (e.g. onboarding-eligible interstitial), we
          // fall back to the default opener via that effect.
          // Skip entirely if this browser has already seen the intro.
          let _introAlreadyShown = false;
          try { _introAlreadyShown = localStorage.getItem(INTRO_SHOWN_KEY) === "1"; }
          catch { /* noop */ }
          if (_introAlreadyShown) {
            setMessages([{ role: "assistant", content: DEFAULT_OPENING_MESSAGE }]);
          } else {
            introSuppressOpenerRef.current = true;
            setMessages([]);
          }
        }
      } else {
        const mcOpener = _consumePendingMissionOpener();
        setMessages(mcOpener ? [...rows, { role: "assistant", content: mcOpener }] : rows);
        // Iter XX.265b — Fresh app launch / meaningful return visit
        // for an established user ⇒ land on the polished Home by
        // default. Uses a per-browser-tab sessionStorage marker so
        // an actively-chatting player who navigates back to /chat
        // mid-session does NOT get bounced back to Home. First hit
        // per tab: pin Home. Subsequent hits within the same tab:
        // leave homePinned=false (send/starter/continue all clear
        // this marker via _consumePendingIntent-style flow — see
        // send() and pickVideo() where homePinned unpins). Only
        // fires when there IS meaningful user content to preserve.
        try {
          const KEY = "g_home_landed_this_tab";
          if (!sessionStorage.getItem(KEY)) {
            const genuine = rows.some((m) => (
              m && m.role === "user"
              && typeof m.content === "string"
              && m.content.trim().length > 0
            ));
            if (genuine) {
              setHomePinned(true);
              sessionStorage.setItem(KEY, "1");
              try {
                postClientEvent(API, sid, "authed_home_landed_on_return", {});
              } catch { /* noop */ }
            }
          }
        } catch { /* sessionStorage may be unavailable */ }
      }
      try {
        const rx = await api.get("/web/return-experience", { params: { session_id: sid } });
        if (rx.data?.show_panel) setReturnPanel(rx.data);
      } catch { /* noop */ }
      fetchConsentStatus(sid);
      // Iter 44.1 — server tells us if the player is anonymous AND has hit
      // the high-value threshold (and isn't in the 7-day cooldown). Cheap
      // single read; safe on every hydrate.
      refreshContinuityEligibility(sid);

      // ------------------------------------------------------------
      // Iter XX.258 — Gully Initiates V1
      // On authed return, ask the server whether a proactive memory-
      // grounded message should open the next conversation. Cheap
      // deterministic gate + short 6h cache on the backend, so this
      // is safe to call once per hydrate. Skipped when:
      //   · not authed
      //   · a landing / starter / mission-complete pending intent is
      //     staged
      //   · already checked this Chat mount (ref guard)
      //   · an initiation-shaped message already lives in history
      //     without a subsequent user reply (dedup: ack:replied is
      //     what clears that state, not another /initiate/check call)
      // ------------------------------------------------------------
      try {
        // Compute "any pending intent that would conflict".
        const hasPendingIntent = (() => {
          try {
            if (peekPendingIntent && peekPendingIntent()) return true;
          } catch { /* noop */ }
          try {
            const p = new URLSearchParams(window.location.search || "");
            if (p.get("starter_id") || p.get("mission_complete") === "1") return true;
          } catch { /* noop */ }
          return false;
        })();

        // History-side detection of an existing (un-acked) initiation.
        let historyInitiationRow = null;
        let historyInitiationIndex = -1;
        for (let i = rows.length - 1; i >= 0; i -= 1) {
          const r = rows[i];
          if (r && r.role === "assistant" &&
              (r.source_kind === "gully_initiate" ||
               (r.gully_initiate && r.gully_initiate.reason_type))) {
            historyInitiationRow = r;
            historyInitiationIndex = i;
            break;
          }
        }
        if (historyInitiationRow) {
          // Was there a user reply after this initiation? If yes,
          // ack:replied already happened on that turn's send() path
          // (or should have — this guard is best-effort).
          let userReplyAfter = false;
          for (let j = historyInitiationIndex + 1; j < rows.length; j += 1) {
            if (rows[j] && rows[j].role === "user") { userReplyAfter = true; break; }
          }
          if (!userReplyAfter) gullyInitiatePendingRef.current = true;
        }

        if (!gullyInitiateCheckedRef.current &&
            !hasPendingIntent &&
            !historyInitiationRow) {
          gullyInitiateCheckedRef.current = true;
          try {
            const { data } = await api.post("/web/initiate/check",
              { session_id: sid });
            if (data && data.initiated && data.message) {
              gullyInitiatePendingRef.current = true;
              setMessages((m) => {
                // Idempotent: never append if the last assistant row
                // already carries the same content (defence against
                // parallel hydrates / effects).
                const last = m.length ? m[m.length - 1] : null;
                if (last && last.role === "assistant" &&
                    last.content === data.message) return m;
                return [...m, {
                  role: "assistant",
                  content: data.message,
                  source_kind: "gully_initiate",
                  gully_initiate: {
                    reason_type: data.reason_type || null,
                  },
                }];
              });
            }
          } catch { /* noop — never let initiate errors break chat */ }
        }
      } catch { /* noop */ }
    } catch (e) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      startSession();
    }
  }

  async function fetchConsentStatus(sid) {
    try {
      const { data } = await api.get("/web/consent-status", { params: { session_id: sid } });
      setConsentStatus(data);
    } catch { setConsentStatus(null); }
  }

  // Iter 44.1 — server-side eligibility check for the Continuity Capture
  // prompt. Anonymous-only, with founder-confirmed trigger thresholds and
  // a 7-day dismissal cooldown enforced by the backend. Safe to call on
  // every reply/upload — single read, no LLM, cheap.
  async function refreshContinuityEligibility(sidOverride) {
    const sid = sidOverride || sessionId;
    if (!sid) return;
    if (linked) { setContinuityEligible(false); return; }
    if (continuityDismissedInSession) return;
    try {
      const { data } = await api.get("/web/continuity/status",
        { params: { session_id: sid } });
      setContinuityEligible(!!data?.eligible);
    } catch {
      setContinuityEligible(false);
    }
  }

  // --- Send ----------------------------------------------------------------
  async function send(textOverride, opts = {}) {
    const text = (textOverride ?? input).trim();
    // Iter XX.265b — Un-pin the Home surface on ANY genuine send.
    // If the user was on the pinned Home surface with a transcript
    // underneath, this is the moment they left Home. The send bubble
    // itself will be appended below via setMessages; unpinning here
    // makes the transition atomic.
    if (homePinned) setHomePinned(false);
    // Iter XX.149 — Instant-navigate landing→chat handoff.
    // When we're replaying the pending intent from the landing page,
    // `busy` was set to true optimistically (to render the typing
    // dots) BEFORE this function is called. Allow the pending-intent
    // replay through the busy guard — the setBusy(true) later in this
    // function is idempotent.
    if (!text || !sessionId) return;
    if (busy && !opts._fromPendingIntent) return;
    // Post-paywall ghosting repair (2026-10-02) — hard guard: never issue a
    // coaching request while the arc lock is active. All 18 send() call sites
    // (Enter key, send button, voice transcript, chips, report capture,
    // pending-intent replays, save-intent, resume-after-onboarding, live voice)
    // flow through this single entry point, so this is the authoritative
    // client-side protection. UI disabling (textarea/button) is necessary UX
    // but not sufficient correctness. Preserve the user's text — do NOT clear
    // input — so nothing they typed is lost.
    if (arcLockedRef.current) {
      try { trackFunnel("post_lock_send_attempted", { source: opts.source || "unknown" }); } catch { /* fire-and-forget */ }
      return;
    }
    // Haptics (2026-10-02) — light impact on genuine message send.
    // Fires after all guards pass so blocked sends don't haptic.
    hapticLight();
    // Iter XX.280 — Journey-start instrumentation: first_interaction
    // for non-typing sends (chip taps, voice, mission openers, entry
    // points). The textarea onChange covers keystrokes; this covers
    // everything else that reaches send(). Fires once per session.
    if (!_journeyFirstInteractionFiredRef.current) {
      _journeyFirstInteractionFiredRef.current = true;
      try {
        let _bid = null;
        try { _bid = getBrowserId(); } catch { /* noop */ }
        postClientEvent(API, sessionId, "first_interaction",
          { source: opts.source || "send",
            ...(_bid ? { browser_id: _bid } : {}) });
      } catch { /* non-fatal */ }
    }
    // Iter XX.96 — Commitment-moment Parent Entry gate for direct
    // Iter XX.142 — Parent gate removed from the /chat send path.
    // Founder rule: never block coaching for a profile question.
    // Parent context is inferred from campaign route + language
    // detection, not from a modal.
    // Iter XX.92 — Chip taps / entry-point clicks / mission openers
    // route through send(). Cancel the scripted intro on the way in
    // and emit `first_input_after_intro` with the caller's source so
    // we can measure how visitors chose to engage. Typed input is
    // already handled by the composer's onChange; here `opts.source`
    // is truthy for chip / entry-point paths.
    if (introRunning) {
      const source = opts.source === "starter_chip"
        ? "chip_clicked"
        : (opts.source === "loop_entry_chip" ? "chip_clicked" : "typed");
      _cancelIntro(`interrupted_${source}`);
      try {
        postClientEvent(API, sessionId, "first_input_after_intro", { source });
        try { clarityTrack("first_input_after_intro", { source }); }
        catch { /* noop */ }
      } catch { /* noop */ }
    }
    // Iter XX.72.4 — deny-by-default: if the universal paywall is
    // currently shown, the user is NOT entitled. Drop the send
    // attempt silently and keep the paywall visible. The backend
    // /web/chat gate would also 402 this, but stopping at the
    // frontend prevents the optimistic bubble from appearing.
    if (paywall && paywall.surface === "universal_plus_paywall") {
      return;
    }
    // Iter XX.12 — conversation is paused until identity is completed.
    // Any attempted send while the gate is up is dropped silently; the
    // input keeps the text so the user doesn't lose what they typed.
    if (gateRequired) {
      // Re-emphasise the modal in case they scrolled past it.
      setGateForcing(true);
      setNeedsIdentity(true);
      try { localStorage.setItem("gully_last_identity_trigger", "engagement_gate"); } catch { /* noop */ }
      return;
    }

    // Iter XX.58 — Journey FSM guard.
    // If the modal onboarding is active, the normal chat pipeline is fully
    // suspended. No memory, no /web/chat, no LLM reply to the intercepted
    // user message. Every user reply is routed exclusively into the
    // journey flow.
    //
    // Iter XX.97 — Exception: a "save this" imperative issued during
    // WAITING_FOR_DREAM must NOT be re-routed back into the FSM
    // (that would cause infinite recursion with the save-intent
    // bypass inside handleJourneyUserInput). `opts.source ===
    // "save_intent_bypass"` is the caller's explicit signal that
    // the message has already been classified as a save intent and
    // should flow straight through to the backend short-circuit.
    //
    // Iter XX.102 — Second exception: `resume_after_onboarding`
    // resumes the original intercepted coaching topic. The caller
    // has already flipped setJourneyState("NONE"), but React's
    // async re-render + the setTimeout-scheduled send create a
    // stale-closure race where journeyState still reads the pre-
    // resume value. Bypass by explicit source to avoid the
    // resume send being re-interpreted as a dream/edit reply.
    if (journeyState !== "NONE" && !FSM_GUARD_BYPASS_SOURCES.has(opts?.source)) {
      return handleJourneyUserInput(text);
    }
    // Iter XX.86 — Hydration-race safeguard. If the journey/me fetch
    // hasn't finished yet (journey === null), we CANNOT know whether
    // the user has a dream stored or not. Bailing out here rather
    // than falling through prevents the "dream chip" prompt from
    // firing for a signed-in returning user whose dream is already
    // stored but the load hasn't resolved. The composer is also
    // disabled while journey is null (see composer render below),
    // so this branch is defence-in-depth. Micro-wait for up to
    // ~700 ms to give the loader a chance to finish.
    if (journey === null) {
      for (let i = 0; i < 14 && journey === null; i++) {
        await new Promise((r) => setTimeout(r, 50));
      }
      // Still null after ~700 ms — bail silently. The user's next
      // tap will re-enter this handler once state is populated.
      if (journey === null) {
        return;
      }
    }
    // Entry: first real message from a player without a Dream. Enter
    // WAITING_FOR_DREAM. Cache the intercepted text purely as evidence
    // for the Next Level proposer — it is NEVER replayed to /web/chat.
    //
    // Iter XX.86 — The guard already correctly requires `!journey.dream`,
    // so a signed-in user with a stored dream never enters this branch.
    // Preserved for genuine first-time users (dream missing on server).
    //
    // Iter XX.107 (hotfix) — The activation-landing pending-intent
    // resume MUST NOT be intercepted by the WAITING_FOR_DREAM flow.
    // The founder rule for XX.106/XX.107: the very first coaching
    // reply after the Parent Entry gate must continue the visitor's
    // original thought (e.g. "yorkers in death overs"), never
    // restart with a dream question. The dream flow can still fire
    // organically on a later turn.
    // P0-B — cricket-intent carve-out. A first message carrying a
    // sufficiently specific cricket problem bypasses the dream picker so
    // coaching runs FIRST (founder invariant: cricket value before
    // onboarding; monetization never pre-empts first value). The dream
    // is deferred, not removed: deferredDreamRef collects it after the
    // first real coaching reply lands (see the reply handler below).
    // Same principle as the XX.107 pending-intent bypass (founder rule:
    // the first coaching reply continues the visitor's original thought).
    // Vague first messages keep the existing dream-gate path unchanged.
    const _qaBypassDreamIntro = (() => {
      // Iter XX.130 — QA bypass for the dream-picker onboarding intro.
      // Same intent as the ParentEntryGate bypass: automated harnesses
      // need to exercise the IDE → PCE-card path in a single hop.
      // Consumers: sessionStorage.g_qa_skip_dream_intro = "1" OR
      // URL ?qa_no_dream_intro=1 (auto-stashes into sessionStorage on
      // first read).
      try {
        if (sessionStorage.getItem("g_qa_skip_dream_intro") === "1") return true;
        const p = new URLSearchParams(window.location.search);
        if (p.get("qa_no_dream_intro") === "1") {
          sessionStorage.setItem("g_qa_skip_dream_intro", "1");
          return true;
        }
      } catch { /* noop */ }
      return false;
    })();
    const _dreamGateEligible =
      journey !== null && !journey.dream && !hasFirstMessage && !_qaBypassDreamIntro;
    const _cricketIntentFirst =
      _dreamGateEligible && looksLikeSpecificCricketProblem(text);
    if (
      opts?._fromPendingIntent
      || opts?.source === RESUME_AFTER_ONBOARDING
    ) {
      // Fall through to the normal /web/chat POST path below —
      // coaching runs on the original topic immediately.
    } else if (_dreamGateEligible && !_cricketIntentFirst) {
      // Iter XX.87 — first_message_sent fires here (BEFORE the dream
      // gate short-circuits) so anonymous funnel visitors are counted
      // as having sent their first valid message. Empty / gated /
      // journey-loading / bailed sends have all returned above.
      clarityTrackOnce("first_message_sent",
                        { source: opts.source || "typed" });
      journeyIntentRef.current = text;
      setJourneyState("WAITING_FOR_DREAM");
      setMessages(m => [...m,
        { role: "user", content: text },
        {
          role: "assistant",
          dream_chips: true,
          content:
            "Before we jump into today's conversation… one question that'll help me coach you better.\n\nWhat's your biggest cricket dream?",
          // Iter XX.265b — Dream-picker interstitial is system-generated;
          // do NOT let it count as meaningful assistant content for
          // Home-surface derivation. (User content flips the flag; this
          // is purely defensive parity so debug tools reflect intent.)
          _system_seeded: true,
        },
      ]);
      setInput("");
      setHasFirstMessage(true);
      return;
    }
    // P0-B — carve-out bookkeeping: the first message was a specific
    // cricket problem, so it is going straight to coaching. Remember to
    // collect the dream after the first real coaching reply lands.
    if (_cricketIntentFirst) deferredDreamRef.current = true;

    setBusy(true);
    setInput("");
    // Iter XX.258 — Gully Initiates V1. If a proactive initiation is
    // currently unanswered, this outbound user message is the ack:replied
    // signal. Fire-and-forget; never blocks the send.
    if (gullyInitiatePendingRef.current) {
      gullyInitiatePendingRef.current = false;
      try {
        api.post("/web/initiate/ack", {
          session_id: sessionId, action: "replied",
        }).catch(() => { /* noop */ });
      } catch { /* noop */ }
    }
    // Iter XX.149 — Instant-navigate landing→chat handoff.
    // De-dupe: on the pending-intent replay path, the user bubble was
    // already painted optimistically in the mount effect (see the
    // peekPendingIntent effect above). Skip re-adding it if the last
    // message is an identical user turn.
    //
    // Iter XX.265b — When we de-dupe against a pending-intent
    // optimistic paint, UPGRADE the row: strip `_system_seeded` so
    // the Home-surface derivation counts it as a meaningful user
    // turn from this point on. Without this, a returning user who
    // came from Landing would stay in "Home" mode forever because
    // the only user row in the transcript would remain system-seeded.
    setMessages((m) => {
      const last = m.length ? m[m.length - 1] : null;
      if (last && last.role === "user" && last.content === text) {
        if (last._system_seeded) {
          const upgraded = m.slice(0, -1);
          const { _system_seeded, ...rest } = last;
          upgraded.push(rest);
          return upgraded;
        }
        return m;
      }
      return [...m, { role: "user", content: text }];
    });
    // Iter XX.89 — Paid Instagram persona gate handoff. Consume-and-
    // clear the stashed persona context on the FIRST outbound
    // /web/chat POST. We deliberately gate on a dedicated ref
    // (`personaContextConsumedRef`) instead of `hasFirstMessage` —
    // the dream-interstitial branch above ALREADY sets
    // hasFirstMessage=true before ever POSTing, which would leave
    // paid-IG visitors' persona/goal stranded in localStorage. This
    // ref only flips true when we successfully attach the payload
    // to a real POST, so it survives dream + resume flows.
    let _personaContextForSend = null;
    if (!personaContextConsumedRef.current) {
      _personaContextForSend = consumeStashedPersonaContext();
      if (_personaContextForSend) {
        personaContextConsumedRef.current = true;
      }
    }
    // Iter XX.94 — Parent Entry gate handoff. Same one-shot semantics
    // as persona_context above. Attached to the FIRST /web/chat POST
    // where the visitor chose "My child" during the entry gate — the
    // backend directive then rewrites first-person prompts into
    // natural third-person language using the child's name.
    let _parentContextForSend = null;
    if (!parentContextConsumedRef.current) {
      _parentContextForSend = consumeStashedParentContext();
      if (_parentContextForSend) {
        parentContextConsumedRef.current = true;
      }
    }
    // Hide the starter chips forever once the player has sent anything.
    if (!hasFirstMessage) {
      setHasFirstMessage(true);
      // Iter XX.87 — first_message_sent fires here (message accepted
      // for sending, message pushed to state) — NOT after the /web/chat
      // response resolves. Empty / gated / journey-loading / bailed
      // sends have all returned earlier, so this event fires ONLY for
      // valid accepted sends. sessionStorage dedup guarantees once-only.
      clarityTrackOnce("first_message_sent",
                        { source: opts.source || "typed" });
    }
    // Iter XX.58 — clear the module-scoped mission opener now that the
    // player has responded. Subsequent visits won't replay it.
    _pendingMissionOpener = null;
    // Keep the textarea focused so Android doesn't collapse the keyboard.
    // Without this the keyboard closes on every send and the user has to
    // re-tap, which is exactly the "can't see Gully's response while typing"
    // symptom on Android Chrome + Instagram/FB WebView.
    textRef.current?.focus();
    // Iter XX.149 — Instant-navigate landing→chat handoff.
    // High-res timing for the pending-intent replay so the founder can
    // grep [gully.latency] in the browser console to verify the
    // waterfall improvement without any dashboard plumbing. Snapshot
    // t_tap into a local because the flusher clears pendingIntentRef
    // before send() runs.
    const _t_req_start = (opts._fromPendingIntent
      && typeof performance !== "undefined" && performance.now)
      ? performance.now() : null;
    const _t_tap_snapshot = (opts._fromPendingIntent
      && opts._t_tap && typeof opts._t_tap === "number")
      ? opts._t_tap : null;
    try {
      // Iter XX.224 follow-up — gully_coaching_started. Fires ONCE
      // per session on the very first authored user turn. Uses the
      // authoritative pre-send message count (BEFORE the append at
      // line ~2565) so we can never double-fire on retries or on
      // subsequent turns. Consent + iOS gate live inside the
      // dispatcher.
      try {
        const _priorUserTurns = messages.filter((m) => m.role === "user").length;
        if (_priorUserTurns <= 1) {   // this is the first authored turn
          logCoachingStarted(opts.source || opts.entry_point || "chat");
        }
      } catch {}
      const { data } = await api.post("/web/chat", {
        session_id: sessionId,
        message: text,
        origin_url: window.location.origin,
        // 2026-02-25 — starter-chip telemetry tag. Server stores in audit
        // event payload so we can measure chip-driven conversation starts.
        ...(opts.source ? { source: opts.source } : {}),
        // Iter XX.172 — Focused Entry Chips experiment metadata.
        // Backend persists to audit_events + activation_funnel_events
        // ONLY — never injected into the LLM prompt (Path C
        // invariant preserved).
        ...(opts.entry_experiment ? { entry_experiment: opts.entry_experiment } : {}),
        ...(opts.entry_problem    ? { entry_problem:    opts.entry_problem    } : {}),
        ...(opts.entry_situation  ? { entry_situation:  opts.entry_situation  } : {}),
        // Iter XX.73 — entry-point (Match / Practice / Video / Question)
        // when the caller supplied one. Server persists it on the first
        // user turn only; subsequent turns pass a null-ish value and
        // the server ignores it.
        ...(opts.entry_point ? { entry_point: opts.entry_point } : {}),
        // First Loop — structured report from the one-tap capture.
        // Backend runs classification + the return/decision path on it
        // (see `report` in the ChatRequest contract).
        ...(opts.report ? { report: opts.report } : {}),
        // Iter XX.74.1 — resume-after-onboarding topic. Present when
        // the client is replaying the player's original coaching
        // message after an interstitial (dream / role / age / level).
        // Backend injects a system-prompt directive so the LLM
        // acknowledges the onboarding answer briefly then pivots
        // explicitly back to the original topic — never restarts
        // with a generic opener.
        ...(opts.resumed_topic ? { resumed_topic: opts.resumed_topic } : {}),
        // Iter XX.89 — Paid Instagram persona gate handoff. Attached
        // ONLY to the FIRST /web/chat call after the gate completed.
        // See consumeStashedPersonaContext() — reading it also clears
        // it, so subsequent turns never re-send.
        ...(_personaContextForSend ? { persona_context: _personaContextForSend } : {}),
        ...(opts.persona_context ? { persona_context: opts.persona_context } : {}),
        // Iter XX.94 — Parent Entry gate handoff (see send() prefix).
        ...(_parentContextForSend ? { parent_context: _parentContextForSend } : {}),
        ...(opts.parent_context ? { parent_context: opts.parent_context } : {}),
        // Iter XX.130 — High-intent Plus signal (?intent=plus / from
        // /plus CTA). One-shot per session — cleared after the first
        // send so subsequent turns behave normally.
        ...(highIntentPlusRef.current ? { high_intent_plus: true } : {}),
        // Iter XX.254 — Deterministic landing-starter hook. Only
        // populated when the visitor tapped a chip whose config
        // declares an explicit starter_id (currently: `got_out` →
        // `keep_getting_out`). Never derived from user prose. URL
        // fallback covers the legacy LandingV2 path which stamps
        // `?starter_id=...` on the /chat URL instead of using
        // stashPendingIntent.
        ...((() => {
          let _sid = opts.starter_id || null;
          if (!_sid) {
            try {
              _sid = new URLSearchParams(window.location.search || "")
                .get("starter_id");
            } catch { /* noop */ }
          }
          return _sid ? { starter_id: _sid } : {};
        })()),
      });
      // Iter XX.280 — Journey-start instrumentation:
      // message_ack_received. Fires when the client receives the
      // server's send-acknowledgment (P0-B's immediate
      // `{accepted: true, message_id}` return). Contract: the ack
      // response carries `accepted === true`. Until P0-B ships the
      // immediate ack, the full-reply response does not match and
      // this stays silent — which is correct (no ack exists yet).
      // Fires per ack (not once) — every send gets an ack.
      try {
        if (data && data.accepted === true && data.message_id) {
          let _bid = null;
          try { _bid = getBrowserId(); } catch { /* noop */ }
          postClientEvent(API, sessionId, "message_ack_received",
            { message_id: String(data.message_id).slice(0, 64),
              ...(_bid ? { browser_id: _bid } : {}) });
        }
      } catch { /* non-fatal */ }
      if (highIntentPlusRef.current) {
        highIntentPlusRef.current = false;
        try { localStorage.removeItem("g_high_intent_plus"); } catch { /* noop */ }
      }
      // Iter XX.149 — Log the /web/chat round-trip for the
      // pending-intent replay. This is the "first reply painted"
      // moment from the visitor's point of view — the busy=true
      // typing dots disappear when setBusy(false) fires downstream.
      if (_t_req_start != null) {
        try {
          const _t_reply = (typeof performance !== "undefined" && performance.now)
            ? performance.now() : Date.now();
          // eslint-disable-next-line no-console
          console.info("[gully.latency] chat.reply-received", {
            ms_web_chat: Math.round(_t_reply - _t_req_start),
            ms_since_tap: (_t_tap_snapshot != null)
              ? Math.round(_t_reply - _t_tap_snapshot) : null,
            source: opts.source || null,
          });
        } catch { /* noop */ }
      }
      // Iter 69 — If the server flagged this turn as the start of a new
      // session (14h+ gap), retro-fit the boundary scalars onto the user
      // message we already pushed optimistically. Historic messages that
      // hydrated with the same session_index will still compare equal, so
      // the <SessionDivider> only renders on the transition.
      if (data && typeof data.session_index === "number") {
        setMessages((arr) => {
          if (!arr.length) return arr;
          const copy = arr.slice();
          for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].role === "user") {
              copy[i] = {
                ...copy[i],
                session_index: data.session_index,
                is_new_session_start: !!data.session_new,
                day_bucket: data.day_bucket || copy[i].day_bucket || "",
              };
              break;
            }
          }
          return copy;
        });
      }
      // Iter XX.224 follow-up — gully_meaningful_coaching_completed.
      // Fires when the server returned a real coaching reply AND we're
      // about to render it. Guarded on `data.reply` being a non-empty
      // string — not merely a 200 status, and never on identity_nudge-
      // only responses. Consent + iOS gate live inside the dispatcher.
      try {
        if (typeof data?.reply === "string" && data.reply.trim().length > 0) {
          logMeaningfulCoachingCompleted(
            opts.entry_point || opts.source || "chat"
          );
          // P0-B — deferred dream collection. The session's first message
          // was a specific cricket problem, so coaching ran first and the
          // dream picker was skipped. Now that real coaching value has
          // landed, collect the dream naturally with the same picker UI
          // the gate would have shown. Deferred, not removed.
          // journeyIntentRef is intentionally left null: there is nothing
          // to resume — the original topic was just answered.
          if (deferredDreamRef.current) {
            deferredDreamRef.current = false;
            if (journey && !journey.dream) {
              setJourneyState("WAITING_FOR_DREAM");
              setMessages((m) => [...m, {
                role: "assistant",
                dream_chips: true,
                content:
                  "Nice — let's keep building on that. One question that'll help me coach you better.\n\nWhat's your biggest cricket dream?",
                // System-seeded like the gate's picker: not meaningful
                // user content for Home-surface derivation.
                _system_seeded: true,
              }]);
            }
          }
        }
      } catch {}
      // Post-paywall ghosting repair (2026-10-02) — if the lock was cleared by
      // Plus entitlement, this is the first successful coaching turn after
      // resume. Fire exactly once, then consume the flag.
      if (_resumedAfterLockRef.current) {
        _resumedAfterLockRef.current = false;
        try { trackFunnel("conversation_resumed", { source: opts.source || "chat" }); } catch { /* fire-and-forget */ }
      }
      setMessages((m) => [...m, {
        role: "assistant", content: data.reply,
        identity_nudge: data.identity_nudge || null,
        // Iter XX.234 — Visual Coaching v1. Backend attaches the visual
        // payload under `data.visual_explanation` when triggers matched.
        // Fold it into `attachments` so the bubble renderer picks it up
        // via the same code path as history reload (Chat.jsx:6284).
        // Older backends omit the key → attachments stays empty.
        attachments: data.visual_explanation
          ? [data.visual_explanation]
          : [],
        // XX.193 Retry affordance. `retryable_review_action` may now
        // be attached here by `_resolve_awaiting_identity` on the
        // backend when the transient XX.191 disk-write failure
        // returns "I have your video safely…". Rendered by the same
        // Retry block below.
        retryable_review_action: data.retryable_review_action || null,
        // Iter 55 — Timeline "Added to your Cricket Timeline" chip.
        // Backend sets `timeline_added: {type}` ONLY for Plus users
        // whose message looked like a match/practice reflection.
        // Free users + non-reflection messages get null → silent.
        timeline_added: data.timeline_added || null,
        // Iter XX.74 — Journey "Added to your Journey" inline pill.
        // Backend sets `journey_added: true` when THIS turn captured
        // a fresh commitment (authed user + improvement-loop flag on).
        // Rendered as a small inline link right after the reply.
        journey_added: Boolean(data.journey_added),
        // Iter 42.1 — billing intent CTA. When present we render a prominent
        // button below the bubble instead of letting the raw URL leak into
        // the message text. cta_url is either a REAL Stripe Checkout URL or
        // the host /upgrade page — never fabricated.
        billing_cta: (data.intent && data.cta_label && data.cta_url) ? {
          intent: data.intent,
          label: data.cta_label,
          url: data.cta_url,
          conversion_context_id: data.conversion_context_id || null,
          cta_variant: data.cta_variant || data.intent,
        } : null,
        // Iter XX.85 — Coach recommendation block. Server attaches
        // this only when the deterministic gate qualifies AND the
        // LLM decides this is the natural coaching moment. Rendered
        // as a distinct card beneath the assistant bubble.
        coach_recommendation: (data.coach_recommendation
          && data.coach_recommendation.text) ? data.coach_recommendation : null,
        // Iter XX.97 — Cricket Memory confirmation card. Server
        // attaches this on the save-intent short-circuit turn with
        // `status: "saving"`. A parallel polling effect (below) then
        // watches /web/memory/parent_child_profile and updates the
        // message payload in-place once the async extraction lands
        // "saved" or "failed".
        cricket_memory_confirmation: data.cricket_memory_confirmation || null,
        // Iter XX.111 — "Today's Session Saved" memory card. Server
        // attaches `session_memory` on the exact turn the coaching
        // arc closes (before any Plus offer appears on the next
        // turn). Rendered as a distinct card beneath the assistant
        // bubble. Backend omits this field when no observation
        // could be extracted — the card is silently absent rather
        // than fabricated.
        session_memory: (data.session_memory
          && data.session_memory.observation) ? data.session_memory : null,
        // Iter XX.130 — Interaction Decision Engine payload. Attached
        // by the server after every coaching turn. Actions:
        //   continue_coaching → no-op (LLM reply is the answer)
        //   ask_micro_commitment → render an inline PCE card
        //   introduce_plus → render the Plus continuity card
        //   surface_journey → render the top themes card
        //   strengthen_memory → light memory nudge chip
        // High-intent bypass is respected — client honours it even if
        // the visitor is mid-conversation.
        next_interaction: (data.next_interaction
          && data.next_interaction.action) ? data.next_interaction : null,
        // Iter XX.246 — Contextual Video Coaching. Backend attaches
        // `video_invitation` when the LLM emitted a validated invite
        // via its <GULLY_META> block AND hard-suppression didn't
        // strip it. Rendered as a natural chip under the reply. On
        // tap → opens the existing upload flow with the suggested
        // focus. On "Not now" → POST /web/video-invite/decline.
        video_invitation: (data.video_invitation
          && data.video_invitation.invitation_text) ? data.video_invitation : null,
        // Iter XX.253 — Format-Aware Cricket Coaching. Backend attaches
        // `format_clarification` when the LLM emitted a validated
        // clarification request via <GULLY_FORMAT> AND session-scoped
        // suppression didn't strip it. Rendered as 4 quick-chip options
        // (T20 / 40-50 over / Multi-day / Across formats). On tap →
        // POST /web/format/select. On dismiss → POST /web/format/dismiss.
        format_clarification: (data.format_clarification
          && data.format_clarification.options
          && data.format_clarification.options.length) ? data.format_clarification : null,
      }]);
      // Iter XX.82 — Context-aware reveals. Server emits the full
      // cumulative revealed set after each turn based on what the
      // conversation just produced (memory saved, video invited,
      // recurring theme, coaching value delivered). Merge into
      // local state so gated surfaces unlock naturally, in-context.
      if (Array.isArray(data.reveals) && data.reveals.length > 0) {
        applyServerReveals(data.reveals);
        // Iter XX.88 — Conversation First Mode. Track whether the
        // reply Gully just sent contextually invited a clip. Used to
        // gate the "Want feedback on a bowling or batting video?"
        // suggestion card, so it appears ONLY when Gully naturally
        // recommends video (not preemptively for signed-in users
        // whose upload surface defaultVisible=true).
        if (data.reveals.includes("video")) {
          setVideoSignaledInLastTurn(true);
        }
      }
      // Iter XX.150 — Founder principle: never interrupt a player who
      // has already decided to talk. The mid-conversation
      // `ask_micro_commitment` overlay previously popped a modal card
      // AFTER Gully's first reply, breaking active coaching to gather
      // data via chips. The backend engine may still emit
      // `ask_micro_commitment` in `next_interaction` — we now
      // deliberately DO NOT render it as a modal. If the engine wants
      // to ask that question, it can weave it into `data.reply` in
      // coach voice. Preserved as an audit log so the founder can see
      // when the engine attempted (but was suppressed) to raise it.
      try {
        const ni = data?.next_interaction;
        if (ni && ni.action === "ask_micro_commitment" && ideFlagOn) {
          // eslint-disable-next-line no-console
          console.info("[gully.ide] ask_micro_commitment suppressed", {
            session_id: sessionId,
            reason: "founder_no_structured_interruption",
          });
        }
      } catch { /* noop */ }
      // Iter XX.74 — nav pill unlock + pulse. First commitment ever
      // reveals the pill; every subsequent commitment briefly pulses
      // it so returning eyes catch the new entry.
      if (data.journey_added) {
        setJourneyReady(true);
        setJourneyPulse(true);
        setTimeout(() => setJourneyPulse(false), 6000);
      }
      // Iter XX.71 — Conversation Plus Offer. Backend attaches
      // `conversation_offer: {show, session_number, threshold, cta,
      // dismiss, trigger, auto_open}` ONLY when the flag is ON, the
      // user is free, and either (a) buying-intent detected,
      // (b) value moment detected, or (c) threshold reached.
      // Rendered AFTER the reply is added so the coaching turn is
      // never interrupted. Iter XX.71.5 — `auto_open` is true when
      // trigger === "buying_intent" so the modal opens immediately.
      // Analytics: the backend emits `conversation_offer_shown` at
      // the exact moment it tells the frontend to render — that IS
      // the modal_opened signal (funnel: shown → clicked → checkout →
      // purchase).
      if (data && data.conversation_offer && data.conversation_offer.show) {
        setConversationOffer(data.conversation_offer);
      }
      // Iter XX.71.3 — Anonymous Identity Claim prompt. Shown once
      // per anonymous identity BEFORE the Plus offer is evaluated.
      if (data && data.identity_claim_prompt && data.identity_claim_prompt.show) {
        setIdentityClaimPrompt(data.identity_claim_prompt);
      }
      // Iter XX.25 — "Something I've noticed…" moment. Backend
      // returns `notice: {content, kind}` after the primary reply
      // when a deterministic detector fires. Render as a SECOND
      // assistant bubble so the moment stands on its own.
      if (data.notice && data.notice.content) {
        setMessages((m) => [...m, {
          role: "assistant",
          content: data.notice.content,
          notice_kind: data.notice.kind || null,
        }]);
      }
      // The legacy `should_prompt_identity` modal trigger is suppressed when
      // the new contextual nudge fires — we never want both surfaces firing
      // at once. The chip lives inside the message bubble; the modal pops
      // only if we have no nudge to show.
      if (data.should_prompt_identity && !identityShown && !linked && !data.identity_nudge) {
        try { localStorage.setItem("gully_last_identity_trigger", "auto_3msg"); } catch { /* noop */ }
        api.post("/web/track", {
          session_id: sessionId, event: "history_link_attempted",
          payload: { source: "auto_3msg" },
        }).catch(() => {});
        setNeedsIdentity(true);
        setIdentityShown(true);
      }
      // After the reply lands, force one more scroll-to-bottom on the next
      // frame — Android sometimes reflows the visualViewport AFTER the DOM
      // mutation, so the scroll we ran on `messages` change happens before
      // the keyboard finishes settling.
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
      // Iter 44.1 — refresh continuity eligibility after every chat reply.
      refreshContinuityEligibility();
      // Iter XX.12 — poll engagement state; if the meaningful-engagement
      // threshold was crossed on this turn and the session is still
      // anonymous, lock the composer + show the Cricket Journey Gate.
      // Fire-and-forget — a failure never blocks the coaching turn.
      if (!linked) {
        api.get(`/web/engagement-state?session_id=${encodeURIComponent(sessionId)}`)
          .then((r) => {
            if (r?.data?.gate_required && !linked) setGateRequired(true);
          })
          .catch(() => { /* fail-open, coaching continues */ });
      }
    } catch (e) {
      // Iter XX.72 — Universal Plus Paywall: /web/chat returns 402
      // with a canonical paywall detail when the user isn't entitled.
      // Route straight to the paywall modal (existing UI) and DROP the
      // optimistic user message so the composer clears cleanly.
      const _resp = e?.response;
      if (_resp && _resp.status === 402) {
        const _detail = _resp.data?.detail || _resp.data || {};
        // Iter XX.126 — Coaching-arc lock. Preserve the conversation
        // (do NOT drop the optimistic user bubble), render the
        // deterministic Plus CTA inline as a `billing_cta` bubble,
        // and lock the composer so this looks like a boundary, not
        // a technical error.
        // Iter XX.158.1 — Same graceful lock UX for the XX.158
        // arc-switch gate. `coaching_arc_completed` is emitted by
        // `web_chat.py::chat` when a non-Plus identity starts a
        // genuinely NEW coaching arc after their first free arc has
        // been completed. Handled identically to `coaching_arc_used`
        // so the composer greys out, history stays visible, and an
        // inline billing CTA bubble renders — never a hard error.
        if (_detail.paywall === "coaching_arc_used"
            || _detail.paywall === "coaching_arc_completed") {
          // Post-paywall ghosting repair (2026-10-02) — if already locked,
          // do NOT append another CTA bubble. A stale client, race, or
          // multi-client request may still reach the backend; the 402 must
          // re-establish the visible locked state, never spam duplicate
          // bubbles and never fail silently. Uses the ref (synchronous)
          // rather than state (async) so rapid 402s dedupe correctly.
          if (arcLockedRef.current) {
            try { trackFunnel("post_lock_request_rejected", { intent: _detail.paywall }); } catch { /* fire-and-forget */ }
            try { setArcLocked(true); } catch { /* noop */ }
            return;
          }
          const _lockIntent = _detail.paywall;    // preserve which lock fired
          const _ctaAttachment = {
            kind:      "billing_cta",
            intent:    _lockIntent,
            cta_label: _detail.cta_label || "Keep Gully learning my cricket",
            cta_url:   _detail.cta_url || "/plus",
          };
          // Iter XX.204 — Personalised "Your Cricket" reveal (Android-first).
          // Server attaches `your_cricket` on the 402 detail when THIS
          // player has stored evidence (active arc topic / observation /
          // next practice commitment). Render as a compact card ABOVE
          // the billing CTA. Payload is null/absent when no truthful
          // reveal is possible — graceful degrade to the plain CTA.
          const _yc = _detail.your_cricket || null;
          // Iter XX.273 — Continue This Journey variant. Backend sets
          // `variant: "journey_offer_v1"` when the flag is ON AND
          // your_cricket.current_focus is genuinely present. In every
          // other case, the branch below behaves exactly as XX.204.
          const _isJourneyVariant = _detail.variant === "journey_offer_v1"
            && !!(_yc && _yc.current_focus);
          // Iter XX.288b — Canonical `journey_offer_impression` fires
          // at the ARC-LOCK-402 BOUNDARY, BEFORE the reveal-vs-plain-CTA
          // branch. XX.288 emitted this inside the `if (_yc && …)`
          // guard, so when the server 402 arrived without a
          // `your_cricket` payload (low-evidence player) the plain
          // `billing_cta` bubble rendered as the visible upsell but
          // the impression never fired — reproduced in the
          // XX.288b controlled prod test (`arc_lock_blocked` > 0,
          // `journey_impressions` == 0). Emitting here mirrors the
          // guaranteed server-side `coaching_arc_lock_blocked` event
          // exactly — one 402 = one impression — with metadata that
          // records whether the enriched reveal was available.
          try {
            trackFunnel("journey_offer_impression", {
              surface: "arc_lock_402",
              variant: _isJourneyVariant ? "journey_offer_v1" : "legacy_your_cricket",
              intent:  _lockIntent,
              has_your_cricket: !!_yc,
              has_focus:        !!(_yc && _yc.current_focus),
              has_observation:  !!(_yc && _yc.observation),
            });
          } catch { /* fire-and-forget */ }
          const _newMessages = [];
          if (_yc && (_yc.current_focus || _yc.observation)) {
            _newMessages.push({
              role: "assistant",
              content: "",       // rendered by the attachment, not text
              attachments: [{
                kind:            "your_cricket_reveal",
                title:           _yc.title || "Your Cricket",
                heading:         (_isJourneyVariant && _detail.heading)
                                    ? _detail.heading
                                    : (_yc.heading || "Here's what I'm starting to learn about your game."),
                sub_line:        _isJourneyVariant ? (_detail.sub_line || null) : null,
                current_focus:   _yc.current_focus || null,
                observation:     _yc.observation || null,
                next_step:       _yc.next_step || null,
                value_line:      _yc.value_line || "",
                cta_label:       _yc.cta_label || _detail.cta_label || "Keep Gully learning my cricket",
                variant:         _isJourneyVariant ? "journey_offer_v1" : null,
              }],
              kind: "your_cricket_reveal",
            });
            try {
              trackFunnel("personalized_value_reveal_shown", {
                surface: "arc_lock_402",
                has_focus: !!_yc.current_focus,
                has_observation: !!_yc.observation,
                has_next_step: !!_yc.next_step,
              });
            } catch { /* fire-and-forget */ }
            if (_isJourneyVariant) {
              // Stash the focus text so the post-purchase redirect
              // can route back to the cricket context rather than
              // generic Home. Idempotent per session — a re-render
              // simply refreshes the timestamp.
              try {
                // eslint-disable-next-line global-require
                const { stashJourneyOrigin } = require("../lib/journeyOrigin");
                stashJourneyOrigin({ focusText: _yc.current_focus });
              } catch { /* noop */ }
            }
          }
          _newMessages.push({
            role: "assistant",
            content: _detail.message ||
              "You’ve started building a picture of your cricket. " +
              "Keep Gully learning it with Plus.",
            attachments: [_ctaAttachment],
            intent: _lockIntent,
            kind:   "billing_cta",
          });
          setMessages((m) => [...m, ..._newMessages]);
          // Post-paywall ghosting repair (2026-10-02) — `paywall_shown` fires
          // here because the CTA bubble is actually rendered (client truth),
          // unlike the server `plus_paywall_viewed` which fires on every 402
          // including ones that produce no visible UI. Sync the ref so the
          // send() guard and already-locked dedupe read the truth immediately.
          try { trackFunnel("paywall_shown", { surface: "arc_lock_402", intent: _lockIntent }); } catch { /* fire-and-forget */ }
          arcLockedRef.current = true;
          try { setArcLocked(true); } catch { /* noop */ }
          return;
        }
        setMessages((m) => {
          // Remove the optimistic user bubble we just appended.
          for (let i = m.length - 1; i >= 0; i--) {
            if (m[i].role === "user" && m[i].content === text) {
              return [...m.slice(0, i), ...m.slice(i + 1)];
            }
          }
          return m;
        });
        setPaywall({
          used: 0, limit: 0,
          reason: _detail.reason || "E_NO_ENTITLEMENT",
          surface: _detail.surface || "universal_plus_paywall",
          message: _detail.message,
          upgrade_url: _detail.upgrade_url || "/upgrade?from=universal_paywall",
        });
        return;
      }
      // 2026-06-25 — Iter 25.9j. Network-error path — be specific.
      setMessages((m) => [...m, { role: "assistant", content:
        "Couldn't reach me just now — check your connection and tap send again. Your message is right above." }]);
    } finally {
      setBusy(false);
    }
  }

  // First Loop — one-tap report capture submit. The structured
  // `report` rides the normal chat send path (`opts.report` → POST
  // body) so classification + the return/decision machinery run on
  // it exactly as they would on a typed report.
  const handleReportSubmit = (report, message) => {
    setShowReportCapture(false);
    send(message, { source: "report_capture", report });
  };

  // Iter XX.59 — Voice MVP handlers -----------------------------------
  //
  // Iter XX.60.1 — Auto-send. The transcript IS the player's next
  // message. The composer never shows the raw transcript for review;
  // this is a deliberate business decision to remove friction and keep
  // Voice Message dramatically cheaper than Realtime. Failure has its
  // own inline Try Again / Type Instead surface inside VoiceComposer.
  const handleVoiceTranscript = (text, meta) => {
    if (!text) return;
    // Flip the session flag so subsequent assistant replies auto-play
    // via TTS on this device.
    if (!voiceSessionActive) setVoiceSessionActive(true);
    voiceTurnRef.current = {
      transcript_at: performance.now(),
      recording_ms: meta?.recording_ms,
      stt_latency_ms: meta?.stt_latency_ms,
      detected_language: meta?.detected_language,
      word_count: meta?.word_count,
    };
    trackVoice("voice_message_sent", {
      stt_latency_ms: meta?.stt_latency_ms,
      recording_ms: meta?.recording_ms,
      word_count: meta?.word_count,
      detected_language: meta?.detected_language || "unknown",
      auto_send: true,
    });
    // Fire the same send() the composer uses. Zero divergence in the
    // downstream pipeline — memory, reports, Journey, identity all see
    // this as a normal user message.
    send(text);
  };

  const handleVoiceTypeInstead = () => {
    trackVoice("voice_fallback_to_text", {});
    setTimeout(() => textRef.current?.focus(), 0);
  };

  const handleVoiceMuteChange = (isMuted) => {
    setVoiceMuted(isMuted);
    // Stop any in-flight playback the moment the player mutes.
    if (isMuted && ttsAudioRef.current) {
      try { ttsAudioRef.current.pause(); } catch { /* noop */ }
      setTtsStatus("idle");
    }
  };

  const stopTtsPlayback = () => {
    if (ttsAudioRef.current) {
      try { ttsAudioRef.current.pause(); } catch { /* noop */ }
      trackVoice("voice_reply_stopped", {});
    }
    setTtsStatus("idle");
  };

  // Fires TTS for the latest assistant message when:
  //   * voice session is active (player used the mic once), AND
  //   * voice is not muted, AND
  //   * the latest message is an assistant text (not a journey card,
  //     not a dream chips prompt, not a system message, not empty).
  useEffect(() => {
    if (!voiceSessionActive || voiceMuted) return;
    if (!messages || messages.length === 0) return;
    const lastIdx = messages.length - 1;
    if (lastIdx <= lastSpokenIndexRef.current) return;
    const m = messages[lastIdx];
    if (!m || m.role !== "assistant") return;
    // Skip journey-flow synthetic messages — the FSM has its own UX
    // and we don't want the mic accidentally reading a chips prompt.
    if (m.dream_chips || m.journey_next_level_card || m.journey_mission_card
        || m.journey_thinking) return;
    const content = typeof m.content === "string" ? m.content.trim() : "";
    if (!content) return;
    lastSpokenIndexRef.current = lastIdx;
    (async () => {
      setTtsStatus("loading");
      const t0 = performance.now();
      try {
        const resp = await api.post("/voice/tts", { text: content },
          { responseType: "blob", timeout: 30000 });
        const blob = resp.data;
        const url = URL.createObjectURL(blob);
        // Reuse a single Audio element so we can always pause the
        // previous clip if a new assistant message arrives quickly.
        if (ttsAudioRef.current) {
          try { ttsAudioRef.current.pause(); } catch { /* noop */ }
        }
        const audio = new Audio(url);
        ttsAudioRef.current = audio;
        audio.addEventListener("ended", () => {
          setTtsStatus("idle");
          URL.revokeObjectURL(url);
        });
        audio.addEventListener("error", () => {
          setTtsStatus("error");
          URL.revokeObjectURL(url);
        });
        try {
          await audio.play();
          const total = voiceTurnRef.current
            ? Math.round(performance.now() - voiceTurnRef.current.transcript_at)
            : null;
          trackVoice("voice_reply_played", {
            tts_generation_ms: Math.round(performance.now() - t0),
            stop_to_audible_ms: total,
            char_count: content.length,
          });
          voiceTurnRef.current = null;
          setTtsStatus("playing");
        } catch {
          // Autoplay blocked — mark idle so the player can re-tap
          // the mic (which is a user gesture) to unlock the next one.
          setTtsStatus("idle");
        }
      } catch (err) {
        trackVoice("voice_transcription_failed", {
          stage: "tts",
          reason: err?.response?.status ? `http_${err.response.status}` : "network",
        });
        setTtsStatus("error");
      }
    })();
  }, [messages, voiceSessionActive, voiceMuted]);


  // Iter XX.58 — Journey FSM handlers ---------------------------------
  //
  // Design rules (from the bug fix):
  //   - The normal /web/chat pipeline is NEVER invoked while the FSM is
  //     active. No memory retrieval. No coaching reply. The intercepted
  //     first message is NEVER replayed unless the player types it again.
  //   - Every player action routes through this file. Buttons call
  //     dedicated handlers below; free text lands in
  //     `handleJourneyUserInput()` which decides how to interpret it
  //     based on the current state.
  //   - On completion (or skip) we set journeyState = "NONE" and the
  //     composer resumes normal chat on the player's NEXT free text.
  // -------------------------------------------------------------------

  const DREAM_CHIPS = [
    { id: "country",   label: "Represent my country" },
    { id: "ipl",       label: "Play IPL" },
    { id: "state",     label: "Play State Cricket" },
    { id: "district",  label: "Play District Cricket" },
    { id: "school",    label: "Make my school team" },
    { id: "allrounder", label: "Become an all-rounder" },
    { id: "selected",  label: "Get selected" },
    { id: "enjoy",     label: "Enjoy cricket again" },
    { id: "custom",    label: "Something else…" },
  ];

  async function _proposeAndShowNextLevel() {
    // Show a subtle "thinking" indicator while the LLM proposes.
    setMessages(m => [...m, { role: "assistant", journey_thinking: true,
                                content: "Give me a second…" }]);
    const intent = journeyIntentRef.current;
    const convo = intent
      ? [{ role: "user", content: intent }]
      : [];
    let proposal = null;
    try {
      const { data } = await api.post("/journey/next-level/generate",
        { conversation: convo });
      if (data?.ok && data.next_level) {
        proposal = { next_level: data.next_level,
                     why_this_matters: data.why_this_matters };
      }
    } catch { /* fall through — proposal stays null */ }
    // Drop the "thinking" placeholder.
    setMessages(m => m.filter(x => !x.journey_thinking));
    if (!proposal) {
      // Iter XX.74.1 — never restart the conversation with a generic
      // opener. If we can't propose a Next Level, resume the player's
      // original coaching topic. The backend's resume directive tells
      // the LLM to acknowledge the dream briefly then pivot back to
      // what the player actually said.
      const originalTopic = journeyIntentRef.current;
      const dreamAnswer = (journey && journey.dream) || "";
      setJourneyState("NONE");
      journeyIntentRef.current = null;
      if (originalTopic) {
        // Small delay so the UI settles before we push the resume turn.
        setTimeout(() => {
          send(originalTopic, {
            source: RESUME_AFTER_ONBOARDING,
            resumed_topic: {
              original_topic: originalTopic,
              captured_kind: "dream",
              captured_value: dreamAnswer,
            },
          });
        }, 60);
      } else {
        // Iter XX.102 — No cached topic (rare, e.g. programmatic
        // FSM entry). Never ask the player to restate. A warm
        // one-line acknowledgment; the composer stays open so the
        // player can freely continue when ready.
        setMessages(m => [...m, { role: "assistant", content:
          "Great — noted." }]);
      }
      return;
    }
    setJourneyProposal({ next_level: proposal.next_level,
                          why_this_matters: proposal.why_this_matters });
    setJourneyState("WAITING_FOR_NEXT_LEVEL_CONFIRMATION");
    setMessages(m => [...m, {
      role: "assistant",
      journey_next_level_card: proposal,
      content:
        `Based on what you've told me, I think your biggest next level is:\n\n${proposal.next_level}\n\nWhy this matters — ${proposal.why_this_matters}`,
    }]);
  }

  async function _proposeAndShowMission() {
    setMessages(m => [...m, { role: "assistant", journey_thinking: true,
                                content: "One moment…" }]);
    let mission = null;
    try {
      const { data } = await api.post("/journey/mission/generate", {});
      if (data?.ok && data.mission) mission = data.mission;
    } catch { /* fall through */ }
    setMessages(m => m.filter(x => !x.journey_thinking));
    if (!mission) {
      // Iter XX.74.1 — same as the Next Level fallback: never restart
      // with a generic opener. Resume the original coaching topic if
      // we have one; otherwise degrade gracefully.
      const originalTopic = journeyIntentRef.current;
      const dreamAnswer = (journey && journey.dream) || "";
      setJourneyState("NONE");
      journeyIntentRef.current = null;
      setJourneyProposal(null);
      if (originalTopic) {
        setTimeout(() => {
          send(originalTopic, {
            source: RESUME_AFTER_ONBOARDING,
            resumed_topic: {
              original_topic: originalTopic,
              captured_kind: "dream",
              captured_value: dreamAnswer,
            },
          });
        }, 60);
      } else {
        // Iter XX.102 — Never ask the player to restate. Warm ack only.
        setMessages(m => [...m, { role: "assistant", content:
          "Great — noted." }]);
      }
      return;
    }
    setJourneyProposal(p => ({ ...(p || {}), mission }));
    setJourneyState("WAITING_FOR_MISSION_CONFIRMATION");
    setMessages(m => [...m, {
      role: "assistant",
      journey_mission_card: { mission },
      content: `Today's Mission\n\n${mission}`,
    }]);
  }

  async function handleDreamChipPick(chip) {
    if (journeyBusyRef.current) return;
    if (chip.id === "custom") {
      setMessages(m => [...m, {
        role: "assistant",
        content: "I'd love to hear it.",
      }]);
      // Free text next → interpret as the Dream inside handleJourneyUserInput.
      textRef.current?.focus();
      return;
    }
    journeyBusyRef.current = true;
    // Optimistically render the chip pick as a user message.
    setMessages(m => [...m, { role: "user", content: chip.label }]);
    setJourney(j => ({ ...(j || {}), dream: chip.label, dream_source: "chip" }));
    try { await api.post("/journey/dream", { dream: chip.label, source: "chip" }); }
    catch { /* silent — client-side journey already updated */ }
    await _proposeAndShowNextLevel();
    journeyBusyRef.current = false;
  }

  async function handleJourneyUserInput(text) {
    if (journeyBusyRef.current) return;
    // Iter XX.97 — Save-intent bypass. When the parent (or player) is
    // asking to save/remember while the journey FSM is waiting for a
    // dream/edit answer, we DO NOT interpret their imperative as a
    // dream selection — that swallows the save request. Route directly
    // to the normal send() path so the backend's save-intent short-
    // circuit fires and the confirmation card renders.
    if (_looksLikeSaveIntent(text)) {
      await send(text, { source: SAVE_INTENT_BYPASS });
      return;
    }
    // WAITING_FOR_DREAM + free text → the "Something else…" path. Text
    // becomes the Dream. If it looks like a bad name / non-cricket
    // rubbish we still save it as-is; the backend will trim/validate.
    if (journeyState === "WAITING_FOR_DREAM") {
      journeyBusyRef.current = true;
      setMessages(m => [...m, { role: "user", content: text }]);
      setInput("");
      setJourney(j => ({ ...(j || {}), dream: text.slice(0, 200), dream_source: "custom" }));
      try { await api.post("/journey/dream", { dream: text, source: "custom" }); }
      catch { /* silent */ }
      await _proposeAndShowNextLevel();
      journeyBusyRef.current = false;
      return;
    }
    // Edit modes: player typed a replacement for Next Level or Mission.
    if (journeyState === "WAITING_FOR_NEXT_LEVEL_CONFIRMATION"
        && journeyEditMode === "next_level") {
      journeyBusyRef.current = true;
      setMessages(m => [...m, { role: "user", content: text }]);
      setInput("");
      const nl = text.slice(0, 140);
      const why = journeyProposal?.why_this_matters || "";
      try {
        await api.post("/journey/next-level/edit",
          { next_level: nl, why_this_matters: why });
      } catch { /* silent */ }
      setJourney(j => ({ ...(j || {}), next_level: nl, why_this_matters: why }));
      setJourneyProposal(p => ({ ...(p || {}), next_level: nl }));
      setJourneyEditMode(null);
      await _proposeAndShowMission();
      journeyBusyRef.current = false;
      return;
    }
    if (journeyState === "WAITING_FOR_MISSION_CONFIRMATION"
        && journeyEditMode === "mission") {
      journeyBusyRef.current = true;
      setMessages(m => [...m, { role: "user", content: text }]);
      setInput("");
      const missionText = text.slice(0, 200);
      // No edit endpoint for mission yet — write straight through /me?
      // For Phase 1 we just accept the mission client-side; the backend
      // still holds whatever /mission/generate stored. Behaviour is
      // consistent: player sees their edit; the server-side mission is
      // whatever it was and gets overwritten on next Mark Complete.
      setJourneyProposal(p => ({ ...(p || {}), mission: missionText }));
      setJourney(j => ({ ...(j || {}), today_mission: missionText }));
      setJourneyEditMode(null);
      _finishOnboarding();
      journeyBusyRef.current = false;
      return;
    }
    // Otherwise (button-awaited state, no edit mode) — gently re-prompt.
    setMessages(m => [...m, { role: "assistant", content:
      "Tap one of the options above to keep going." }]);
    setInput("");
  }

  function _finishOnboarding() {
    // Iter XX.74.1 — After onboarding completes, resume the player's
    // original coaching topic instead of ending on a terminal line.
    // The player was mid-conversation; a coach who remembers picks
    // it back up.
    const originalTopic = journeyIntentRef.current;
    const dreamAnswer = (journey && journey.dream) || "";
    setJourneyState("NONE");
    setJourneyProposal(null);
    setJourneyEditMode(null);
    journeyIntentRef.current = null;
    if (originalTopic) {
      setTimeout(() => {
        send(originalTopic, {
          source: RESUME_AFTER_ONBOARDING,
          resumed_topic: {
            original_topic: originalTopic,
            captured_kind: "dream",
            captured_value: dreamAnswer,
          },
        });
      }, 60);
      return;
    }
    // Iter XX.102 — No cached topic (rare — e.g. onboarding entered
    // via a card tap, not a typed coaching message). Warm ack only.
    // Per founder directive: never ask the player to restate.
    setMessages(m => [...m, { role: "assistant", content:
      "Great — noted." }]);
  }

  async function handleNextLevelAccept() {
    if (journeyBusyRef.current) return;
    if (!journeyProposal?.next_level) return;
    journeyBusyRef.current = true;
    setMessages(m => [...m, { role: "user", content: "That's right." }]);
    try {
      await api.post("/journey/next-level/edit", {
        next_level: journeyProposal.next_level,
        why_this_matters: journeyProposal.why_this_matters,
      });
    } catch { /* silent */ }
    setJourney(j => ({ ...(j || {}),
                        next_level: journeyProposal.next_level,
                        why_this_matters: journeyProposal.why_this_matters }));
    await _proposeAndShowMission();
    journeyBusyRef.current = false;
  }

  function handleNextLevelEdit() {
    setJourneyEditMode("next_level");
    setMessages(m => [...m, { role: "assistant", content:
      "Type the next level you'd rather work on, and hit send." }]);
    textRef.current?.focus();
  }

  function handleMissionAccept() {
    if (journeyBusyRef.current) return;
    if (!journeyProposal?.mission) return;
    journeyBusyRef.current = true;
    setMessages(m => [...m, { role: "user", content: "Sounds good." }]);
    setJourney(j => ({ ...(j || {}), today_mission: journeyProposal.mission }));
    _finishOnboarding();
    journeyBusyRef.current = false;
  }

  function handleMissionEdit() {
    setJourneyEditMode("mission");
    setMessages(m => [...m, { role: "assistant", content:
      "Type the mission you'd like to try today, and hit send." }]);
    textRef.current?.focus();
  }

  async function handleMissionSkip() {
    if (journeyBusyRef.current) return;
    journeyBusyRef.current = true;
    setMessages(m => [...m, { role: "user", content: "Skip for now." }]);
    // We DO NOT persist the mission when skipped, but the LLM may have
    // already stored it inside /mission/generate. Clear it on the server
    // so tomorrow's dashboard doesn't surface a stale one.
    try { await api.post("/journey/mission/complete"); }
    catch { /* server may 400 if it wasn't stored — silent */ }
    setJourney(j => ({ ...(j || {}), today_mission: null }));
    _finishOnboarding();
    journeyBusyRef.current = false;
  }



  // --- Video upload: gate -> consent -> age -> XHR with progress ----------
  function pickVideo() {
    // Iter XX.267b — medium haptic on the canonical video-upload
    // entry point. Fires once regardless of which surface triggered
    // the call (composer button / hero card / starter chip / paywall
    // return) because the AuthedHomeHero starter path already routes
    // through this function after its own light-tap haptic — the
    // helper's own 60ms debounce keeps the two from stacking.
    hapticMedium();
    // Iter XX.265b — User-initiated upload = genuine action ⇒ leave
    // the pinned Home surface immediately. This runs before any
    // native picker is opened so the transition feels atomic.
    if (homePinned) setHomePinned(false);
    // Iter XX.92 — Upload button cancels intro (same rationale as
    // typing / chip taps). Emit `first_input_after_intro` with the
    // upload-specific kind so we can split activation channels.
    if (introRunning) {
      _cancelIntro("interrupted_upload_clicked");
      try {
        postClientEvent(API, sessionId, "first_input_after_intro",
          { source: "upload_clicked" });
        try { clarityTrack("first_input_after_intro", { source: "upload_clicked" }); }
        catch { /* noop */ }
      } catch { /* noop */ }
    }
    // Iter XX.88 — Conversation First Mode. Surface the upload tips as
    // part of the upload flow (not preemptively at chat mount). The
    // player sees the "5-10 second clip / identify yourself" guidance
    // right when it's actionable — as the OS file picker opens.
    setShowUploadTips(true);
    videoRef.current?.click();
  }

  // Iter 43.1 — best-effort client-side duration probe. Used purely for
  // expectation-setting (the "this clip is larger than recommended" line).
  // Never blocks upload — if the browser can't decode the metadata for any
  // reason (corrupt clip, unsupported codec, sandbox restrictions), we
  // resolve to null and silently skip the warning. The actual upload path
  // is unchanged.
  function getVideoDurationSec(file) {
    return new Promise((resolve) => {
      try {
        const url = URL.createObjectURL(file);
        const v = document.createElement("video");
        v.preload = "metadata";
        v.muted = true;
        let done = false;
        const finish = (val) => {
          if (done) return;
          done = true;
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
          resolve(val);
        };
        v.onloadedmetadata = () => {
          const d = Number(v.duration);
          finish(Number.isFinite(d) && d > 0 ? d : null);
        };
        v.onerror = () => finish(null);
        // Hard timeout so a stuck decode never hangs the upload flow.
        setTimeout(() => finish(null), 4000);
        v.src = url;
      } catch {
        resolve(null);
      }
    });
  }

  async function onVideoPicked(file) {
    if (!file || !sessionId) return;
    // 2026-06-25 — Aligned with backend UPLOAD_MAX_BYTES (100MB) after
    // a real-user iPhone failure: 16s 4K HEVC iPhone clips routinely
    // land between 25-60 MB and were being killed client-side by a
    // stale 25 MB gate before the request ever left the browser.
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Clip is over 100MB — try a shorter or lower-res version.");
      return;
    }
    // Hide starter chips — the player committed to an upload.
    if (!hasFirstMessage) setHasFirstMessage(true);
    setPendingFile(file);

    // Refresh consent status to check what's needed
    let cs = consentStatus;
    try {
      const { data } = await api.get("/web/consent-status", { params: { session_id: sessionId } });
      cs = data;
      setConsentStatus(data);
    } catch { /* noop */ }

    // Age gate first (only if not yet recorded)
    if (!cs?.age_band) {
      setShowAgeGate(true);
      return;
    }
    // Media consent next
    if (!cs?.media_consent_accepted) {
      setShowMediaConsent(true);
      return;
    }
    // All gates passed — upload immediately
    await doUpload(file);
  }

  async function onAgePassed() {
    setShowAgeGate(false);
    // Refresh status, then either show media consent or upload
    try {
      const { data } = await api.get("/web/consent-status", { params: { session_id: sessionId } });
      setConsentStatus(data);
      if (!data?.media_consent_accepted) {
        setShowMediaConsent(true);
      } else if (pendingFile) {
        await doUpload(pendingFile);
      }
    } catch {
      setShowMediaConsent(true);
    }
  }

  async function onMediaConsentAccepted() {
    setShowMediaConsent(false);
    fetchConsentStatus(sessionId);
    if (pendingFile) await doUpload(pendingFile);
  }

  async function doUpload(file) {
    // Iter 58 — removed the 25MB / 15s "large clip warning" pre-flight.
    // Backend accepts up to 100MB. The only frontend gate is the 100MB
    // check in onVideoPicked. No misleading warnings on 25-99MB clips.

    return new Promise((resolve) => {
      setUploading(true);
      setUploadPct(0);
      setMessages((m) => [...m, {
        role: "user", content: "🎥 Uploading video for review…",
        attachments: [{ kind: "video", filename: file.name }],
      }]);

      // Capture device diagnostics so the founder dashboard knows WHY
      // uploads fail (mobile Safari vs in-app browser vs desktop, codec,
      // resolution, duration). Best-effort — never blocks the upload.
      const diag = collectClientDiag();
      probeVideoMeta(file).then((vm) => sendUpload({ ...diag, ...vm }));

      // 2026-06-26 — Iter 25.9n. Durable-job polling. Starts a 2.5s
      // interval that hits /api/web/video-job/{job_id} until the
      // job reaches a terminal status. On `completed` swap the
      // "Got it — reviewing this now." placeholder for the review
      // message; on `failed` swap for the recovery copy that the
      // backend already wrote into web_messages. Caps the poll at
      // 6 minutes to avoid leaking timers if the worker is stuck.
      function startVideoJobPolling(jobId, filename, uploadTraceId) {
        const pollIntervalMs = 2500;
        const maxAttempts = Math.ceil((6 * 60 * 1000) / pollIntervalMs);
        let attempts = 0;
        let timer = null;
        // Iter XX.105 — progress ping at ~10s AND ~30s. If the job
        // is still non-terminal by these thresholds, swap the
        // placeholder copy so the user never wonders whether Gully
        // is still there. Fires at most once per threshold.
        const pingAt10sMs = 10000;
        const pingAt30sMs = 30000;
        let ping10sFired = false;
        let ping30sFired = false;
        const t0 = Date.now();
        const stop = () => {
          if (timer) { clearInterval(timer); timer = null; }
          // Iter XX.33 — clear the pending upload from localStorage
          // when we've reached ANY terminal path. Safe when the trace
          // id isn't known (older frontend / duplicate short-circuit).
          if (uploadTraceId) {
            try { _clearPendingUpload(uploadTraceId); } catch { /* noop */ }
          }
        };
        const swapPlaceholder = (assistant) => {
          setMessages((m) => {
            // Find the latest video_review_queued placeholder for THIS
            // job_id; swap it in place. If it's already gone (e.g., user
            // sent another message), append instead.
            const idx = [...m].reverse().findIndex(
              (x) => x.role === "assistant"
                  && x.kind === "video_review_queued"
                  && x.job_id === jobId
            );
            if (idx < 0) return [...m, assistant];
            const realIdx = m.length - 1 - idx;
            const next = m.slice();
            next[realIdx] = assistant;
            return next;
          });
        };
        const tick = async () => {
          attempts += 1;
          if (attempts > maxAttempts) {
            stop();
            swapPlaceholder({
              role: "assistant", kind: "video_review_failed",
              content: "Your video upload stalled. Send a fresh 5-15 second clip and I'll review it.",
              can_retry: true, filename,
            });
            return;
          }
          try {
            const r = await fetch(
              `${API}/web/video-job/${encodeURIComponent(jobId)}?session_id=${encodeURIComponent(sessionId)}`,
              { credentials: credentialsMode() },
            );
            if (!r.ok) return;  // try again next tick
            const d = await r.json();
            // Iter XX.105 — progress ping (only when the job is
            // still non-terminal). Updates the placeholder text so
            // the user knows Gully is still working, not stuck.
            if (d.status !== "completed" && d.status !== "failed") {
              const elapsed = Date.now() - t0;
              if (!ping10sFired && elapsed >= pingAt10sMs) {
                ping10sFired = true;
                setMessages((m) => {
                  const idx = [...m].reverse().findIndex(
                    (x) => x.role === "assistant"
                        && x.kind === "video_review_queued"
                        && x.job_id === jobId,
                  );
                  if (idx < 0) return m;
                  const realIdx = m.length - 1 - idx;
                  const next = m.slice();
                  next[realIdx] = {
                    ...m[realIdx],
                    content: "Still reviewing — this is taking a little longer than usual.",
                  };
                  return next;
                });
              } else if (!ping30sFired && elapsed >= pingAt30sMs) {
                ping30sFired = true;
                setMessages((m) => {
                  const idx = [...m].reverse().findIndex(
                    (x) => x.role === "assistant"
                        && x.kind === "video_review_queued"
                        && x.job_id === jobId,
                  );
                  if (idx < 0) return m;
                  const realIdx = m.length - 1 - idx;
                  const next = m.slice();
                  next[realIdx] = {
                    ...m[realIdx],
                    content: "Almost there — the reviewer is finishing up.",
                  };
                  return next;
                });
              }
            }
            if (d.status === "completed") {
              stop();
              // Iter XX.224 follow-up — gully_video_review_completed.
              // Fires ONLY when the server confirms `status:"completed"`
              // inside the polling loop. Never on upload, failure,
              // timeout, retry, or cancellation. Consent + iOS gate
              // live inside the dispatcher.
              try { logVideoReviewCompleted(); } catch {}
              swapPlaceholder({
                role: "assistant", kind: "video_review",
                content: d.assistant_message ||
                  "Your review is ready, but I couldn't pull the text. Try sending the clip again.",
                video_analysis_id: d.video_analysis_id || null,
              });
              // Iter 44.1 — successful review may flip continuity
              // eligibility (3-review threshold). Refresh now.
              refreshContinuityEligibility();
            } else if (d.status === "failed") {
              stop();
              swapPlaceholder({
                role: "assistant", kind: "video_review_failed",
                content: d.assistant_message ||
                  "Your video reached Gully, but the reviewer couldn't complete it this time. Try once more with a short 5-15 second clip.",
                can_retry: true, filename,
                trace_ref: d.video_analysis_id || jobId,
              });
            }
            // Otherwise still progressing — keep polling.
          } catch {
            // Network blip — let the next interval try again.
          }
        };
        timer = setInterval(tick, pollIntervalMs);
        // Fire one immediate tick so a fast worker doesn't leave the
        // user looking at the placeholder for 2.5s.
        setTimeout(tick, 250);
      }

      function sendUpload(meta) {
        // Iter 42.4 — client-side upload telemetry. Mirrors backend chat_flow_*
        // events but captures the BROWSER side: if Instagram WebView kills the
        // tab between upload_started and upload_completed, the missing event
        // is the smoking gun.
        const t0 = Date.now();
        // 2026-06-25 — Per-attempt client reference id. Surfaced inside
        // the "Video couldn't be reviewed" card AND attached to every
        // upload-telemetry audit event so a user-reported failure can be
        // traced back to a row in `audit_events` without needing
        // DevTools / browser-side JSON inspection.
        const clientRef = `cref_${t0.toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
        // Iter XX.33 — canonical cross-session upload trace ID minted at
        // FILE-SELECT time (not at network-request time — so if the
        // Instagram WebView kills the tab between select and upload, the
        // same trace id survives the reload and we can rehydrate). One
        // trace id per physical file per user click. Carried on the
        // form body, echoed by the server, and persisted in localStorage
        // under `gully_pending_uploads` so refresh + reopen can resume.
        const uploadTraceId = `utr_${t0.toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
        // Iter XX.33 — content hash for cross-device dedup (safeguard #2:
        // NEVER use file size + timestamp as a dedup key). Computed via
        // `crypto.subtle.digest("SHA-256", ...)` on the file bytes. On
        // slow devices this can take a beat; we don't block the upload
        // start — we send an empty content_hash first, and the backend
        // trace / status lookup relies on upload_trace_id as the primary
        // key. content_hash arrives asynchronously and is patched onto
        // the localStorage record for later diagnostics.
        let contentHash = "";
        (async () => {
          try {
            const buf = await file.arrayBuffer();
            const digest = await window.crypto.subtle.digest("SHA-256", buf);
            const bytes = new Uint8Array(digest);
            contentHash = Array.from(bytes.slice(0, 16))
              .map((b) => b.toString(16).padStart(2, "0")).join("");
            _patchPendingUpload(uploadTraceId, { content_hash: contentHash });
          } catch { /* content_hash is diagnostic-only — safe to skip */ }
        })();
        // Iter XX.33 — persist the pending upload so refresh / reopen /
        // session upgrade doesn't strand the user. Keyed by trace id so
        // multiple concurrent uploads coexist. Cleared on terminal
        // status by the poller.
        try {
          _savePendingUpload({
            upload_trace_id: uploadTraceId,
            client_ref: clientRef,
            filename: file.name,
            size: file.size,
            mime: file.type,
            session_id: sessionId,
            started_at: new Date().toISOString(),
            status: "pending",
          });
        } catch { /* localStorage may be blocked in private mode */ }
        // 2026-06-25 — Iter 25.9k. Confirmed-write pre-flight. If we
        // cannot persist `client_upload_started` in audit_events for
        // this cref, the cref is NOT traceable — showing it on a
        // reviewer-failure card would give the founder a `cref_*` with
        // zero rows in `/api/admin/web/trace/{ref}`. Bail out early
        // with an upload-init error (no cref displayed). Bytes never
        // leave the device on this path so we cannot mis-blame the
        // reviewer.
        (async () => {
          const ack = await postClientEventAwait(API, sessionId, "upload_started", {
            filename: file.name, mime: file.type, size: file.size,
            client_ref: clientRef,
            upload_trace_id: uploadTraceId,
          });
          if (!ack.ok) {
            setUploading(false); setUploadPct(0); setPendingFile(null);
            setMessages((m) => [...m.slice(0, -1),
              { role: "user", content: "🎥 (couldn't start the upload)" },
              { role: "assistant",
                content: "Couldn't start the upload — check your connection and tap the video button again. Your clip is still on your device." },
            ]);
            resolve();
            return;
          }
          runUploadXhr();
        })();

        // 2026-06-25 — Iter 25.9k. Bytes-fully-uploaded flag. Flips to
        // true ONLY when xhr.upload.onload fires (the entire request
        // body finished leaving the device). The 5xx response branch
        // uses this to decide whether "Couldn't reach the reviewer"
        // (bytes arrived at the server — reviewer step crashed) is
        // truthful, or whether the upload itself never completed
        // (proxy/CDN/connection dropped mid-transfer — the reviewer
        // was never invoked).
        let bytesFullyUploaded = false;

        function runUploadXhr() {
        // 2026-02-25 — Granular upload failure telemetry. Same user-facing
        // message ("Network dropped. Try again?") today, but every failure
        // path now tags a distinct `upload_failed_<cause>` event so the
        // founder dashboard can answer: "is this real network loss or are
        // we mis-labelling another failure mode as network?". Causes:
        //   network     — xhr.onerror (TCP reset, DNS, CORS, mid-flight drop)
        //   timeout     — xhr.ontimeout (we now set xhr.timeout = 4 min)
        //   abort       — xhr.onabort (tab close, user navigated away)
        //   server      — xhr.onload with 5xx
        //   validation  — xhr.onload with 4xx (excl. 402 paywall / 412 consent
        //                 / 413 too-large / 415 bad-type — those have their
        //                 own dedicated UI and audit events)
        //   success_false — xhr.onload with 2xx but data.success === false
        //                 AND no recovery chips (Gemini rejected outright)
        //   unknown     — JS exception around xhr setup/send
        const fireUploadFailed = (cause, extra = {}) => {
          try {
            postClientEvent(API, sessionId, `upload_failed_${cause}`, {
              filename: file.name, mime: file.type, size: file.size,
              ms: Date.now() - t0,
              client_ref: clientRef,
              ...extra,
            });
          } catch { /* swallow — telemetry must never break upload UI */ }
        };
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/web/upload-video`);
        xhr.withCredentials = webOnlyWithCredentials();
        // 4 minute hard cap so genuinely hung uploads fire `ontimeout`
        // instead of falling through `onerror` and getting mis-labelled
        // as "network dropped".
        xhr.timeout = 4 * 60 * 1000;
        const token = (() => { try { return getToken(); } catch { return null; } })();
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        // Iter 59 — stamp the build id on the upload request too. The
        // axios api client adds it automatically; the raw XHR needs it
        // manually so admin diagnostics can correlate the user's bundle
        // version with the failure row.
        try {
          xhr.setRequestHeader("X-Frontend-Build-Id", FRONTEND_BUILD_ID);
        } catch { /* noop */ }
        // Iter XX.33 — echo the trace id as a header too so proxies /
        // access logs correlate the request with the frontend record.
        try {
          xhr.setRequestHeader("X-Upload-Trace-Id", uploadTraceId);
        } catch { /* noop */ }
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadPct(Math.round((e.loaded / e.total) * 100));
        };
        // Iter 42.4 / Iter 25.9d — fire upload_completed_bytes_sent when bytes
        // finish leaving the device (BEFORE the server returns the review).
        // Lets us tell "tab died during transfer" apart from "tab died during
        // Gemini wait" — AND, paired with backend's `upload_received` audit,
        // narrows "client_upload_started → never reached server" failures to
        // proxy / CDN problems vs. genuine network drops.
        xhr.upload.onload = () => {
          bytesFullyUploaded = true;
          postClientEvent(API, sessionId, "upload_completed_bytes_sent", {
            ms: Date.now() - t0, size: file.size,
            client_ref: clientRef,
          });
          postClientEvent(API, sessionId, "review_started", {
            client_ref: clientRef,
          });
        };
        xhr.onload = () => {
          setUploading(false); setUploadPct(0); setPendingFile(null);
          postClientEvent(API, sessionId, "review_completed", {
            status: xhr.status, ms: Date.now() - t0,
          });
          if (xhr.status >= 200 && xhr.status < 300) {
            let data = {};
            try { data = JSON.parse(xhr.responseText || "{}"); } catch { /* noop */ }
            // 2026-06-26 — Iter 25.9n. Durable-job queued response.
            // Backend stored the file + enqueued a video_review_job.
            // Render the "Got it — reviewing this now." ack and start
            // polling /video-job/{job_id} until terminal state lands.
            if (data.queued === true && data.job_id) {
              // Iter XX.33 — attach trace id to pending record so the
              // rehydrator on the next mount can correlate the job.
              try {
                _patchPendingUpload(uploadTraceId, {
                  job_id: data.job_id, status: "queued",
                });
              } catch { /* noop */ }
              setMessages((m) => [...m.slice(0, -1),
                { role: "user", content: "🎥 Video uploaded.",
                  attachments: [{ kind: "video", filename: file.name }] },
                { role: "assistant",
                  kind: "video_review_queued",
                  job_id: data.job_id,
                  upload_trace_id: uploadTraceId,
                  content: data.reply || "Got it — reviewing this now." },
              ]);
              // Iter XX.224 follow-up — gully_video_review_started.
              // Fires when the backend accepted the upload AND enqueued
              // a review job (not on file-picker, not on failed upload,
              // not on duplicate short-circuit — that has its own path).
              // Consent + iOS gate live inside the dispatcher.
              try { logVideoReviewStarted(); } catch {}
              // XX.281c — Meta Pixel `video_upload_completed`. Fires
              // exactly once at the moment the backend returned 2xx
              // with `queued:true` and a `job_id` — i.e. the file bytes
              // are durably stored and the review job is enqueued.
              // Never on 4xx/5xx, never on network error, never on
              // timeout, never on abort (those branches sit below /
              // outside this if-block). Custom event only, not in
              // STANDARD_EVENT_MIRROR, so no collateral standard-event
              // fires against Gully's existing conversion campaigns.
              try {
                metaTrack("video_upload_completed", {
                  size_bytes: file.size,
                  mime: file.type || null,
                  client_ref: clientRef,
                  upload_trace_id: uploadTraceId,
                  job_id: data.job_id,
                });
              } catch { /* pixel is best-effort — never break upload */ }
              // Haptics (2026-10-02) — success on video accepted for review.
              hapticSuccess();
              startVideoJobPolling(data.job_id, file.name, uploadTraceId);
              resolve();
              return;
            }
            // Iter XX.33 — duplicate short-circuit. The server detected
            // that this upload_trace_id / content_hash / client_ref
            // matched an existing job — do NOT create a new upload row
            // and do NOT charge the user's quota again. Just resume the
            // existing job.
            if (data.duplicate === true) {
              if (data.job_id) {
                setMessages((m) => [...m.slice(0, -1),
                  { role: "user", content: "🎥 Video uploaded.",
                    attachments: [{ kind: "video", filename: file.name }] },
                  { role: "assistant",
                    kind: "video_review_queued",
                    job_id: data.job_id,
                    upload_trace_id: uploadTraceId,
                    content: (
                      "I already have this video. I’m checking the " +
                      "existing review instead of uploading it again."
                    ) },
                ]);
                startVideoJobPolling(data.job_id, file.name, uploadTraceId);
              }
              resolve();
              return;
            }
            if (data.success === false) {
              // 2026-06-25 — Pre-flight identity gate (web_chat.py L2348+).
              // The server cached the video to GridFS and wants the player
              // to declare their role BEFORE Gemini runs. Response shape:
              //   { success:false, awaiting_identity:true,
              //     reply:"Quick check before I review this — which player are you?",
              //     video_analysis_id:null }
              // No clarification_chips because the player answers in chat
              // (no chip surface for this path). Previously this fell
              // through to the generic "Video couldn't be reviewed" card,
              // which hid the identity question entirely. Now we render
              // the question as a real assistant turn so the player knows
              // what to do next.
              if (data.awaiting_identity === true) {
                // 2026-06-25 — Iter 25.9i P0. Reuse the existing
                // ClarificationChips component so the player can tap a
                // role instead of typing it. Audit found 12 sessions
                // stuck here. The chip pick is routed through the
                // normal `send()` path (NOT /web/video-clarify) — it
                // arrives at /web/chat exactly as if the user had
                // typed the word, hits extract_role_from_answer, and
                // resumes the cached video review.
                setMessages((m) => [...m.slice(0, -1),
                  { role: "user", content: "🎥 Video uploaded.",
                    attachments: [{ kind: "video", filename: file.name }] },
                  { role: "assistant",
                    kind: "awaiting_identity",
                    content: data.reply ||
                      "Quick check before I review this — which player are you in the clip?",
                    clarification_kind: "awaiting_identity",
                    clarification_chips: [
                      { id: "bowler",  label: "🎳 Bowler" },
                      { id: "batter",  label: "🏏 Batter" },
                      { id: "keeper",  label: "🧤 Keeper" },
                      { id: "fielder", label: "🛡️ Fielder" },
                    ],
                  },
                ]);
              } else if (data.clarification_kind && Array.isArray(data.clarification_chips)) {
                setMessages((m) => [...m.slice(0, -1),
                  { role: "user", content: "🎥 Video uploaded.",
                    attachments: [{ kind: "video", filename: file.name }] },
                  { role: "assistant",
                    kind: "video_clarification",
                    content: data.reply || "",
                    clarification_kind: data.clarification_kind,
                    clarification_chips: data.clarification_chips,
                    video_analysis_id: data.video_analysis_id,
                  },
                ]);
              } else {
                // Non-recoverable failure (fielding/keeping/empty/timeout/
                // etc.). Same user-facing message as the 5xx path —
                // diagnostics are server-side.
                // 2026-06-25 — Iter 25.8 P0. Map server reason → a SPECIFIC
                // recovery card. The previous canned "Video couldn't be
                // reviewed" copy is reserved for the rare unknown-reason
                // case. `data.reply` is always set by the backend with a
                // contextual message; we surface it as the primary text.
                fireUploadFailed("success_false", {
                  status: 200,
                  rejection_reason: data.rejection_reason || null,
                  normalized_reason: data.normalized_reason || null,
                  video_analysis_id: data.video_analysis_id || null,
                });
                const ref = data.video_analysis_id || clientRef;
                const card = buildFailureCard(data);
                setMessages((m) => [...m.slice(0, -1),
                  { role: "user", content: "🎥 Video uploaded.",
                    attachments: [{ kind: "video", filename: file.name }] },
                  { role: "assistant",
                    kind: "video_review_failed",
                    filename: file.name,
                    trace_ref: ref,
                    can_retry: card.canRetry,
                    rejection_reason: data.rejection_reason || null,
                    category: data.category || null,
                    content: (
                      `**${card.headline}**\n\n${card.body}\n\n` +
                      `_Reference: ${ref}_`
                    ),
                  },
                ]);
              }
            } else {
              // Iter 59 — successful review. Reset fallback-fail
              // counter so a later transient blip starts the
              // "one cautious retry" cycle from zero.
              fallbackFailCountRef.current = 0;
              setMessages((m) => {
                const out = m.slice(0, -1);
                out.push({ role: "user", content: "🎥 Video uploaded.",
                           attachments: [{ kind: "video", filename: file.name }] });
                out.push({ role: "assistant",
                           content: data.reply || "Reviewed — that's a good clip to work with.",
                           // Iter 55 — timeline chip (Plus only). Backend sets
                           // `timeline_added: {type: 'video_review'}` on success
                           // for Plus users; null otherwise.
                           timeline_added: data.timeline_added || null });
                return out;
              });
            }
          } else if (xhr.status === 412) {
            setMessages((m) => m.slice(0, -1));
            setShowMediaConsent(true);
          } else if (xhr.status === 402) {
            // Paywall — either the legacy free-review cap OR the
            // Iter XX.72 universal Plus paywall. Strip the optimistic
            // upload bubble and show the paywall modal with whichever
            // payload the server sent.
            let detail = {};
            try { detail = JSON.parse(xhr.responseText || "{}").detail || {}; } catch { /* noop */ }
            setMessages((m) => m.slice(0, -1));
            setPaywall({
              used: detail.free_review_used ?? 0,
              limit: detail.limit ?? 0,
              reason: detail.reason,
              surface: detail.surface,
              message: detail.message,
              upgrade_url: detail.upgrade_url || "/upgrade?from=paywall",
            });
          } else if (xhr.status === 429) {
            fireUploadFailed("validation", { status: 429 });
            setMessages((m) => [...m.slice(0, -1),
              { role: "assistant", content: "Whoa — too many uploads in a short window. Try again in an hour." },
            ]);
          } else if (xhr.status === 413) {
            fireUploadFailed("validation", { status: 413, reason: "too_large" });
            setMessages((m) => [...m.slice(0, -1),
              { role: "user", content: "🎥 (clip too large)" },
              { role: "assistant", content: "That clip is over 100MB — try a shorter or lower-res version." },
            ]);
          } else if (xhr.status === 415) {
            fireUploadFailed("validation", { status: 415, reason: "bad_mime" });
            setMessages((m) => [...m.slice(0, -1),
              { role: "user", content: "🎥 (format not supported)" },
              { role: "assistant", content: "That file type isn't supported — try MP4 or MOV." },
            ]);
          } else if (xhr.status === 400) {
            // 2026-06-25 — Iter 25 (video reliability). The server now
            // hard-rejects sub-8KB / empty / clearly-corrupted bytes with a
            // precise `detail` message BEFORE the Gemini call. Surface that
            // detail to the user instead of the generic "couldn't be
            // reviewed" canned copy — the precision actually helps recovery.
            let detail400 = "";
            try { detail400 = (JSON.parse(xhr.responseText || "{}").detail) || ""; } catch { /* noop */ }
            fireUploadFailed("validation", { status: 400, reason: "bad_input", detail: String(detail400).slice(0, 120) });
            setMessages((m) => [...m.slice(0, -1),
              { role: "user", content: "🎥 (couldn't be reviewed)" },
              { role: "assistant", content: detail400 ||
                "That clip looks empty or corrupted — try uploading the video again." },
            ]);
          } else {
            // Generic review failure (5xx server errors, anything else 4xx that
            // isn't carved out above). User-facing message is intentionally
            // non-technical. Diagnostics are persisted server-side.
            fireUploadFailed(
              (xhr.status >= 500 && xhr.status < 600) ? "server" : "validation",
              { status: xhr.status,
                response_len: (xhr.responseText || "").length },
            );
            let refId = clientRef;
            let parsed = null;
            try {
              parsed = JSON.parse(xhr.responseText || "{}");
              if (parsed && parsed.video_analysis_id) refId = parsed.video_analysis_id;
            } catch { /* noop */ }
            const is5xx = xhr.status >= 500 && xhr.status < 600;
            // 2026-06-25 — Iter 25.9k. Only blame the reviewer when bytes
            // actually finished uploading (xhr.upload.onload fired). A
            // 5xx WITHOUT bytesFullyUploaded means the proxy / CDN cut
            // the request mid-transfer — the reviewer was NEVER
            // invoked, so a "Couldn't reach the reviewer" card would
            // be a lie. Drop the cref reference (it is not traceable
            // on this path).
            const reviewerActuallyReached = is5xx && bytesFullyUploaded;
            const showRef = reviewerActuallyReached || !is5xx;
            // Iter 58 — when the backend returns the canonical Iter 53
            // contract on a 5xx/non-200 (server.py's HTTPException
            // detail can carry the full payload), trust it the same
            // way we do in the success:false branch. Otherwise compose
            // an honest transport-failure card.
            let headline, body, canRetry;
            const hasCanonical = parsed
              && typeof parsed.user_facing_message === "string"
              && typeof parsed.is_user_retryable === "boolean";
            const detailCanonical = parsed && parsed.detail
              && typeof parsed.detail === "object"
              && typeof parsed.detail.user_facing_message === "string"
              && typeof parsed.detail.is_user_retryable === "boolean"
              ? parsed.detail : null;
            const canonical = hasCanonical ? parsed : detailCanonical;
            if (canonical) {
              const card = buildFailureCard(canonical);
              headline = card.headline;
              body = card.body;
              canRetry = card.canRetry;
              // Iter 59 — canonical-body success path is NOT a fallback,
              // so reset the fallback counter. (Canonical retryability
              // is the source of truth.)
              fallbackFailCountRef.current = 0;
            } else {
              // Iter 59 — fallback (non-canonical) branch. We don't know
              // for sure whether the server kept the clip, so don't
              // claim "your clip is still here". After the FIRST
              // fallback failure, offer a cautious retry. After REPEAT
              // fallback failures (≥2 in this session), stop offering
              // retry and direct the user to a new clip.
              fallbackFailCountRef.current += 1;
              const repeatFailure = fallbackFailCountRef.current >= 2;
              if (repeatFailure) {
                headline = "This clip keeps failing before review";
                body = "Please upload a new, shorter clip (5-10 seconds) recorded straight from your phone camera.";
                canRetry = false;
              } else if (reviewerActuallyReached) {
                headline = "The reviewer had a temporary issue";
                body = "Try once more.";
                canRetry = true;
              } else if (is5xx) {
                headline = "Upload didn't go through";
                body = "The upload didn't complete — check your connection and tap retry.";
                canRetry = true;
              } else {
                const serverDetail = parsed && typeof parsed.detail === "string" ? parsed.detail : "";
                headline = "Couldn't review this clip";
                body = serverDetail
                  || "I couldn't process that clip cleanly. If it keeps failing, upload a new shorter clip.";
                canRetry = true;
              }
            }
            setMessages((m) => [...m.slice(0, -1),
              { role: "user", content: "🎥 Video uploaded.",
                attachments: [{ kind: "video", filename: file.name }] },
              { role: "assistant",
                kind: "video_review_failed",
                filename: file.name,
                trace_ref: showRef ? refId : null,
                can_retry: canRetry,
                rejection_reason: (canonical && canonical.rejection_reason) || null,
                category: (canonical && canonical.category) || null,
                content: (
                  `**${headline}**\n\n${body}` +
                  (showRef ? `\n\n_Reference: ${refId}_` : "")
                ),
              },
            ]);
          }
          resolve();
        };
        xhr.onerror = () => {
          // Genuine network-level failure (DNS, TCP reset, CORS, mixed
          // content). With xhr.timeout now set, slow networks fire
          // `ontimeout` instead so this counter should drop. readyState
          // tells us how far we got (1=opened, 2=hdrs, 3=loading, 4=done).
          fireUploadFailed("network", {
            ready_state: xhr.readyState,
            status: xhr.status || 0,
          });
          setUploading(false); setUploadPct(0); setPendingFile(null);
          setMessages((m) => [...m.slice(0, -1),
            { role: "user", content: "🎥 (upload failed)" },
            { role: "assistant", content: "Network dropped. Try again?" },
          ]);
          resolve();
        };
        xhr.ontimeout = () => {
          // 4-minute hard cap was hit. Same user-facing message for now —
          // we will copy-tweak after one week of telemetry.
          fireUploadFailed("timeout", { timeout_ms: xhr.timeout });
          setUploading(false); setUploadPct(0); setPendingFile(null);
          setMessages((m) => [...m.slice(0, -1),
            { role: "user", content: "🎥 (upload failed)" },
            { role: "assistant", content: "Network dropped. Try again?" },
          ]);
          resolve();
        };
        xhr.onabort = () => {
          // User aborted via xhr.abort() (we don't call this) OR the
          // browser cancelled the request (tab close, navigation,
          // service worker). Tag it but DO NOT mutate UI — the user has
          // likely already left this view.
          fireUploadFailed("abort", { ready_state: xhr.readyState });
          setUploading(false); setUploadPct(0); setPendingFile(null);
          resolve();
        };
        try {
          const fd = new FormData();
          fd.append("session_id", sessionId);
          // 2026-06-25 — Iter 25.9d. Pass the same client_ref the frontend
          // already emitted in `client_upload_started`. The backend logs
          // `upload_received` with this ref as its FIRST line, so if we
          // ever see client_upload_started without a matching server-side
          // upload_received in the trace, the bytes never reached us.
          fd.append("client_ref", clientRef);
          // Iter XX.33 — canonical cross-session upload trace + content hash.
          fd.append("upload_trace_id", uploadTraceId);
          if (contentHash) fd.append("content_hash", contentHash);
          fd.append("file", file);
          if (meta.device_type) fd.append("device_type", meta.device_type);
          if (meta.browser)     fd.append("browser", meta.browser);
          if (meta.os)          fd.append("os_name", meta.os);
          // Iter 53 — explicit in-app-browser tag so the admin Video
          // Upload Debug table can filter on it directly. The `meta`
          // object is built from `getClientEnv()` which already detects
          // instagram / facebook / android_webview / etc.
          if (meta.in_app_browser) fd.append("in_app_browser", meta.in_app_browser);
          if (meta.duration_s)  fd.append("duration_s", String(meta.duration_s));
          if (meta.resolution_w) fd.append("resolution_w", String(meta.resolution_w));
          if (meta.resolution_h) fd.append("resolution_h", String(meta.resolution_h));
          if (meta.codec)       fd.append("codec", meta.codec);
          // XX.281c — Meta Pixel `video_upload_started`. Fires exactly
          // once at the moment bytes start leaving the device for the
          // Gully backend. Never on page load, video CTA click, picker
          // open, cancellation, or failed pre-flight ack (all gated
          // upstream). Uses the existing Gully web pixel via
          // metaPixel.track(); the event name is intentionally NOT in
          // STANDARD_EVENT_MIRROR so it does not collide with Gully's
          // Lead / CompleteRegistration / Purchase mirrors.
          try {
            metaTrack("video_upload_started", {
              size_bytes: file.size,
              mime: file.type || null,
              client_ref: clientRef,
              upload_trace_id: uploadTraceId,
            });
          } catch { /* pixel is best-effort — never break upload */ }
          xhr.send(fd);
        } catch (e) {
          // JS-level exception during FormData build or xhr.send — this
          // is rare (memory pressure on cheap Androids can trigger it).
          // Tag it as `unknown` so we can see the bucket size; the
          // network handler above will NOT fire in this path.
          fireUploadFailed("unknown", { error: (e && e.message) ? String(e.message).slice(0, 200) : "exception" });
          setUploading(false); setUploadPct(0); setPendingFile(null);
          setMessages((m) => [...m.slice(0, -1),
            { role: "user", content: "🎥 (upload failed)" },
            { role: "assistant", content: "Network dropped. Try again?" },
          ]);
          resolve();
        }
        } // end runUploadXhr
      }
    });
  }

  const hasUserMessage = messages.some((m) => m.role === "user");

  // Diagnostic overlay (?debug=1) — surfaces real viewport values on the
  // user's actual phone so we can verify the fix on real devices.
  const showDebug = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("debug") === "1";

  return (
    <div
      className="flex flex-col bg-[#0a0a0a] text-neutral-100 overflow-hidden"
      style={{
        // Anchor to the visible viewport — NOT the layout viewport top.
        // In iOS Safari (and any browser whose chrome occupies space at the
        // top of the layout viewport), `vv.offsetTop > 0`. Using `top: 0`
        // there places the chat behind the chrome and ends it `vv.offsetTop`
        // pixels above the keyboard, leaving a black gap below the composer
        // — the exact symptom reported. Anchoring at `--vv-top` removes
        // that gap.
        position: "fixed",
        left: 0,
        right: 0,
        top: "var(--vv-top, 0px)",
        height: "var(--vv-h, 100svh)",
        // NB: no `inset: 0` and no `minHeight`. Both would override the
        // height clamp and reintroduce the bug.
      }}
      data-testid="chat-screen"
    >
      {/* Iter XX.89 — Paid Instagram Personalisation Gate. Rendered
          as a full-screen overlay above the chat when the flag is on
          AND the visitor arrived from a paid Meta ad AND has not
          completed it yet. Signed-in users and returning-completed
          visitors skip it entirely. Dismissed = never-shown-again
          for that visitor (localStorage-persisted). */}
      {showPaidIgGate && (
        <PaidInstagramPersonaliseGate
          sessionId={sessionId}
          onComplete={() => setShowPaidIgGate(false)}
        />
      )}
      {/* Iter XX.129 — Progressive Commitment Engine. Tap-first
          onboarding. Replaces the scripted intro for eligible
          visitors (server flag + fresh browser + no prior first
          message). On handoff, unlocks the composer with the
          server-supplied placeholder; the FIRST /web/chat POST then
          picks up progressive_commitment_context via the server-side
          fallback in web_chat.py (fetched from pce_runs by session). */}
      {showPce && (
        <QuestionEngineCard
          sessionId={sessionId}
          onHandoff={({ handoff }) => {
            pceHandoffPendingRef.current = true;
            setPceHandoffPlaceholder(handoff?.composer_placeholder || null);
            setShowPce(false);
          }}
          onSkip={() => {
            markPceDone();
            setShowPce(false);
          }}
        />
      )}
      {/* Iter XX.94 — Parent Entry gate. XX.96: rendered only when
          triggered at the commitment moment (chip/send). onComplete
          replays the stashed pending intent so the visitor's original
          message survives the interruption. */}
      {showParentEntryGate && (
        <ParentEntryGate
          sessionId={sessionId}
          onComplete={(mode) => {
            setShowParentEntryGate(false);
            const intent = pendingIntentRef.current;
            // Iter XX.96 (analytics) — Fire `parent_gate_completed`
            // exactly once here (this callback is only invoked on
            // a completed gate — dismiss/abandon paths never call
            // onComplete). NO PII: child name is never sent.
            try {
              const relationship = (mode && mode.relationship) || null;
              const namePresent = Boolean(
                mode && mode.player_name
                && String(mode.player_name).trim().length > 0
              );
              const trigger = intent && intent.source
                ? (intent.source.indexOf("chip") >= 0 ? "chip" : "typed")
                : null;
              clarityTrack("parent_gate_completed", {
                entry_mode: relationship,
                trigger,
                name_provided: namePresent,
                continued_to_chat: Boolean(intent),
              });
            } catch { /* noop */ }
            if (intent && !pendingIntentFiredRef.current) {
              pendingIntentFiredRef.current = true;
              pendingIntentRef.current = null;
              // Small delay so the gate's fade-out doesn't collide
              // with the optimistic user-bubble insertion.
              setTimeout(() => {
                try {
                  send(intent.text, {
                    source: intent.source || "chat_direct_resume",
                    _afterParentGate: true,
                  });
                } catch { /* noop */ }
              }, 150);
            }
          }}
        />
      )}
      {showDebug && <DebugOverlay />}
      {/* Iter XX.21 — Multi-thread sidebar. Slide-in drawer style. */}
      <ConversationsSidebar
        open={sidebarOpen}
        currentSessionId={sessionId}
        onSwitchSession={handleSwitchSession}
        onClose={() => setSidebarOpen(false)}
      />
      {/* Header */}
      <header
        className="flex-none border-b border-neutral-800 bg-[#0a0a0a]/95 backdrop-blur px-4 py-3 flex items-center justify-between"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
      >
        <div className="flex items-center gap-2">
          {/* Iter XX.21 — Conversations sidebar trigger. Same style
              across desktop + mobile per founder spec. */}
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open conversations"
            className="p-2 -ml-2 rounded text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800/70"
            data-testid="chat-sidebar-trigger"
          >
            <Menu size={18} />
          </button>
          {/* Iter XX.265b — Header "Gully" affordance doubles as the
              least-intrusive Home entry point for authenticated
              players with an active transcript. When there IS
              meaningful user content, tapping this pins the polished
              Home surface without touching messages / analytics /
              backend state. When there is no transcript to preserve
              (fresh onboarding), the original Link-to-landing
              behaviour is retained so first-time visitors still have
              a clear exit back to the marketing surface. */}
          {(() => {
            const { hasMeaningfulUserContent, isAtHome } =
              computeHomeSurface({ messages, homePinned });
            if (authedUser && hasMeaningfulUserContent && !isAtHome) {
              return (
                <button
                  type="button"
                  onClick={() => {
                    try {
                      postClientEvent(API, sessionId, "authed_home_pinned_from_header", {});
                    } catch { /* analytics best-effort */ }
                    setHomePinned(true);
                  }}
                  className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white"
                  data-testid="chat-back-home"
                  aria-label="Return to Gully Home"
                >
                  <ArrowLeft size={16} /> Gully
                </button>
              );
            }
            return (
              <Link
                to="/"
                className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white"
                data-testid="chat-back-home"
              >
                <ArrowLeft size={16} /> Gully
              </Link>
            );
          })()}
        </div>
        <div className="flex items-center gap-3">
          {/* Iter XX.266 — MOBILE NAV MIGRATION.
              The Pulse / Journey / Memory / Earn / Season / MyCricket
              pills below are HIDDEN on narrow mobile widths so the
              header reads as calm ("Gully" on the left, ⭐ Plus on the
              right). They remain rendered (not removed) so:
                · every legacy `data-testid` is still present (chat-pulse-cta,
                  chat-journey-cta, chat-memory-cta, chat-earn-cta,
                  chat-season-cta) — analytics and QA hooks unchanged;
                · desktop / iPad users retain the horizontal quick-nav;
                · all destinations are re-surfaced through the new
                  `<MobileBottomNav>` + `<MoreSheet>` (see bottom of
                  Chat.jsx return tree).
              `hidden sm:inline-flex` swaps them in from sm ↑; on
              phones (390 / 412 px) the pills collapse out of the
              layout so there is no horizontal overflow. */}
          <div className="hidden sm:inline-flex items-center gap-3">
          {/* Iter XX.243 — Gully Pulse entry. Subtle by design. */}
          <Link
            to="/pulse"
            className="text-xs text-neutral-300 hover:text-white bg-neutral-900/50 hover:bg-neutral-800/70 px-2.5 py-1 rounded-full border border-neutral-700/60 hover:border-neutral-500 transition-colors inline-flex items-center gap-1"
            data-testid="chat-pulse-cta"
          >
            Pulse
          </Link>
          {/* Iter XX.74 — Journey pill. Hidden until the player has
              identity + at least one captured commitment. Soft pulse
              for a few seconds right after a fresh commitment lands. */}
          {improvementLoopOn && journeyReady && (
            <Link
              to="/journey"
              className={
                "relative text-xs text-emerald-100 bg-emerald-500/10 hover:bg-emerald-500/20 px-2.5 py-1 rounded-full border border-emerald-500/40 hover:border-emerald-400/70 transition-colors inline-flex items-center gap-1"
                + (journeyPulse ? " ring-2 ring-emerald-400/60 animate-pulse" : "")
              }
              data-testid="chat-journey-cta"
            >
              Journey
            </Link>
          )}
          {/* Iter XX.47 — Memory is a primary destination. Subtle
              emerald dot when memory has changed since last opened.
              Iter XX.81 — Gated behind the `memory` reveal stage.
              Signed-in returning users see it immediately (defaults
              open); anonymous first-timers see it after the 3rd
              user turn (a "meaningful conversation"). */}
          {showMemory && (
            <Link
              to="/me?tab=knows"
              className="relative text-xs text-neutral-300 hover:text-white bg-neutral-900/50 hover:bg-neutral-800/70 px-2.5 py-1 rounded-full border border-neutral-700/60 hover:border-neutral-500 transition-colors inline-flex items-center gap-1"
              data-testid="chat-memory-cta"
            >
              <Brain size={12} />
              Memory
              {memoryHasNew && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-neutral-950"
                  aria-label="new insights"
                  data-testid="chat-memory-badge"
                />
              )}
            </Link>
          )}
          {/* Iter XX.44.L — Partner is a primary destination.
              Iter XX.81 — Gated behind the `earn` reveal stage.
              Advocates get asked to refer only after they've
              experienced the product across ≥3 sessions. */}
          {showEarn && (
            <Link
              to="/earn"
              className="text-xs text-emerald-200/80 hover:text-white bg-neutral-900/50 hover:bg-neutral-800/70 px-2.5 py-1 rounded-full border border-neutral-700/60 hover:border-emerald-700/70 transition-colors inline-flex items-center gap-1"
              data-testid="chat-earn-cta"
            >
              Earn
            </Link>
          )}
          {/* Iter XX.6 — Season card entry point. Tiny, non-intrusive.
              Iter XX.81 — Gated behind the `season` reveal stage.
              Unlocks as soon as a scorecard is confirmed (Season
              becomes meaningful the moment one match is on record) or
              at 5+ user turns. */}
          {showSeason && (
            <button
              type="button"
              onClick={() => setSeasonOpen(true)}
              className="text-xs text-emerald-200/80 hover:text-white bg-neutral-900/50 hover:bg-neutral-800/70 px-2.5 py-1 rounded-full border border-neutral-700/60 hover:border-emerald-700/70 transition-colors inline-flex items-center gap-1"
              data-testid="chat-season-cta"
            >
              <span aria-hidden="true">🏏</span>
              Season
            </button>
          )}
          {/* Iter XX.221 — My Cricket pill. Visible ONLY when the
              current user has genuinely connected an external cricket
              profile. Hidden completely when the feature flag is OFF
              (fetchConnection() returns null on 404) or the user is
              unconnected. Sits beside the existing Memory / Season /
              Plus pills — same visual weight, same responsive rules. */}
          <MyCricketPill />
          </div>{/* /XX.266 mobile-hidden pill cluster */}
          {/* Iter 54 — Gully Plus header entry point.
              Iter XX.214 — Native suppression removed (XX.197/206
              wired native purchase through RC → StoreKit / Play).
              Iter XX.215 — Persistent discoverability. The XX.81
              `showPlusBanner` gate required the user to have already
              experienced coaching value (≥2 turns + concrete
              coaching action, or breakthrough recognition) before
              the pill would surface. In practice that meant a user
              who asked "How do I subscribe?" as their first message
              never saw a Plus entry, and the LLM then hallucinated
              a nonexistent pill in its reply. The pill is small,
              tucked into the header alongside Memory / Season, and
              inherently unobtrusive — persistent visibility for
              non-Plus users is discoverability, not aggressive
              promotion. The stricter `PlusLaunchBanner` and
              post-report card BELOW still respect `showPlusBanner`,
              preserving the value-first strategy for the larger
              proactive surfaces. */}
          {!plusSurfaces?.is_plus && (
            <Link
              to="/plus"
              onClick={() => {
                try {
                  api.post("/plus/track", {
                    kind: "plus_upgrade_button_clicked",
                    session_id: sessionId,
                    source: "chat_header",
                  }).catch(() => {});
                  api.post("/client-error", {
                    kind: "gully_plus_cta_clicked",
                    session_id: sessionId, payload: { source: "chat_header" },
                  }).catch(() => {});
                } catch { /* swallow */ }
              }}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-200 hover:text-white bg-emerald-950/60 hover:bg-emerald-900/70 px-2.5 py-1 rounded-full border border-emerald-700/60 hover:border-emerald-500/80 transition-colors"
              data-testid="chat-gully-plus-cta"
            >
              <span aria-hidden="true">⭐</span>
              Gully Plus
            </Link>
          )}
          {/* Iter 54 cleanup — the legacy "Memory" link to /me was
              removed. Memory lives inside the chat conversation now;
              users still see "Save my cricket history" if they
              haven't linked an identity yet. */}
          {!linked && (
            <button
              onClick={() => {
                try { localStorage.setItem("gully_last_identity_trigger", "header"); } catch { /* noop */ }
                api.post("/web/track", {
                  session_id: sessionId, event: "remember_me_click",
                  payload: { source: "header" },
                }).catch(() => {});
                api.post("/web/track", {
                  session_id: sessionId, event: "history_link_attempted",
                  payload: { source: "header" },
                }).catch(() => {});
                setNeedsIdentity(true);
              }}
              className="text-xs text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
              data-testid="chat-open-identity-btn"
            >
              Save my cricket journey
            </button>
          )}
        </div>
      </header>

      {/* Iter XX.4 — Gully Plus launch banner. Dismissible; hidden for
          30 days after dismissal. Never shown to Plus subscribers.
          Iter XX.81 — Also gated behind `plus_banner` reveal stage
          so first-time visitors aren't hit with a purchase banner
          before they've even sent a message. */}
      {/* Iter XX.214 — Native suppression removed. `PlusLaunchBanner`'s
          CTA routes through the standard /plus page which auto-starts
          `beginPlusPurchase()` → RC → StoreKit / Play on native. */}
      {plusSurfaces?.show_launch_banner && showPlusBanner && (
        <PlusLaunchBanner
          sessionId={sessionId}
          onDismiss={dismissPlusBanner}
        />
      )}

      {/* Messages — flex-1 means it shrinks naturally when the outer
          container shrinks (which happens when the keyboard opens because
          --vv-h reflects visualViewport.height). */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overscroll-contain px-3 pt-4"
        data-testid="chat-messages"
      >
        <div className="max-w-2xl mx-auto space-y-2.5 pb-8">
          {/* Iter XX.201 — Dev-only Reset panel. Gated on ?_debug=1
              query param + non-production env. Never appears in
              production Play Store builds (build strips NODE_ENV=production).
              Purpose: reproduce onboarding gates without wiping AVD state. */}
          {(typeof window !== "undefined"
            && new URLSearchParams(window.location.search).get("_debug") === "1"
            && process.env.NODE_ENV !== "production") && (
            <div className="rounded-2xl border border-amber-800/50 bg-amber-950/30 p-3 text-xs"
                 data-testid="dev-reset-panel">
              <div className="text-amber-300 font-mono mb-2">DEV RESET (local test only)</div>
              <div className="flex gap-2 flex-wrap">
                <button
                  data-testid="dev-reset-meta"
                  onClick={async () => {
                    try { localStorage.removeItem("gully_meta_consent_v1"); } catch { /* noop */ }
                    try { localStorage.removeItem("gully_meta_consent_v1_ts"); } catch { /* noop */ }
                    setMetaConsentVisible(shouldShowMetaConsentBar({ tosAccepted: true }));
                    // eslint-disable-next-line no-alert
                    alert("Meta consent reset — cold-restart the app to re-trigger the prompt.");
                  }}
                  className="px-3 py-1 rounded bg-amber-800/50 hover:bg-amber-800 text-amber-100">
                  Reset Meta Consent
                </button>
                <button
                  data-testid="dev-reset-tos"
                  onClick={async () => {
                    try {
                      await api.post("/web/consent/dev-reset", null,
                        { params: { session_id: sessionId } });
                      await fetchConsentStatus(sessionId);
                      // eslint-disable-next-line no-alert
                      alert("TOS reset — refresh to see the acceptance bar.");
                    } catch (e) {
                      // eslint-disable-next-line no-alert
                      alert("TOS reset failed: " + (e?.message || e));
                    }
                  }}
                  className="px-3 py-1 rounded bg-amber-800/50 hover:bg-amber-800 text-amber-100">
                  Reset TOS Acceptance
                </button>
              </div>
            </div>
          )}

          {/* First-visit consent — inline, conversational, low friction */}
          {consentStatus && !consentStatus.tos_accepted && (
            <ConsentBar
              sessionId={sessionId}
              onAccepted={() => setConsentStatus((c) => ({ ...c, tos_accepted: true }))}
            />
          )}

          {/* Iter XX.199 — "Gully remembers me" second-open card.
              Lifetime-once per identity (server-enforced). Only paints
              when the current session has no user messages yet, so
              typing dismisses it naturally. */}
          {secondOpen && messages.length === 0 && (
            <SecondOpenCard
              salutation={secondOpen.salutation}
              sentence={secondOpen.sentence}
              onContinue={() => {
                try {
                  trackFunnel("second_open_continue_clicked", {
                    is_authenticated: !!(secondOpen.identity
                                          && secondOpen.identity.startsWith("user:")),
                    previous_session_id: secondOpen.continueSessionId || null,
                  });
                } catch { /* noop */ }
                const prev = secondOpen.continueSessionId;
                setSecondOpen(false);
                if (prev && prev !== sessionId) {
                  try { saveSession(prev); } catch { /* noop */ }
                  setSessionId(prev);
                  setMessages([]);
                  // hydrate() reads /web/history for `prev` and paints.
                  try { hydrate(prev); } catch { /* noop */ }
                }
              }}
              onNewTopic={() => {
                try {
                  trackFunnel("second_open_new_topic_clicked", {
                    is_authenticated: !!(secondOpen.identity
                                          && secondOpen.identity.startsWith("user:")),
                  });
                } catch { /* noop */ }
                setSecondOpen(false);
              }}
            />
          )}

          {/* Iter XX.242 — Cricket Graph V1. For returning users only,
              painted at the top of an empty chat so they see
              "Where am I / What Changed / What's Next" BEFORE typing.
              First-time users never see this card; they experience the
              existing simple chat entry. The card fetches state itself
              and renders nothing on the first-time / no-content path.
              Both CTAs ("Continue my cricket" / "Talk to Gully")
              simply dismiss the card and focus the composer — chat is
              never blocked. */}
          {messages.length === 0 && (
            <ReturningUserCricketGraph />
          )}

          {/* Iter XX.200 — Meta measurement consent bar (Android-only,
              once per install). Fires the Facebook Android SDK only
              after Allow. Never blocks the composer. */}
          {metaConsentVisible && (
            <MetaConsentBar
              onAllow={async () => {
                setMetaConsentVisible(false);
                try {
                  trackFunnel("meta_consent_granted", {});
                } catch { /* noop */ }
                try { await grantMetaConsent(); } catch { /* noop */ }
              }}
              onDecline={async () => {
                setMetaConsentVisible(false);
                try {
                  trackFunnel("meta_consent_denied", {});
                } catch { /* noop */ }
                try { await denyMetaConsent(); } catch { /* noop */ }
              }}
            />
          )}

          {/* Install banner */}
          <InstallBanner sessionId={sessionId} />

          {/* Iter 42.4 — In-app browser warning (Instagram / Facebook WebView)
              shows once per session above all other content. */}
          <InAppBrowserBanner sessionId={sessionId} />

          {/* Iter XX.31 — 30-day Gully Plus free trial banner.
              Rendered at natural moments: user has been chatting
              (≥ 3 assistant turns in this session) AND no urgent
              flow is active. The banner itself calls
              /plus/trial/eligibility and hides for non-eligible users
              (already Plus / already redeemed) — so a user who's
              already trialing or paid will simply never see it. */}
          {(() => {
            const assistantTurns = messages.filter(m => m.role === "assistant").length;
            const hasUrgentFlow = !!olderPrompt;
            if (assistantTurns < 3 || hasUrgentFlow) return null;
            return (
              <TrialBanner
                trialSource="in_chat_banner"
                onActivated={() => {
                  try { window.dispatchEvent(new CustomEvent("gully:plus-changed")); }
                  catch { /* noop */ }
                }}
              />
            );
          })()}

          {/* Return panel — only for linked users with prior activity */}
          {returnPanel && (
            <div className="rounded-2xl border border-emerald-900/40 bg-emerald-950/20 p-4" data-testid="chat-return-panel">
              <div className="text-xs text-emerald-400 mb-1.5">
                Welcome back{returnPanel.name ? `, ${returnPanel.name}` : ""}.
              </div>
              {returnPanel.recent_theme && (
                <div className="text-sm text-neutral-200 mb-1">
                  <span className="text-neutral-500">Last time:</span> {returnPanel.recent_theme}
                </div>
              )}
              {returnPanel.current_pattern && (
                <div className="text-sm text-neutral-200 mb-1">
                  <span className="text-neutral-500">Current pattern:</span> {returnPanel.current_pattern}
                </div>
              )}
              {Number(returnPanel.videos_this_month) > 0 && (
                <div className="text-sm text-neutral-300 mb-1">
                  You&apos;ve uploaded {returnPanel.videos_this_month} video{returnPanel.videos_this_month === 1 ? "" : "s"} this month.
                </div>
              )}
              <button
                onClick={() => { setInput(returnPanel.suggested_starter); setReturnPanel(null); textRef.current?.focus(); }}
                className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline"
                data-testid="chat-return-starter-btn"
              >
                {returnPanel.suggested_starter}
              </button>
            </div>
          )}

          {(() => {
            // Iter XX.92 — Scripted intro messages render BEFORE the
            // real `messages` array so the audit log stays clean and
            // reloads never replay the intro. Each shown line is a
            // static assistant bubble; the typing indicator between
            // lines is a compact three-dot animation.
            const introNodes = [];
            for (let i = 0; i < introVisibleCount; i++) {
              introNodes.push(
                <div key={`intro-${i}`}
                  className="max-w-2xl mx-auto w-full px-3 py-2"
                  data-testid={`intro-msg-${i}`}>
                  <div className="rounded-2xl bg-neutral-900/60 border border-neutral-800/50 px-4 py-3 text-neutral-100 text-[15px] leading-relaxed whitespace-pre-line">
                    {INTRO_SCRIPT[i]}
                  </div>
                </div>
              );
            }
            if (introTyping && introRunning) {
              introNodes.push(
                <div key="intro-typing"
                  className="max-w-2xl mx-auto w-full px-3 py-1.5"
                  data-testid="intro-typing-indicator">
                  <div className="inline-flex items-center gap-1 rounded-2xl bg-neutral-900/60 border border-neutral-800/50 px-4 py-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-pulse [animation-delay:-0.2s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-pulse [animation-delay:0.2s]" />
                  </div>
                </div>
              );
            }
            return introNodes;
          })()}

          {/* Iter XX.160.1 — Video-intent entry hero. Fires only when
              the visitor arrived via the landing chip
              `/chat?open=video`. The pickVideo() auto-open attempt at
              mount is frequently blocked by browser gesture policy
              (the tap on the landing chip is not carried across the
              navigation), so we ALWAYS render this prominent CTA as
              the guaranteed fallback. Clicking the button here IS a
              real user gesture, so pickVideo() opens the OS picker
              reliably on Chrome, Safari and Firefox.

              Renders only for a zero-turn chat and hides the moment
              the user either sends a message or starts an upload —
              it is a first-frame nudge, not a persistent surface.

              Reuses the existing `pickVideo` + `videoRef` upload
              pipeline — no new uploader, no new backend call. */}
          {_videoEntryActive && userMessageCount === 0 && !uploading && (
            <div
              data-testid="video-intent-hero-card"
              className="max-w-2xl mx-auto w-full px-3 mb-3"
            >
              <div className="rounded-2xl border border-emerald-800/60 bg-emerald-950/25 px-4 py-4">
                <div className="flex items-start gap-3">
                  <div
                    className="flex-none h-10 w-10 rounded-xl bg-emerald-900/50 flex items-center justify-center text-lg"
                    aria-hidden="true"
                  >
                    📹
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-semibold text-emerald-100 leading-snug">
                      Review your cricket video
                    </div>
                    <div className="mt-1 text-[13px] leading-snug text-neutral-400">
                      Pick a short clip from your device — a 5-10 second
                      angle where you are clearly in frame works best.
                      Gully will watch it and reply here.
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        data-testid="video-intent-hero-upload-btn"
                        onClick={() => {
                          try { pickVideo(); } catch { /* noop */ }
                          // Dismiss the card as soon as the picker
                          // is invoked. If the user cancels the OS
                          // dialog, the composer upload icon
                          // (revealed by _videoEntryActive above)
                          // remains available as their second try.
                          _setVideoEntryActive(false);
                        }}
                        disabled={uploading || busy}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 disabled:opacity-50 px-4 py-2 text-[13.5px] font-medium text-white transition-colors"
                      >
                        <span aria-hidden="true">⬆</span>
                        <span>Upload your video</span>
                      </button>
                      <button
                        type="button"
                        data-testid="video-intent-hero-dismiss-btn"
                        onClick={() => _setVideoEntryActive(false)}
                        className="text-[12.5px] text-neutral-500 hover:text-neutral-300 underline-offset-2 hover:underline"
                      >
                        Not now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(() => {
            // Iter 69 — Session divider injection. Tracked outside the map so
            // we can compare each user message's session_index against the
            // previous rendered user message. Assistant rows are transparent
            // (they carry no session_index server-side).
            let lastSessionIndex = null;
            return messages.map((m, idx) => {
              const hasBoundary = typeof m.session_index === "number";
              const isNewStart = m.is_new_session_start === true;
              const changed =
                hasBoundary && lastSessionIndex !== null && m.session_index !== lastSessionIndex;
              // Never show a divider before the very first message even
              // if is_new_session_start === true — it would look like a
              // stray label above an empty history.
              const showDivider = (isNewStart || changed) && lastSessionIndex !== null;
              if (hasBoundary) lastSessionIndex = m.session_index;
              // Iter XX.4 — is this bubble the LAST report-kind message
              // in the current thread? If yes AND user is free AND cadence
              // allows, we render the PostReportUpgradeCard right below.
              const isReportKind =
                m.kind === "todays_report" || m.kind === "video_review";
              let isLatestReport = false;
              if (isReportKind) {
                isLatestReport = true;
                for (let j = idx + 1; j < messages.length; j++) {
                  const kk = messages[j].kind;
                  if (kk === "todays_report" || kk === "video_review") {
                    isLatestReport = false;
                    break;
                  }
                }
              }
              const showPostReportCard =
                isLatestReport
                && !!plusSurfaces
                && plusSurfaces.is_plus === false
                && plusSurfaces.show_post_report_card === true;
                // Iter XX.214 — Native suppression removed. The card
                // CTA routes through /plus which auto-starts
                // beginPlusPurchase() → RC → StoreKit / Play on native.
              return (
                <React.Fragment key={idx}>
                  {showDivider && (
                    <SessionDivider dayBucket={m.day_bucket} />
                  )}
                  <Bubble
                    role={m.role}
                    content={
                      // Iter XX.71.1 — Video gate bypass fix.
                      // When the server says the structured card will
                      // render (render_mode=structured), we MUST hide
                      // the full markdown review from the bubble —
                      // otherwise free users read the whole report in
                      // the bubble and the locked-card gate is moot.
                      // Chat sets `_structured_hidden=true` on the
                      // message once VideoReviewCard confirms it will
                      // render structured. When flag OFF (or user is
                      // truly free/legacy), render_mode is
                      // "markdown_legacy" and the full content shows.
                      (m.kind === "video_review" && m._structured_hidden)
                        ? "🏏 Gully Video Review"
                        : m.content
                    }
                    attachments={m.attachments}
                    kind={m.kind}
                    identity_nudge={m.identity_nudge}
                    billing_cta={m.billing_cta}
                    coach_recommendation={m.coach_recommendation}
                    cricket_memory_confirmation={m.cricket_memory_confirmation}
                    session_memory={m.session_memory}
                    video_invitation={m.video_invitation}
                    onAcceptVideoInvitation={m.video_invitation ? () => {
                      // Iter XX.246 — Accept the contextual video
                      // invitation. Persists pending_analysis_focus
                      // via the accept endpoint so the imminent
                      // upload uses THIS focus for its Gemini call.
                      try {
                        fetch(`${API}/api/web/video-invite/accept`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            session_id: sessionId,
                            discipline: m.video_invitation.discipline,
                            focus_category: m.video_invitation.focus_category,
                          }),
                        }).catch(() => {});
                      } catch { /* fire-and-forget */ }
                      // Open the existing upload picker — unchanged
                      // upload plumbing (XX.244/XX.245 pipeline safe).
                      try { pickVideo(); } catch {}
                    } : null}
                    onDeclineVideoInvitation={m.video_invitation ? () => {
                      try {
                        fetch(`${API}/api/web/video-invite/decline`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            session_id: sessionId,
                            focus_category: m.video_invitation.focus_category,
                          }),
                        }).catch(() => {});
                      } catch { /* fire-and-forget */ }
                      // Mark this bubble's invitation as dismissed
                      // locally so the chip disappears.
                      setMessages((prev) => prev.map((mm) =>
                        mm === m ? { ...mm, video_invitation: null } : mm
                      ));
                    } : null}
                    format_clarification={m.format_clarification}
                    onSelectFormat={m.format_clarification ? (formatKey) => {
                      // Iter XX.253 — Persist active_format on the
                      // session (and add to formats_played identity).
                      try {
                        fetch(`${API}/api/web/format/select`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            session_id: sessionId,
                            format:     formatKey,
                            source:     "conversation",
                          }),
                        }).catch(() => {});
                      } catch { /* fire-and-forget */ }
                      // Dismiss the chip locally after tap + auto-send
                      // a lightweight confirmation so Gully continues
                      // the diagnosis with the format now anchored.
                      setMessages((prev) => prev.map((mm) =>
                        mm === m ? { ...mm, format_clarification: null } : mm
                      ));
                      const label = (m.format_clarification.options
                        .find((o) => o.key === formatKey) || {}).label
                        || formatKey;
                      try { send(label); } catch {}
                    } : null}
                    onDismissFormat={m.format_clarification ? () => {
                      try {
                        fetch(`${API}/api/web/format/dismiss`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ session_id: sessionId }),
                        }).catch(() => {});
                      } catch { /* fire-and-forget */ }
                      setMessages((prev) => prev.map((mm) =>
                        mm === m ? { ...mm, format_clarification: null } : mm
                      ));
                    } : null}
                    timeline_added={m.timeline_added}
                    journey_added={m.journey_added}
                    clarification_kind={m.clarification_kind}
                    clarification_chips={m.clarification_chips}
                    clarification_resolved={m.clarification_resolved}
                    onTryAgain={(m.kind === "video_review_failed" && m.can_retry !== false) ? () => {
                      // Iter 53 — canonical retry analytics. Fire BEFORE
                      // opening the picker so we count intent even if the
                      // user cancels the file dialog.
                      try {
                        postClientEvent(API, sessionId, "video_retry_clicked", {
                          trace_ref: m.trace_ref || null,
                          rejection_reason: m.rejection_reason || null,
                          category: m.category || null,
                        });
                      } catch { /* swallow — telemetry must never break retry */ }
                      pickVideo();
                    } : undefined}
                    // Iter XX.124 — Continue Without Video. The user's
                    // goal is coaching, not uploading. Fire a "keep
                    // coaching from what you noticed" prompt so Gully
                    // resumes immediately without the video.
                    onContinueWithoutVideo={m.kind === "video_review_failed" ? () => {
                      try {
                        postClientEvent(API, sessionId, "video_continue_without_clicked", {
                          trace_ref: m.trace_ref || null,
                          rejection_reason: m.rejection_reason || null,
                        });
                      } catch { /* swallow */ }
                      // Seed the composer with a natural "let's keep
                      // going" line and send it. Preserves the coaching
                      // relationship rather than leaving the failure
                      // as the last message on screen.
                      try {
                        const line = "Let’s keep coaching without the video — tell me what to focus on based on what I described.";
                        setInput(line);
                        setTimeout(() => send(line), 40);
                      } catch { /* swallow */ }
                    } : undefined}
                    onClarifyChip={async (chipId, freeform) => {
                      // Disable the chip card by marking the message as resolved.
                      // Even if the network call fails, the user shouldn't be able
                      // to double-submit.
                      setMessages((arr) => arr.map((mm, i) =>
                        i === idx ? { ...mm, clarification_resolved: true } : mm
                      ));
                      // 2026-06-25 — Iter 25.9i P0. The awaiting_identity
                      // chip row routes through the normal `send()` path
                      // (NOT /web/video-clarify). The label maps 1:1 to the
                      // word the existing `extract_role_from_answer` knows
                      // how to resolve: "Bowler", "Batter", "Keeper",
                      // "Fielder". Per founder spec: "selecting a chip
                      // should immediately send the corresponding answer
                      // into the existing flow exactly as if the user typed
                      // it manually." No new endpoint, no schema change.
                      if (m.clarification_kind === "awaiting_identity") {
                        const answer = {
                          bowler: "Bowler",
                          batter: "Batter",
                          keeper: "Keeper",
                          fielder: "Fielder",
                        }[chipId] || chipId;
                        postClientEvent(API, sessionId,
                          "awaiting_identity_chip_pick",
                          { chip_id: chipId, answer });
                        send(answer, { source: "awaiting_identity_chip" });
                        return;
                      }
                      try {
                        const { data } = await api.post("/web/video-clarify", {
                          session_id: sessionId,
                          kind: m.clarification_kind,
                          choice: chipId,
                          freeform: (freeform || "").trim() || undefined,
                        });
                        // Append the user's pick + Gully's reply as normal bubbles.
                        // Iter XX.193 — if the backend returned a
                        // `retryable_review_action`, attach it to the
                        // assistant bubble so a Retry button can be
                        // rendered inline (XX.191 truthful-error copy
                        // now finally has a real action next to it).
                        setMessages((arr) => [
                          ...arr,
                          { role: "user",
                            content: _humanReadableChipPick(m.clarification_kind, chipId, freeform) },
                          { role: "assistant",
                            content: data?.reply || "Got it.",
                            retryable_review_action: data?.retryable_review_action || null },
                        ]);
                      } catch (e) {
                        const msg = e?.response?.data?.detail
                          || "Couldn't run that recovery. Please upload the clip again.";
                        setMessages((arr) => [
                          ...arr,
                          { role: "assistant", content: msg },
                        ]);
                      }
                    }}
                    onNudgeClick={(trigger) => {
                      try { localStorage.setItem("gully_last_identity_trigger", trigger); } catch { /* noop */ }
                      api.post("/web/track", {
                        session_id: sessionId, event: "remember_me_click",
                        payload: { source: "nudge", trigger },
                      }).catch(() => {});
                      api.post("/web/track", {
                        session_id: sessionId, event: "history_link_attempted",
                        payload: { source: "nudge", trigger },
                      }).catch(() => {});
                      setNeedsIdentity(true);
                    }}
                  />
                  {/* Iter XX.193 — Retry action for XX.191 transient
                      video-review failures. Renders under the specific
                      assistant bubble that told the user "your video
                      is safe, tap Retry" — turns that promise into a
                      real button that re-runs analysis against the
                      already-uploaded video (no re-upload, no
                      re-answering the bowler/batter chip). Fires the
                      same /web/video-clarify endpoint with the same
                      kind + choice the backend stapled onto the
                      response payload. */}
                  {m.role === "assistant"
                    && m.retryable_review_action
                    && !m.retry_in_flight && (
                    <button
                      type="button"
                      data-testid="video-review-retry-btn"
                      onClick={async () => {
                        const act = m.retryable_review_action;
                        setMessages((arr) => arr.map((mm, i) =>
                          i === idx ? { ...mm, retry_in_flight: true } : mm
                        ));
                        try {
                          // Iter XX.194 — Route retry by the kind the
                          // backend stapled onto the payload.
                          //   · `awaiting_identity` came in through
                          //     /web/chat (a normal chat turn with the
                          //     role word). Retry = re-send the same
                          //     role word through send().
                          //   · Any other kind (multi_player /
                          //     non_cricket / …) came in through
                          //     /web/video-clarify. Retry = same
                          //     endpoint, same kind + choice.
                          if (act && act.kind === "awaiting_identity") {
                            setMessages((arr) => arr.map((mm, i) =>
                              i === idx
                                ? { ...mm,
                                    retryable_review_action: null,
                                    retry_in_flight: false }
                                : mm
                            ));
                            await send(act.choice, { hidden: false });
                            return;
                          }
                          const { data } = await api.post(
                            "/web/video-clarify",
                            {
                              session_id: sessionId,
                              kind: act.kind,
                              choice: act.choice,
                            },
                          );
                          // Clear the retry marker on this bubble and
                          // append the new reply. If the retry ALSO
                          // failed transiently the new bubble will
                          // carry its own retryable_review_action.
                          setMessages((arr) => {
                            const next = arr.map((mm, i) =>
                              i === idx
                                ? { ...mm,
                                    retryable_review_action: null,
                                    retry_in_flight: false }
                                : mm
                            );
                            next.push({
                              role: "assistant",
                              content: data?.reply || "Got it.",
                              retryable_review_action:
                                data?.retryable_review_action || null,
                            });
                            return next;
                          });
                        } catch (e) {
                          const detail = e?.response?.data?.detail
                            || "Retry didn't go through. Give it another moment.";
                          setMessages((arr) => arr.map((mm, i) =>
                            i === idx
                              ? { ...mm, retry_in_flight: false }
                              : mm
                          ).concat([{
                            role: "assistant", content: detail,
                          }]));
                        }
                      }}
                      className="mt-2 text-xs px-3 py-1.5 rounded-full bg-emerald-800 hover:bg-emerald-700 text-emerald-50 font-medium"
                    >
                      Retry review
                    </button>
                  )}
                  {m.role === "assistant"
                    && m.retry_in_flight && (
                    <div
                      data-testid="video-review-retry-inflight"
                      className="mt-2 text-xs text-neutral-500"
                    >
                      Retrying…
                    </div>
                  )}
                  {/* Iter XX.192 — In-app AI response reporting.
                      Small unobtrusive "Report" link under every
                      assistant message. Required by Google Play's
                      generative-AI policy. Skipped for special-render
                      messages (video-review card owns its own UI,
                      billing CTAs, journey chips) so the affordance
                      doesn't collide with functional buttons. */}
                  {m.role === "assistant"
                    && !!m.content
                    && m.kind !== "video_review"
                    && m.kind !== "video_review_failed"
                    && m.kind !== "video_reveal_synthesis"
                    && !m.billing_cta
                    && !m.identity_nudge && (
                    <ReportAIResponseTrigger
                      sessionId={sessionId}
                      messageRef={m.id || m.message_id || `t${idx}`}
                      reportedText={m.content}
                      precedingUserText={
                        idx > 0 && messages[idx - 1]?.role === "user"
                          ? messages[idx - 1].content
                          : null
                      }
                    />
                  )}
                  {showPostReportCard && (
                    <PostReportUpgradeCard
                      sessionId={sessionId}
                      onSeen={() => {
                        // Mark cadence lock so it doesn't show again for
                        // 24h. Uses the ref set inside the card to fire
                        // exactly once per render.
                        if (markPlusPostReportShown) {
                          markPlusPostReportShown();
                        }
                        setPostReportSeenIndex(idx);
                      }}
                    />
                  )}
                  {/* Iter XX.70 — Structured Video Review card. Mounts
                      alongside the markdown bubble whenever the message
                      carries a `video_analysis_id`. The card fetches
                      /api/video-review/{id} which decides render_mode:
                        • structured  → renders locked/unlocked cards
                        • markdown_legacy → renders nothing; the Bubble
                          above already shows the legacy text.
                      Founder preview is triggered from a URL param
                      that only admins can activate on the backend. */}
                  {m.role === "assistant"
                    && m.kind === "video_review"
                    && m.video_analysis_id && (
                    <VideoReviewCard
                      key={`vrc-${m.video_analysis_id}`}
                      videoAnalysisId={m.video_analysis_id}
                      isFounderPreviewParam={typeof window !== "undefined"
                        && (new URLSearchParams(window.location.search)).get("founder_preview") === "1"}
                      autoReveal={m.auto_reveal_on_mount === true}
                      onCardReady={(rev) => {
                        // Iter XX.71.1 — When the server decides the
                        // card will render structurally, hide the
                        // full markdown in the parent Bubble so the
                        // paywall isn't bypassed. When rev.render_mode
                        // === "markdown_legacy" we leave the message
                        // untouched — the bubble carries the full
                        // review exactly as before XX.70.
                        const structural = rev && rev.render_mode === "structured";
                        setMessages((arr) => arr.map((mm, i) => (
                          i === idx
                            ? { ...mm, _structured_hidden: !!structural }
                            : mm
                        )));
                      }}
                    />
                  )}
                  {/* Iter XX.70.1 — Anchor for smooth-scroll-into-view
                      on the post-payment synthesis message. Placed as a
                      sibling of the Bubble so scrollIntoView lands on
                      the bottom edge of the message, not above it. */}
                  {m.role === "assistant"
                    && m.kind === "video_reveal_synthesis"
                    && m.video_analysis_id && (
                    <div
                      data-testid={`video-reveal-synthesis-anchor-${m.video_analysis_id}`}
                      aria-hidden
                      className="h-0"
                    />
                  )}
                  {/* Iter XX.58 — Journey FSM inline cards. Chips
                      appear only in WAITING_FOR_DREAM; Next Level and
                      Mission confirmation cards appear in their
                      respective states. */}
                  {m.dream_chips && journeyState === "WAITING_FOR_DREAM" && (
                    <div className="mt-3 flex flex-wrap gap-2"
                         data-testid="chat-dream-chips">
                      {DREAM_CHIPS.map(chip => (
                        <button
                          key={chip.id}
                          type="button"
                          onClick={() => handleDreamChipPick(chip)}
                          data-testid={`chat-dream-chip-${chip.id}`}
                          className="text-sm rounded-full border border-neutral-800 bg-neutral-950 text-neutral-100 hover:border-emerald-800/70 hover:bg-neutral-900 hover:-translate-y-0.5 transition-all px-3 py-1.5"
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {m.journey_next_level_card
                    && journeyState === "WAITING_FOR_NEXT_LEVEL_CONFIRMATION"
                    && !journeyEditMode && (
                    <div className="mt-3 flex flex-wrap gap-2"
                         data-testid="chat-next-level-actions">
                      <button
                        type="button"
                        onClick={handleNextLevelAccept}
                        data-testid="chat-next-level-accept"
                        className="text-sm rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-1.5 font-medium transition-colors"
                      >
                        That&apos;s right
                      </button>
                      <button
                        type="button"
                        onClick={handleNextLevelEdit}
                        data-testid="chat-next-level-edit"
                        className="text-sm rounded-full border border-neutral-800 bg-neutral-950 text-neutral-100 hover:border-neutral-700 hover:bg-neutral-900 px-4 py-1.5 transition-colors"
                      >
                        Change it
                      </button>
                    </div>
                  )}
                  {m.journey_mission_card
                    && journeyState === "WAITING_FOR_MISSION_CONFIRMATION"
                    && !journeyEditMode && (
                    <div className="mt-3 flex flex-wrap gap-2"
                         data-testid="chat-mission-actions">
                      <button
                        type="button"
                        onClick={handleMissionAccept}
                        data-testid="chat-mission-accept"
                        className="text-sm rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-1.5 font-medium transition-colors"
                      >
                        Sounds good
                      </button>
                      <button
                        type="button"
                        onClick={handleMissionEdit}
                        data-testid="chat-mission-edit"
                        className="text-sm rounded-full border border-neutral-800 bg-neutral-950 text-neutral-100 hover:border-neutral-700 hover:bg-neutral-900 px-4 py-1.5 transition-colors"
                      >
                        Change it
                      </button>
                      <button
                        type="button"
                        onClick={handleMissionSkip}
                        data-testid="chat-mission-skip"
                        className="text-sm rounded-full border border-neutral-800 bg-neutral-950 text-neutral-500 hover:text-neutral-300 hover:border-neutral-700 px-4 py-1.5 transition-colors"
                      >
                        Skip for now
                      </button>
                    </div>
                  )}
                </React.Fragment>
              );
            });
          })()}

          {busy && <TypingDots />}
          {uploading && <UploadingRow pct={uploadPct} phase={uploadPhase} />}

          {/* Iter XX.78 — Scorecard flow. Rendered directly after the
              R3-seeded first assistant reply so it feels like Gully
              is saying "show me the scorecard" mid-conversation, not
              like a form. Component self-manages offer / upload /
              confirm / follow-up phases and dismisses itself on skip.
              Trigger: URL carries `?from=r3` (survives the /web/history
              hydrate that clears `_r3_seeded` markers on messages). */}
          {(() => {
            if (!sessionId) return null;
            const fromR3 = (() => {
              try {
                return (new URLSearchParams(window.location.search))
                  .get("from") === "r3";
              } catch { return false; }
            })();
            if (!fromR3) return null;
            return (
              <ScorecardFlow
                sessionId={sessionId}
                onFollowup={(data) => {
                  // Fire a soft analytics ping. The follow-up copy
                  // renders inline; we do NOT inject it as a bubble to
                  // avoid confusing "assistant" attribution — Gully
                  // uses the pattern verdict on the NEXT actual reply.
                  try {
                    api.post("/web/track", {
                      session_id: sessionId,
                      event: "scorecard_confirmed",
                      payload: {
                        scorecard_id: data?.scorecard_id,
                        action: data?.pattern?.action,
                        n_confirmed: data?.pattern?.n_confirmed,
                      },
                    }).catch(() => {});
                  } catch { /* noop */ }
                  // Iter XX.81 — Confirming a scorecard is a strong
                  // engagement signal + it introduces a concrete
                  // "match on record" that makes Season and Memory
                  // meaningful right now. Advance both stages.
                  try { onScorecardConfirmed(); } catch { /* noop */ }
                }}
              />
            );
          })()}

          {/* Iter 44.1 — Continuity Capture (non-blocking). Renders only
              for anonymous users at a high-value moment (server-checked).
              No pricing language, no paywall language; ONLY a Google
              sign-in invite to preserve memory across devices. */}
          {!linked && continuityEligible && !continuityDismissedInSession && (
            <ContinuityPrompt
              sessionId={sessionId}
              eligible={continuityEligible}
              onDismissed={() => {
                setContinuityDismissedInSession(true);
                setContinuityEligible(false);
              }}
            />
          )}

          {/* 2026-06-25 — Legacy "Try one of these:" chip row removed.
              The canonical starter chips live above the composer (block at
              L965+, controlled by `hasFirstMessage`). Per the Core
              Conversation Rule, we keep ONE chip surface to avoid the
              "the app wants me to pick from a menu" feel. */}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Iter XX.223 — Welcome-Back reveal. Renders inline only AFTER
          the player has sent at least one message this session
          (conversation-first rule). Feature-flagged: the fetch returns
          null on 404 so production stays silent. */}
      <div className="px-4 max-w-3xl mx-auto w-full">
        <WelcomeBackReveal messages={messages} />
      </div>

      {/* Iter XX.222 — Performance candidate poller. Renders the pending
          "Save this innings?" card AFTER the coaching reply has landed.
          Placed BELOW the messages stream + ABOVE the composer + BELOW
          the ConnectCricketOffer so it never overlaps a coaching bubble. */}
      <PerformanceCandidateSlot messages={messages} />

      {/* Iter XX.221 — Connect Your Cricket offer. Renders ONCE after the
          player has had ~3 back-and-forth turns AND has no external
          cricket profile linked AND has not dismissed the card this
          session. Feature-flagged: `<ConnectCricketOffer />` returns null
          when `/api/cricket/connection` responds 404 (flag off in prod). */}
      <ConnectCricketOffer messages={messages} />

      {/* Composer — premium minimal. No hard border; a soft top fade visually
          attaches it to the conversation. Video is a ghost attachment icon;
          text input is dominant; send is the only solid CTA. */}
      <div
        ref={composerRef}
        className="relative flex-none bg-[#0a0a0a] sm:mb-0 mb-mobile-nav"
        style={{
          // Iter XX.266 → XX.286 — Composer bottom padding + margin.
          // Desktop / iPad keeps the original 0.45rem inset. On narrow
          // mobile widths the composer sits ABOVE the fixed
          // `<MobileBottomNav>` via the shared clearance contract
          // (`.mb-mobile-nav` binds to `--gully-bottom-nav-h`, which
          // the nav publishes at its measured height including the
          // iOS safe-area inset). Previously the offset was a
          // hard-coded 64px which under-reserved ~8px on modern iOS
          // devices where the nav's true rendered height is ~72px —
          // that gap put the send button 1–2px behind the nav's
          // top border. Binding to the measured variable eliminates
          // the drift for all future device heights.
          paddingBottom:
            "calc(0.45rem + env(safe-area-inset-bottom, 0px))",
        }}
        data-testid="chat-composer"
        data-arc-locked={arcLocked ? "true" : "false"}
      >
        {/* Iter XX.126 — Coaching-arc lock notice. Rendered above
            the composer when the backend has returned HTTP 402 with
            `paywall=coaching_arc_used`. The composer inputs below
            are also `disabled` in this state — this notice explains
            WHY so it doesn't look like a technical error. The Plus
            CTA lives inside the last assistant bubble (rendered as
            a `billing_cta` attachment). */}
        {arcLocked && (
          <div
            data-testid="chat-arc-locked-notice"
            className="mx-4 mb-2 rounded-xl border border-emerald-900/40 bg-emerald-950/[0.18] px-3 py-2 text-[12.5px] leading-snug text-emerald-200/90"
          >
            {isNative() ? (
              // Iter XX.214 — On Capacitor native, the /plus surface
              // auto-starts beginPlusPurchase() → RC → StoreKit / Play
              // Billing (no external checkout, no browser). Rendering
              // an in-line link mirrors the web copy so a free user
              // never gets stranded at the arc lock with no purchase
              // route. Supersedes the XX.193 stranding.
              <>You&apos;ve started building a picture of your cricket. Continue with
              {/* Iter XX.295 — Native IAP integrity gate (inline): a build
                  that cannot configure RevenueCat shows truthful
                  update-app text instead of a dead purchase link. */}
              <NativePurchaseCta inline testId="chat-arc-locked-plus-unavailable">
              <button
                type="button"
                onClick={() => {
                  try {
                    beginPlusPurchase({
                      entryPoint: "chat_arc_locked_native",
                    });
                  } catch { /* fire-and-forget */ }
                }}
                data-testid="chat-arc-locked-plus-link"
                className="ml-1 underline decoration-emerald-500 underline-offset-2 hover:text-emerald-100 bg-transparent border-0 p-0 cursor-pointer font-inherit"
              >
                Gully Plus
              </button>
              </NativePurchaseCta>
              {" "}so Gully keeps learning it — across every match, practice and conversation.</>
            ) : (
              <>You&apos;ve started building a picture of your cricket. Continue with
              <a
                href="/plus"
                data-testid="chat-arc-locked-plus-link"
                className="ml-1 underline decoration-emerald-500 underline-offset-2 hover:text-emerald-100"
              >
                Gully Plus
              </a>
              {" "}so Gully keeps learning it — across every match, practice and conversation.</>
            )}
          </div>
        )}
        {/* Top fade — connects conversation to composer */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 -top-6 h-6"
          style={{
            background: "linear-gradient(to bottom, rgba(10,10,10,0) 0%, rgba(10,10,10,1) 100%)",
          }}
        />
        {/* Iter 62 — First-time onboarding card. Replaces the legacy
            starter-chip row for brand-new users (server-gated by
            /onboarding/status). Returning users keep seeing the
            existing starter chips below. Disappears with the chips
            the moment the player sends a real message. */}
        {!hasFirstMessage && onboarding?.eligible
          && Array.isArray(onboarding.options)
          && onboarding.options.length > 0 && (
          <OnboardingCard
            options={onboarding.options}
            disabled={busy || uploading || !sessionId || arcLocked}
            onSelect={async (opt) => {
              // 1. Fire-and-forget analytics so the founder can see
              //    `onboarding_option_selected` + `selected_option`.
              try {
                await fetch(`${API}/web/onboarding/event`, {
                  method: "POST",
                  credentials: credentialsMode(),
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    session_id: sessionId,
                    event: "option_selected",
                    option_id: opt.id,
                  }),
                });
              } catch { /* non-fatal */ }
              // 2. Route the option:
              //    - the two video options open the file picker
              //      directly (the player wants to upload, not chat).
              //    - everything else auto-sends the label as the
              //      first user message.
              if (opt.id === "video_batting" || opt.id === "video_bowling") {
                // We pre-seed the input with a tiny caption so the
                // upload carries context. Empty caption is fine too;
                // pickVideo() reads the input field for the caption.
                setInput(opt.label);
                pickVideo();
                return;
              }
              send(opt.label, { source: "onboarding_chip",
                                onboarding_option: opt.id });
            }}
          />
        )}
        {/* 2026-02-25 — Starter chips. Show only on a fresh session
            (no user messages sent yet AND no upload picked). Each chip
            sends a normal /web/chat message tagged source="starter_chip"
            so we can measure conversation starts. The 5th chip opens the
            video picker directly (no chat message). Disappear forever
            once the player sends anything.
            Iter 62 — these chips are now ONLY shown to RETURNING users
            (the onboarding card above takes over for first-timers).
            Iter XX.73 — When the improvement-loop flag is ON, replace
            the legacy 5-chip row with the 4 entry-point tiles so every
            coaching journey starts from the same set of doors. */}
        {/* Iter XX.265b — Authenticated Home Hero gating.
            Was: `!hasFirstMessage` (broken — system-seeded onboarding
            messages flipped that flag before the player ever typed).
            Now: `computeHomeSurface({messages, homePinned}).isAtHome`
            derived from the SINGLE canonical state model in
            /lib/homeSurface.js. See homeSurface.test.js for the 13
            regression cases including the exact iOS dream-question
            failure path. */}
        {(() => {
          const { isAtHome, hasMeaningfulUserContent } =
            computeHomeSurface({ messages, homePinned });
          if (!isAtHome) return null;
          if (onboarding?.eligible) return null;
          // Iter XX.267a — DO NOT gate the authenticated Home hero on
          // `improvementLoopOn`. XX.265b / XX.266 shipped the hero as
          // the canonical Home surface for ALL authenticated players,
          // not just the `new_improvement_loop_enabled` rollout cohort.
          // Previously this gate hid the hero on real devices whenever
          // the rollout flag was off, leaving Home stuck on the legacy
          // chip layout. The flag now only controls the starter chip
          // set inside AuthedHomeHero itself (via the entry-point
          // openers), not the hero's visibility.
          if (introRunning) return null;
          return (
            <AuthedHomeHero
              user={authedUser}
              disabled={busy || uploading || !sessionId || arcLocked}
              showBackToConversation={hasMeaningfulUserContent}
              onBackToConversation={() => setHomePinned(false)}
              // Iter XX.270 — "Build Your Gully" narrative gates.
              // `emptyState` is the exact inverse of the surface's
              // own `hasMeaningfulUserContent`; the hero uses it to
              // decide whether to render the "Start building your
              // Gully" eyebrow. `journeySignalsCount` is the AND-
              // gate for the returning-user narrative line. Both
              // props default to no-op when the flag is off.
              narrativeEnabled={gullyNarrativeOn}
              emptyState={!hasMeaningfulUserContent}
              journeySignalsCount={
                narrativeSignalsCount + (activeMission?.mission_id ? 1 : 0)
              }
              // Iter XX.272 — contextual challenge banner. `null`
              // when the user isn't invited, participating, or a
              // Plus subscriber during open registration.
              challengeBanner={challengeBanner}
              onPrimaryTap={() => {
                try {
                  postClientEvent(API, sessionId, "authed_home_primary_tapped", {});
                } catch { /* analytics best-effort */ }
                // Iter XX.267c — fix #4. Previously we only called
                // `textRef.current.focus()` which opened the keyboard
                // against a composer that was still hidden BEHIND the
                // AuthedHomeHero on Home, producing a "floating caret"
                // on the Home surface. The correct sequence is:
                //   1. unpin Home so <Chat /> composer becomes visible
                //   2. wait one animation frame for React + iOS layout
                //   3. focus the now-visible composer
                setHomePinned(false);
                try {
                  requestAnimationFrame(() => {
                    try { textRef.current?.focus(); } catch { /* focus best-effort */ }
                  });
                } catch {
                  try { textRef.current?.focus(); } catch { /* focus best-effort */ }
                }
              }}
              onStarterTap={(id, meta) => {
                postClientEvent(API, sessionId, "loop_entry_chip_clicked", {
                  entry_point: id,
                  chip_label: (meta && meta.label) || id,
                });
                send(ENTRY_POINT_OPENERS[id], {
                  source: "loop_entry_chip",
                  entry_point: id,
                });
              }}
              onVideoTap={() => {
                postClientEvent(API, sessionId, "loop_entry_chip_clicked", {
                  entry_point: "video",
                  chip_label: "Video Analysis",
                });
                pickVideo();
              }}
              // Iter XX.268 — pass the active mission down. Card
              // renders inside AuthedHomeHero above the primary
              // Gully card ONLY when non-null. Chat is unaffected.
              activeMission={activeMission}
              // Iter XX.285h — REMEMBERED moment. Null when no valid
              // moment exists; RememberedMoment renders nothing.
              rememberedMoment={rememberedMoment}
              sessionId={sessionId}
              onMissionTriedTap={(missionId, focusText) => {
                // Analytics — "user tapped I tried it on the mission
                // card". Kept from the pre-fill era.
                try {
                  api.post("/plus/track", {
                    kind: "current_mission_chat_return",
                    session_id: sessionId,
                    source: "current_mission_card",
                    payload: { mission_id: missionId, surface: "home" },
                  }).catch(() => {});
                } catch { /* noop */ }

                // Un-pin Home so the capture surface is on-screen
                // (same rule as XX.267c fix #4: never show an input
                // behind the pinned Home on iOS WKWebView).
                setHomePinned(false);

                // First Loop — open the one-tap report capture instead
                // of pre-filling the composer. The capture posts a
                // structured `report` through the normal send path.
                setShowReportCapture(true);
              }}
            />
          );
        })()}
        {/* Iter XX.285h — "Claim your cricket" banner for anonymous
            users with meaningful stored state. Shows only when:
            - User is anonymous (no authedUser)
            - /home/anonymous-state returned has_state=true
            - No active coaching conversation (don't interrupt)
            - Not dismissed this session
            Lightweight, dismissible, never paywalls. Sign-in preserves
            state via the existing link_session_to_user migration. */}
        {/* Iter XX.285i — Hide on native until OAuth deep-link flow is implemented.
            The native OAuth currently strands users in the system browser and
            cannot link anonymous sessions. Banner remains web-only until the
            native auth continuity path is proven end-to-end. */}
        {showClaimBanner && !authedUser?.user_id && !isNative() && (
          <ClaimYourCricketBanner
            onSignIn={() => {
              try {
                // Route to Google sign-in (existing auth surface).
                // The backend migration preserves anonymous state.
                window.location.href = "/login?claim_cricket=1";
              } catch { /* noop */ }
            }}
            onDismiss={() => setClaimBannerDismissed(true)}
          />
        )}
        {/* Iter XX.75 — Practice with Gully entry (founder-only). Shown
            independently of the improvement-loop flag so the founder
            can test the flow regardless of which starter-chip cluster
            is currently active. */}
        {!hasFirstMessage && !(onboarding?.eligible) && practiceOn && !introRunning && (
          <div
            className="max-w-2xl mx-auto px-3 pt-1 pb-0.5 flex flex-wrap gap-1.5"
            data-testid="practice-with-gully-row"
          >
            <button
              onClick={() => {
                postClientEvent(API, sessionId, "practice_starter_opened", {
                  surface: "practice_chip",
                });
                setPracticeOpen(true);
              }}
              disabled={busy || uploading || !sessionId || arcLocked}
              className="px-3 py-1.5 rounded-full bg-indigo-600/20 border border-indigo-700/60 text-sm text-indigo-200 hover:bg-indigo-600/30 hover:border-indigo-500 disabled:opacity-40 transition-colors"
              data-testid="practice-with-gully-chip"
            >
              <Video size={14} className="inline-block mr-1 -mt-0.5" />
              Practice with Gully · Rhythm
            </button>
          </div>
        )}
        {!hasFirstMessage && !(onboarding?.eligible) && !improvementLoopOn && !introRunning && !computeHomeSurface({ messages, homePinned }).isAtHome && (
          <div
            className="max-w-2xl mx-auto px-3 pt-1.5 pb-1 flex flex-wrap gap-1.5"
            data-testid="starter-chips"
          >
            {[
              { id: "match",      label: "I played a match" },
              { id: "practice",   label: "I had practice" },
              { id: "technique",  label: "Working on my technique" },
              { id: "confidence", label: "Confidence / mindset" },
              { id: "thinking",   label: "I'm just thinking about cricket" },
            ].map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  postClientEvent(API, sessionId, "starter_chip_clicked",
                    { chip_id: c.id, chip_label: c.label });
                  send(c.label, { source: "starter_chip" });
                }}
                disabled={busy || uploading || !sessionId || arcLocked}
                className="px-3 py-1.5 rounded-full bg-neutral-900 border border-neutral-800/80 text-sm text-neutral-200 hover:bg-neutral-800 hover:border-neutral-700 disabled:opacity-40 transition-colors"
                data-testid={`starter-chip-${c.id}`}
              >
                {c.label}
              </button>
            ))}
            <button
              onClick={() => {
                postClientEvent(API, sessionId, "starter_chip_clicked",
                  { chip_id: "upload_video", chip_label: "I want to upload a video" });
                pickVideo();
              }}
              disabled={busy || uploading || !sessionId || arcLocked}
              className="px-3 py-1.5 rounded-full bg-emerald-600/20 border border-emerald-700/60 text-sm text-emerald-200 hover:bg-emerald-600/30 hover:border-emerald-500 disabled:opacity-40 transition-colors"
              data-testid="starter-chip-upload_video"
            >
              <Video size={14} className="inline-block mr-1 -mt-0.5" />
              I want to upload a video
            </button>
          </div>
        )}
        {/* 2026-06-25 — Iter 25.9g. Today's Report chip.
            Iter XX.88 — Conversation First Mode. The chip stays out of
            sight through the first coaching exchange and only appears
            once the player has continued the conversation (≥ 2 user
            messages sent). Founder principle: after Gully's opening
            question, the ONE thing we want the player to do is keep
            talking — every secondary surface waits its turn. */}
        {hasFirstMessage && userMessageCount >= 2 && (
          <div className="max-w-2xl mx-auto px-3 pt-1 pb-0.5 flex">
            <button
              onClick={async () => {
                if (busy || uploading || !sessionId) return;
                postClientEvent(API, sessionId, "todays_report_chip_click", {});
                setBusy(true);
                try {
                  const { data } = await api.get(
                    `/web/report/today?session_id=${encodeURIComponent(sessionId)}`,
                  );
                  // Reuse the assistant bubble — the backend already
                  // renders the markdown body when this is asked for
                  // in chat. For the chip path we render client-side
                  // from the structured payload so we can show the
                  // confidence dot precisely.
                  let body;
                  if (!data?.available) {
                    body = data?.reason ||
                      "Not enough yet to give you a useful report.";
                  } else {
                    const r = data.report || {};
                    const conf = r.confidence || {};
                    const color = (conf.color || "yellow").toLowerCase();
                    const dot = color === "green" ? "🟢" : color === "red" ? "🔴" : "🟡";
                    body = [
                      "📋 *Today's Cricket Summary*",
                      "",
                      `*Biggest strength*\n${(r.biggest_strength || "").trim()}`,
                      "",
                      `*Biggest issue*\n${(r.biggest_issue || "").trim()}`,
                      "",
                      `*Focus next*\n${(r.focus_next || "").trim()}`,
                      "",
                      `*Confidence* — ${dot} ${color[0].toUpperCase() + color.slice(1)}\n${(conf.reason || "").trim()}`,
                      "",
                      `_${(r.coach_note || "").trim()}_`,
                    ].join("\n");
                  }
                  setMessages((arr) => [
                    ...arr,
                    { role: "assistant", content: body, kind: "todays_report" },
                  ]);
                } catch (e) {
                  const msg = e?.response?.data?.detail
                    || "Couldn't pull today's report just now — try again in a moment.";
                  setMessages((arr) => [
                    ...arr,
                    { role: "assistant", content: msg },
                  ]);
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy || uploading || !sessionId || arcLocked}
              className="px-3 py-1 rounded-full bg-neutral-900 border border-neutral-800/80 text-xs text-neutral-300 hover:bg-neutral-800 hover:border-neutral-700 disabled:opacity-40 transition-colors"
              data-testid="todays-report-chip"
            >
              📋 Today&apos;s report
            </button>
          </div>
        )}
        {/* Iter XX.83 / Iter XX.88 — Contextual video suggestion.
            Conversation First Mode: gated on the per-turn
            `videoSignaledInLastTurn` flag, which flips ON only when
            Gully's own reply invited a clip (see backend/
            context_reveals.detect_video_signal). For signed-in
            returning users the persistent `showVideoUpload` reveal
            is defaultVisible=true, so we deliberately don't gate on
            that here — otherwise the card would ambiently show on
            chat mount and compete with the first coaching exchange.
            Founder brief: contextual suggestion, not a large CTA. */}
        {videoSignaledInLastTurn && showVideoSuggestion && !uploading && (
          <div className="max-w-2xl mx-auto px-3 pb-2">
            <div
              data-testid="video-suggestion-card"
              className="flex items-center gap-2 rounded-xl border border-neutral-800/70 bg-neutral-900/40 px-3 py-2 text-[13px] text-neutral-300"
            >
              <button
                type="button"
                onClick={() => {
                  try {
                    if (typeof window !== "undefined" && window.clarity) {
                      window.clarity("event", "video_suggestion_clicked");
                    }
                  } catch { /* noop */ }
                  try { pickVideo(); } catch { /* noop */ }
                }}
                disabled={busy || uploading || !sessionId || arcLocked}
                data-testid="video-suggestion-cta"
                className="flex-1 min-w-0 text-left text-neutral-100 hover:text-white transition-colors disabled:opacity-50"
              >
                <span className="mr-1 select-none" aria-hidden="true">🎥</span>
                Want feedback on a bowling or batting video instead?
                <span className="ml-2 text-neutral-500 underline underline-offset-2">
                  Upload a clip
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowVideoSuggestion(false);
                  try {
                    sessionStorage.setItem("g_video_suggestion_dismissed", "1");
                  } catch { /* noop */ }
                  try {
                    if (typeof window !== "undefined" && window.clarity) {
                      window.clarity("event", "video_suggestion_dismissed");
                    }
                  } catch { /* noop */ }
                }}
                aria-label="Dismiss video suggestion"
                data-testid="video-suggestion-dismiss"
                className="shrink-0 h-7 w-7 rounded-full text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 transition-colors flex items-center justify-center"
              >
                ×
              </button>
            </div>
          </div>
        )}
        {showUploadTips && !uploading && (
          <VideoUploadTips
            onDismiss={() => {
              setShowUploadTips(false);
              try { sessionStorage.setItem("g_upload_tips_dismissed", "1"); } catch { /* noop */ }
            }}
          />
        )}
        {/* Iter XX.59 — Voice MVP. Sits immediately above the text
            composer. Renders null when the server flag is off or the
            browser can't record. Voice IS text — the transcript lands
            in this same composer and the player sends it via the
            existing send button. */}
        <div className="max-w-2xl mx-auto px-3">
          <VoiceComposer
            onTranscript={handleVoiceTranscript}
            onTypeInstead={handleVoiceTypeInstead}
            onMuteChange={handleVoiceMuteChange}
            ttsStatus={ttsStatus}
            onStopPlayback={stopTtsPlayback}
          />
          {realtimeEnabled && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => {
                  trackVoice("realtime_entry_tapped", {});
                  setShowLiveOverlay(true);
                }}
                data-testid="realtime-voice-entry"
                className="inline-flex items-center gap-2 rounded-full border border-sky-800/60 bg-neutral-950 hover:bg-neutral-900 hover:border-sky-700 text-sky-300 px-3 py-1.5 text-sm transition-colors"
              >
                <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                Talk live with Gully
              </button>
            </div>
          )}
        </div>
        <div className="max-w-2xl mx-auto px-3 pt-1.5 flex items-end gap-1.5">
          <input ref={videoRef} type="file" accept="video/*" className="hidden"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) onVideoPicked(f); e.target.value = ""; }}
                 data-testid="chat-video-input" />
          {/* Iter XX.81 — Video upload gated. Uploading video is a
              heavy commitment for a first-time visitor; we reveal
              it only once the conversation has moved past ~5 user
              turns (indicates real engagement, not curiosity poke).
              Signed-in users always see it. */}
          {showVideoUpload && (
            <button
              onClick={pickVideo}
              disabled={uploading || busy}
              className="flex-none p-2 rounded-full text-neutral-500 hover:text-emerald-400 hover:bg-neutral-900/60 disabled:opacity-40 transition-colors"
              title="Upload a cricket video"
              aria-label="Upload video"
              data-testid="chat-upload-video-btn"
            >
              <Video size={18} />
            </button>
          )}
          {showVideoUpload && !showUploadTips && !uploading && (
            <button
              type="button"
              onClick={() => setShowUploadTips(true)}
              className="flex-none px-2 py-1 rounded-full text-[11px] text-neutral-500 hover:text-emerald-400 hover:bg-neutral-900/60 transition-colors"
              data-testid="chat-upload-tips-link"
            >
              Video tips
            </button>
          )}
          <textarea
            ref={textRef}
            value={input}
            onChange={(e) => {
              // Iter XX.92 — user typing cancels the scripted intro.
              // Messages already shown remain visible; remaining ones
              // are dropped so we don't force the user to watch a
              // performance once they're engaged.
              if (introRunning && e.target.value.length > 0) {
                _cancelIntro("interrupted_typed");
                try {
                  postClientEvent(API, sessionId, "first_input_after_intro",
                    { source: "typed" });
                  try { clarityTrack("first_input_after_intro", { source: "typed" }); }
                  catch { /* noop */ }
                } catch { /* noop */ }
              }
              // Iter XX.280 — Journey-start instrumentation:
              // first_interaction. Fires once on the first keystroke
              // (type only — never content). Chip taps are covered in
              // send() below.
              if (!_journeyFirstInteractionFiredRef.current
                  && e.target.value.length > 0) {
                _journeyFirstInteractionFiredRef.current = true;
                try {
                  let _bid = null;
                  try { _bid = getBrowserId(); } catch { /* noop */ }
                  postClientEvent(API, sessionId, "first_interaction",
                    { source: "typed", ...(_bid ? { browser_id: _bid } : {}) });
                } catch { /* non-fatal */ }
              }
              setInput(e.target.value);
            }}
            onFocus={() => {
              setComposerFocused(true);
              _handleComposerFocusOnce();
              requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }));
              setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 300);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            onBlur={() => setComposerFocused(false)}
            placeholder={gateRequired
              ? "Save your cricket journey to keep chatting…"
              : (journey === null
                  ? "Loading your journey…"
                  // Iter XX.129 — after PCE handoff, use the server's
                  // purposeful placeholder ("Type in your own words…")
                  // so the very first free-form beat picks up the
                  // momentum. Fresh sessions without PCE fall back to
                  // Iter XX.90's rotating placeholders.
                  : (pceHandoffPlaceholder
                      || COMPOSER_PLACEHOLDERS[placeholderIndex]))}
            rows={1}
            // Post-paywall ghosting repair (2026-10-02) — disable the textarea
            // while the arc lock is active. Previously only the buttons were
            // disabled; the textarea stayed editable so users could type and
            // hit Enter into a void.
            disabled={gateRequired || journey === null || arcLocked}
            className="flex-1 resize-none bg-neutral-900 border border-neutral-800/80 rounded-xl px-3.5 py-2.5 text-[16px] leading-snug placeholder-neutral-500 focus:outline-none focus:border-neutral-700 max-h-28 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="chat-input"
          />
          <button
            onClick={() => send()}
            // Post-paywall ghosting repair (2026-10-02) — disable the send
            // button while the arc lock is active.
            disabled={!input.trim() || busy || gateRequired || journey === null || arcLocked}
            className="flex-none p-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Send"
            data-testid="chat-send-btn"
          >
            {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
          </button>
        </div>
      </div>

      {/* Modals */}
      {/* First Loop — one-tap report capture. Opened by the mission
          card's "I tried it" tap (manual taps always work;
          `shouldAutoSurface` guards only future proactive surfacing,
          never this path). */}
      {showReportCapture && activeMission ? (
        <ReportCapture
          mission={activeMission}
          sessionId={sessionId}
          onSubmit={handleReportSubmit}
          onClose={() => setShowReportCapture(false)}
        />
      ) : null}
      {/* Iter XX.60 — Realtime Voice full-screen overlay. Mounted only
          when the founder has tapped the "Talk live with Gully" chip.
          On close we call `hydrate()` so the finalized voice turns
          (now in web_messages) render in the thread. */}
      {showLiveOverlay && (
        <LiveVoiceOverlay
          onClosed={() => {
            setShowLiveOverlay(false);
            if (sessionId) hydrate(sessionId);
          }}
          onFallback={() => {
            setShowLiveOverlay(false);
            // Focus the text composer and surface the voice message
            // control as the graceful fallback.
            setTimeout(() => textRef.current?.focus(), 50);
          }}
        />
      )}
      {/* Iter XX.12 — Cricket Journey Gate. Un-dismissable. Shown ONLY
          when the meaningful-engagement score has crossed threshold AND
          the session is still anonymous. Composer is disabled behind it. */}
      {gateRequired && !linked && !needsIdentity && (
        <CricketJourneyGate
          onContinue={() => {
            try { localStorage.setItem("gully_last_identity_trigger", "engagement_gate"); } catch { /* noop */ }
            api.post("/web/track", {
              session_id: sessionId, event: "history_link_attempted",
              payload: { source: "engagement_gate" },
            }).catch(() => {});
            setGateForcing(true);
            setNeedsIdentity(true);
          }}
        />
      )}
      {/* Iter XX.12 — Cricket Journey Started celebration. Full-screen
          overlay for ~4.5s the moment identity is linked. */}
      <CricketJourneyStarted
        visible={showJourneyStarted}
        onFinished={() => {
          setShowJourneyStarted(false);
          // Iter XX.93 — Flush the deferred transition telemetry now
          // that the overlay has fully faded. Guarded / one-shot so
          // spurious re-renders never double-emit.
          try {
            const emit = _pendingTransitionTelemetryRef.current;
            if (emit) {
              _pendingTransitionTelemetryRef.current = null;
              emit();
            }
          } catch { /* noop */ }
        }}
      />
      {needsIdentity && (
        <IdentitySheet
          gated={gateForcing}
          onClose={() => {
            // A gated identity sheet can only be closed by success — the
            // player must complete linking to keep chatting. If gateForcing
            // is set, we ignore the close request and re-emphasise the
            // gate.
            if (gateForcing) {
              setNeedsIdentity(false);
              return;
            }
            setNeedsIdentity(false);
            setIdentityForPaywall(false);
            // Iter XX.72.4 — User closed the identity capture BEFORE
            // completing checkout. Deny-by-default: re-check entitlement
            // so the paywall is guaranteed to be visible (even if the
            // upgrade flow briefly cleared it) before chat is exposed.
            recheckEntitlement();
          }}
          sessionId={sessionId}
          onLinked={(ok) => {
            setLinked(true);
            setNeedsIdentity(false);
            setGateForcing(false);
            setGateRequired(false);
            // If this identity capture was kicked off by the paywall, the
            // /identity response just dropped a fresh JWT into localStorage —
            // continue to checkout immediately. Skip the "locked in" message
            // since the user's mid-flow.
            //
            // Iter XX.72.4 — DO NOT clear the paywall or let the user
            // into chat. Identity capture is NOT entitlement — only
            // active Plus or a valid Pass unlocks chat. We route to
            // Stripe checkout; if the user cancels checkout, the
            // pageshow/focus listener will re-check entitlement and
            // the paywall stays visible.
            if (identityForPaywall) {
              setIdentityForPaywall(false);
              // Iter XX.72.6 — canonical funnel event: identity has
              // been linked, token is persisted, and we're about to
              // hand the user off to /upgrade for auto-checkout.
              api.post("/web/track", {
                session_id: sessionId,
                event: "plus_identity_linked",
                payload: { entry: "paywall_modal" },
              }).catch(() => {});
              // Iter XX.72.5 — auto-resume checkout on landing; the
              // user doesn't have to click the CTA a second time.
              window.location.href = "/upgrade?from=paywall&autostart=1";
              return;
            }
            // Iter XX.12 — Sign-up should feel like the beginning of a
            // journey. Fire the celebration overlay; the assistant's
            // warm welcome message is appended IMMEDIATELY so the chat
            // is ready underneath the fading overlay (no waiting).
            //
            // Iter XX.93 — Transition tightened from 4.9s to ~700ms
            // (target: <800ms). The old code hardcoded matched
            // setTimeout(4900) calls in both the overlay and here,
            // artificially blocking the visitor from the chat they'd
            // already earned by signing up. Instrumentation below
            // records the three timing checkpoints the founder
            // requested (auth_complete → transition_shown, →
            // first_chat_paint, → fully_hydrated). Non-critical
            // network requests (profile, entitlements, history hydrate,
            // analytics warmups) continue running asynchronously
            // beneath the overlay — they never gated the transition.
            const _t_auth_complete = performance.now();
            setShowJourneyStarted(true);
            const _t_transition_shown = performance.now();
            // Welcome message appended right away, not deferred.
            setMessages((m) => [...m, {
              role: "assistant",
              content: ok
                ? "Right — this is where your cricket lives now. From here on I'll remember your matches, videos, patterns, and how you're building your game. Pick up wherever you like."
                : "Right — memory is on. From here on I'll remember your cricket. Pick up wherever you like.",
            }]);
            // Fully-hydrated marker: when the overlay finishes fading
            // (JOURNEY_STARTED_TOTAL_MS ~ 700ms), all UI is visible
            // and the assistant welcome message is already rendered.
            // Emit a single telemetry event carrying all three deltas
            // so the founder can watch p50/p95 without stitching
            // separate events together.
            const _emitTransitionTelemetry = () => {
              const _t_hydrated = performance.now();
              try {
                postClientEvent(API, sessionId, "cricket_journey_transition", {
                  delta_auth_to_transition_ms: Math.round(
                    _t_transition_shown - _t_auth_complete,
                  ),
                  delta_transition_to_first_paint_ms: 0,
                  delta_first_paint_to_hydrated_ms: Math.round(
                    _t_hydrated - _t_transition_shown,
                  ),
                  identity_ok: Boolean(ok),
                });
              } catch { /* noop */ }
            };
            // Fire once the transition finishes (parent doesn't need
            // to import JOURNEY_STARTED_TOTAL_MS — the overlay's
            // onFinished handler below already knows when it's done).
            _pendingTransitionTelemetryRef.current = _emitTransitionTelemetry;
          }}
        />
      )}
      {showAgeGate && (
        <AgeGateModal
          sessionId={sessionId}
          onPassed={onAgePassed}
          onCancel={() => { setShowAgeGate(false); setPendingFile(null); }}
        />
      )}
      {showMediaConsent && (
        <MediaConsentModal
          sessionId={sessionId}
          onAccept={onMediaConsentAccepted}
          onCancel={() => { setShowMediaConsent(false); setPendingFile(null); }}
        />
      )}
      {paywall && (
        <PaywallModal
          sessionId={sessionId}
          freeReviewUsed={paywall.used}
          freeReviewLimit={paywall.limit}
          surface={paywall.surface}
          reason={paywall.reason}
          message={paywall.message}
          upgrade_url={paywall.upgrade_url}
          onClose={() => setPaywall(null)}
          onRequireIdentity={() => {
            // Iter XX.72.4 — DO NOT clear paywall state here. If the
            // user closes IdentitySheet, the paywall must still be
            // visible underneath. Only entitlement (Plus/Pass) may
            // unlock chat — completing identity capture alone must
            // NEVER grant access.
            setIdentityForPaywall(true);
            try { localStorage.setItem("gully_last_identity_trigger", "paywall"); } catch { /* noop */ }
            api.post("/web/track", {
              session_id: sessionId, event: "history_link_attempted",
              payload: { source: "paywall" },
            }).catch(() => {});
            setNeedsIdentity(true);
          }}
        />
      )}
      {/* Iter XX.6 — Season Card modal (opened by header 🏏 Season link). */}
      <SeasonCard open={seasonOpen} onClose={() => setSeasonOpen(false)} />
      {/* Iter XX.266 — Mobile bottom navigation shell + More sheet.
          Rendered ONLY on narrow mobile widths (`sm:hidden`). Desktop
          keeps the legacy horizontal pill row (which is `hidden sm:inline-flex`).
          The nav gets its `currentRoute` from the same `computeHomeSurface`
          derivation the hero uses, so tab-selected state stays in
          lockstep with XX.265b state. Additive test IDs
          (`mobile-bottom-nav`, `mobile-nav-*`, `mobile-more-*`) —
          no legacy IDs modified. */}
      <div className="sm:hidden">
        {(() => {
          const { isAtHome } = computeHomeSurface({ messages, homePinned });
          const currentRoute = isAtHome ? "home" : "chat";
          return (
            <MobileBottomNav
              currentRoute={currentRoute}
              onHomeTap={() => {
                try { postClientEvent(API, sessionId, "authed_home_pinned_from_bottom_nav", {}); }
                catch { /* analytics best-effort */ }
                setHomePinned(true);
                try { window.scrollTo({ top: 0, behavior: "smooth" }); }
                catch { /* noop */ }
              }}
              onGullyTap={() => {
                try { postClientEvent(API, sessionId, "authed_chat_focused_from_bottom_nav", {}); }
                catch { /* analytics best-effort */ }
                // Iter XX.267c — fix #4 (bottom-nav centre variant).
                // Same rule as onPrimaryTap: unpin Home first so the
                // composer is visible, THEN focus it inside a
                // requestAnimationFrame so iOS doesn't render a
                // floating caret against a hidden textarea.
                setHomePinned(false);
                try {
                  requestAnimationFrame(() => {
                    try { textRef.current?.focus(); } catch { /* noop */ }
                  });
                } catch {
                  try { textRef.current?.focus(); } catch { /* noop */ }
                }
              }}
              onMoreTap={() => setMoreOpen(true)}
              isPlus={!!plusSurfaces?.is_plus}
            />
          );
        })()}
      </div>
      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onOpenSeason={() => setSeasonOpen(true)}
        isPlus={!!plusSurfaces?.is_plus}
        showEarn={showEarn}
        showJourney={improvementLoopOn && journeyReady}
        showSeason={showSeason}
      />
      {/* Iter XX.71 — Conversation Plus Offer. Shown ONCE after the
          user completes N free meaningful conversations. Renders only
          after the assistant reply has been appended. */}
      {conversationOffer && (
        <ConversationOfferModal
          sessionId={sessionId}
          offer={conversationOffer}
          onDismiss={() => setConversationOffer(null)}
          onClose={() => setConversationOffer(null)}
        />
      )}
      {/* Iter XX.71.3 — Anonymous Identity Claim. Shown ONCE per
          anonymous identity when they hit the meaningful-conversation
          threshold. Bridges to sign-in BEFORE the Plus offer surfaces. */}
      {identityClaimPrompt && (
        <AnonymousIdentityClaimModal
          sessionId={sessionId}
          claim={identityClaimPrompt}
          onSignIn={() => {
            setIdentityClaimPrompt(null);
            // Route to Emergent Google Auth. Preserve return path so the
            // player lands back in this same chat after claim.
            try {
              const returnTo = encodeURIComponent(
                window.location.pathname + window.location.search
              );
              window.location.href = `/login?return_to=${returnTo}`;
            } catch {
              window.location.href = "/login";
            }
          }}
          onDismiss={() => setIdentityClaimPrompt(null)}
          onClose={() => setIdentityClaimPrompt(null)}
        />
      )}
      {/* Iter XX.75 — Practice with Gully starter modal (founder-only). */}
      {practiceOpen && (
        <PracticeStarter
          onClose={() => setPracticeOpen(false)}
          sessionId={sessionId}
        />
      )}
      {/* Iter XX.26 — one-time "we found older conversations" prompt.
          Shown right after login when the browser had anonymous
          sessions older than the 30-day auto-claim window. Dismiss
          → nothing lost, just not surfaced again. Add → claims them
          via /api/web/sessions/claim with include_older=true.
          FIXED XX.71.1: was previously misplaced inside CountryPicker
          causing "Can't find variable: olderPrompt" on mobile Safari. */}
      {olderPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
             data-testid="older-conversations-modal">
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative w-full max-w-md bg-[#0c0c0c] border border-neutral-800 rounded-2xl p-6">
            <h3 className="font-display text-2xl tracking-tight mb-3">
              We found {olderPrompt.sessions_skipped_older} conversation{olderPrompt.sessions_skipped_older === 1 ? "" : "s"} from before you signed in.
            </h3>
            <p className="text-neutral-400 text-sm leading-relaxed mb-6">
              Add them to your Gully journey so nothing you shared earlier is lost?
            </p>
            <div className="flex flex-col gap-2">
              <button
                data-testid="older-add-btn"
                disabled={olderPromptBusy}
                onClick={async () => {
                  setOlderPromptBusy(true);
                  try {
                    // eslint-disable-next-line global-require
                    const _authMod = require("../lib/auth");
                    await _authMod.claimOlderConversations();
                    toast.success("Added to your journey.");
                  } catch { /* swallow — token has been saved on the row-only path */ }
                  setOlderPrompt(null);
                  setOlderPromptBusy(false);
                }}
                className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 font-medium"
              >
                {olderPromptBusy ? "Adding…" : "Add conversations"}
              </button>
              <button
                data-testid="older-dismiss-btn"
                disabled={olderPromptBusy}
                onClick={() => setOlderPrompt(null)}
                className="w-full py-3 rounded-xl border border-neutral-700 text-neutral-300 hover:border-neutral-500"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function Bubble({ role, content, attachments, kind, onTryAgain,
                 // Iter XX.124 — Continue coaching without the video.
                 onContinueWithoutVideo,
                 identity_nudge, onNudgeClick,
                 billing_cta, coach_recommendation,
                 timeline_added, journey_added,
                 clarification_kind, clarification_chips,
                 clarification_resolved, onClarifyChip,
                 cricket_memory_confirmation, session_memory,
                 video_invitation,
                 onAcceptVideoInvitation, onDeclineVideoInvitation,
                 // Iter XX.253 — Format-Aware Cricket Coaching chip.
                 format_clarification, onSelectFormat, onDismissFormat }) {
  const isUser = role === "user";
  const isReviewFailed = kind === "video_review_failed";
  const isClarification = kind === "video_clarification";
  // Iter XX.204 — "Your Cricket" personalised value reveal. Renders
  // as a distinct card ABOVE the standard bubble frame so the player
  // sees THEIR cricket, not a generic sales bubble.
  const yourCricketAtt = (attachments || []).find(
    (a) => a && a.kind === "your_cricket_reveal"
  );
  if (yourCricketAtt) {
    return (
      <div
        className="flex flex-col items-start w-full"
        data-testid="your-cricket-reveal-card"
      >
        <div className="max-w-[85%] w-full rounded-2xl border border-emerald-800/50 bg-gradient-to-br from-neutral-900 to-neutral-950 px-4 py-3.5">
          <div className="text-[11px] uppercase tracking-widest text-emerald-500/90 font-semibold" data-testid="your-cricket-title">
            {yourCricketAtt.title || "Your Cricket"}
          </div>
          <div className="mt-1 text-[15px] text-neutral-100 leading-snug" data-testid="your-cricket-heading">
            {yourCricketAtt.heading}
          </div>
          {/* Iter XX.273 — Continue This Journey variant sub-line.
              Renders "Your current focus: {focus}" only when the
              journey_offer_v1 variant is present. Suppresses the
              standalone current-focus block below to avoid a
              duplicate render. */}
          {yourCricketAtt.variant === "journey_offer_v1" && yourCricketAtt.sub_line && (
            <div
              className="mt-1.5 text-[13px] text-emerald-300/90 leading-snug"
              data-testid="your-cricket-sub-line"
            >
              {yourCricketAtt.sub_line}
            </div>
          )}
          {yourCricketAtt.current_focus && yourCricketAtt.variant !== "journey_offer_v1" && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">Current focus</div>
              <div className="mt-0.5 text-[14px] text-neutral-200" data-testid="your-cricket-focus">
                {yourCricketAtt.current_focus}
              </div>
            </div>
          )}
          {yourCricketAtt.observation && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">What we&apos;ve found</div>
              <div className="mt-0.5 text-[14px] text-neutral-200" data-testid="your-cricket-observation">
                {yourCricketAtt.observation}
              </div>
            </div>
          )}
          {yourCricketAtt.next_step && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">Your next step</div>
              <div className="mt-0.5 text-[14px] text-neutral-200" data-testid="your-cricket-next-step">
                {yourCricketAtt.next_step}
              </div>
            </div>
          )}
          {yourCricketAtt.value_line && (
            <div className="mt-3 pt-3 border-t border-neutral-800 text-[13px] text-neutral-400 leading-snug" data-testid="your-cricket-value-line">
              {yourCricketAtt.value_line}
            </div>
          )}
          {/* Iter XX.295 — Native IAP integrity gate: on a native build
              that cannot configure RevenueCat this CTA is replaced with
              a truthful update-app state instead of a dead purchase
              button. Web renders unchanged. */}
          <NativePurchaseCta testId="your-cricket-unavailable">
          {/* Iter XX.204 — CTA. On Android/iOS native this invokes the
              native Google Play / RevenueCat purchase path via
              beginPlusPurchase(). On web it flows through the same
              helper which routes to Stripe Checkout. */}
          <button
            type="button"
            data-testid="your-cricket-cta"
            data-cta-intent="coaching_arc_used"
            onClick={async () => {
              // Iter XX.273 — journey_offer variant fires the tap
              // + paywall-view impressions and uses a distinct
              // entryPoint so downstream Meta / activation events
              // can correlate this cohort. Falls back to the XX.204
              // entryPoint when the variant is absent.
              const _isJourney = yourCricketAtt.variant === "journey_offer_v1";
              // Iter XX.282 — Canonical `plus_cta_tapped` fires
              // UNCONDITIONALLY for every arc-lock CTA tap (both
              // journey_offer_v1 and legacy_your_cricket). Carries
              // {surface, variant, product_id, offer_id, source} so
              // XX.277 / Muse can split CTA→purchase-start funnel by
              // paywall variant without inferring the tap from the
              // downstream `purchase_started` event. Additive; the
              // existing `journey_offer_tap` event continues to fire
              // for backwards compatibility with XX.277 variant
              // derivation.
              try {
                const _variantForTap = _isJourney
                  ? "journey_offer_v1"
                  : "legacy_your_cricket";
                await trackMetaFunnelEvent("plus_cta_tapped", {
                  milestone_key: "arc_lock_402",
                  extra: {
                    surface:    "arc_lock_402",
                    variant:    _variantForTap,
                    product_id: "plus_monthly",
                    offer_id:   "plus_default_v1",
                    source:     _variantForTap === "journey_offer_v1"
                                  ? "journey_offer_v1"
                                  : "your_cricket_arc_lock_402",
                  },
                });
              } catch { /* fire-and-forget */ }
              // Iter XX.288 — Canonical `journey_offer_tap` fires
              // UNCONDITIONALLY on every arc-lock CTA tap, tagged with
              // the resolved `variant` so Muse's `cta_taps` funnel
              // dimension counts both journey_offer_v1 and the
              // legacy_your_cricket fallback. Cohort split lives in the
              // event metadata, not the emit gate.
              //
              // Previously this emit was inside `if (_isJourney)`,
              // which caused `cta_taps` to stay at 0 for the
              // legacy_your_cricket cohort even though Stripe Checkout
              // opened correctly. Reproduced in the XX.288 controlled
              // production test (`purchase_started` > 0, `cta_taps` == 0).
              //
              // One tap = one event (fires inside the onClick handler,
              // not inside any render effect).
              try {
                trackFunnel("journey_offer_tap", {
                  surface: "arc_lock_402",
                  variant: _isJourney ? "journey_offer_v1" : "legacy_your_cricket",
                });
              } catch { /* fire-and-forget */ }
              if (_isJourney) {
                try {
                  trackFunnel("journey_offer_paywall_view", { surface: "arc_lock_402" });
                } catch { /* fire-and-forget */ }
                try {
                  // eslint-disable-next-line global-require
                  const { stashJourneyOrigin } = require("../lib/journeyOrigin");
                  stashJourneyOrigin({ focusText: yourCricketAtt.current_focus });
                } catch { /* noop */ }
              }
              try {
                await beginPlusPurchase({
                  entryPoint: _isJourney
                    ? "journey_offer_v1"
                    : "your_cricket_arc_lock_402",
                  variant: _isJourney
                    ? "journey_offer_v1"
                    : "legacy_your_cricket",
                });
              } catch (e) {
                try { console.error("[your_cricket] beginPlusPurchase failed", e); } catch { /* noop */ }
              }
            }}
            className="mt-4 w-full inline-flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 transition-colors shadow-sm"
          >
            <span className="flex items-center gap-2 text-white">
              <Sparkles size={16} className="opacity-90" />
              <span className="text-[15px] font-medium">
                {yourCricketAtt.cta_label || "Continue with Gully Plus"}
              </span>
            </span>
            <span aria-hidden className="text-white/80 text-lg leading-none">→</span>
          </button>
          </NativePurchaseCta>
        </div>
      </div>
    );
  }
  // Render the failed-review body with a bold title (markdown-style **...**).
  const renderContent = () => {
    if (!isReviewFailed) return content;
    const parts = String(content || "").split(/\n\n/);
    return parts.map((p, i) => {
      const m = p.match(/^\*\*(.+?)\*\*$/);
      if (m) return <p key={i} className="font-medium text-neutral-50 mb-1">{m[1]}</p>;
      return <p key={i} className={i ? "mt-2 text-neutral-300" : "text-neutral-300"}>{p}</p>;
    });
  };
  return (
    <div
      className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
      data-testid={isUser ? "chat-msg-user" : "chat-msg-assistant"}
    >
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] leading-snug whitespace-pre-wrap break-words ${
        isUser ? "bg-emerald-600 text-white" : "bg-neutral-900 border border-neutral-800 text-neutral-100"
      } ${isReviewFailed ? "whitespace-normal" : ""}`}>
        {renderContent()}
        {(attachments || []).map((a, i) => a.kind === "video" && (
          <div key={i} className="mt-1 text-xs opacity-70">🎥 {a.filename}</div>
        ))}
        {/* Iter XX.234 — Visual Coaching v1. Renders the first
            `visual_explanation` attachment on this assistant message.
            Older clients silently drop the unknown kind (safe default). */}
        {!isUser && (attachments || [])
          .filter((a) => a && a.kind === "visual_explanation")
          .slice(0, 1)
          .map((a, i) => (
            <VisualExplanation key={`ve-${i}`} payload={a} />
          ))}
        {isReviewFailed && onTryAgain && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* Iter XX.124 — "Continue without video" is the PRIMARY
                affordance. The user's goal is improving, not
                uploading. Retry stays as a secondary path. */}
            <button
              onClick={() => {
                try {
                  if (typeof onContinueWithoutVideo === "function") {
                    onContinueWithoutVideo();
                    return;
                  }
                } catch { /* fallthrough */ }
                // Fallback: synthesise a "let's keep coaching" user
                // message so the chat loop resumes naturally.
                try {
                  const evt = new CustomEvent("gully:continue-without-video");
                  window.dispatchEvent(evt);
                } catch { /* noop */ }
              }}
              data-testid="video-review-continue-without-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-neutral-950 text-sm font-medium transition-colors"
            >
              Continue coaching without video
            </button>
            <button
              onClick={onTryAgain}
              data-testid="video-review-try-again-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-100 text-sm font-medium transition-colors"
            >
              <Video size={14} /> Try again
            </button>
          </div>
        )}
      </div>
      {/* Iter 55 — Cricket Timeline chip. Subtle, tappable footnote
          shown ONLY on assistant messages for Plus users whose turn
          was added to the timeline (match / practice / video review).
          Free users and non-reflection messages never see this. */}
      {!isUser && timeline_added && timeline_added.type && (
        <Link
          to="/timeline"
          data-testid="timeline-added-chip"
          data-timeline-type={timeline_added.type}
          className="mt-1.5 ml-1 text-[11px] text-emerald-400/80 hover:text-emerald-300 transition-colors inline-flex items-center gap-1"
        >
          <Sparkles size={11} className="opacity-70" />
          Added to your Cricket Timeline
        </Link>
      )}
      {/* Iter XX.74 — Journey inline pill. Shown on the assistant
          turn that just captured a fresh commitment. Small, warm,
          taps through to /journey. Only rendered once per bubble
          (the flag lives on the message, not the session). */}
      {!isUser && journey_added && (
        <Link
          to="/journey"
          data-testid="journey-added-chip"
          className="mt-1.5 ml-1 text-[11px] text-emerald-300 hover:text-emerald-200 transition-colors inline-flex items-center gap-1"
        >
          <Sparkles size={11} className="opacity-70" />
          Added to your Journey →
        </Link>
      )}
      {/* Iter XX.97 — Cricket Memory confirmation card. Rendered
          beneath the assistant bubble when the server (or the
          poller) sets `cricket_memory_confirmation`. Three visual
          states: saving / saved / failed. */}
      {!isUser && cricket_memory_confirmation && (
        <CricketMemoryConfirmationCard payload={cricket_memory_confirmation} />
      )}
      {/* Iter XX.111 — "Today's Session Saved" memory card. Rendered
          beneath the assistant bubble on the exact turn a coaching
          arc closes (server attaches `session_memory` only then).
          Never appears alongside its own Plus CTA — the Plus offer
          lives on the NEXT turn per the founder's brief. */}
      {!isUser && session_memory && session_memory.observation && (
        <SessionMemoryCard memory={session_memory} />
      )}
      {/* Iter XX.85 — Coach Recommendation Card. Rendered beneath
          the assistant bubble when the server attaches a
          coach_recommendation block. NEVER shown alongside a
          billing_cta (backend enforces this too via
          already_has_paywall_in_reply). */}
      {!isUser && !billing_cta && coach_recommendation
        && coach_recommendation.text && (
        <CoachRecommendationCard recommendation={coach_recommendation} />
      )}
      {/* Iter XX.246 — Contextual Video Coaching chip. Backend attaches
          `video_invitation` when Gemini emitted a validated invite via
          <GULLY_META> AND hard-suppression didn't strip it. Chip
          explains WHY the video would help (not a generic "upload a
          video"). On tap → opens the existing upload picker with the
          focus already staged server-side (POST /video-invite/accept
          persists the pending_analysis_focus). "Not now" declines the
          invite (POST /video-invite/decline sets the silence window). */}
      {!isUser && video_invitation
        && video_invitation.invitation_text
        && onAcceptVideoInvitation && (
        <div
          data-testid="video-invitation-chip"
          data-invitation-focus={video_invitation.focus_category}
          data-invitation-discipline={video_invitation.discipline}
          className="mt-2 max-w-[85%] rounded-2xl border border-emerald-800/40 bg-gradient-to-br from-neutral-950 to-neutral-900 px-4 py-3"
        >
          <div className="text-[11px] uppercase tracking-widest text-emerald-500/90 font-semibold">
            Send a clip · {String(video_invitation.discipline || "").replace(/^./, (c) => c.toUpperCase())}
          </div>
          <div className="mt-1 text-[14px] text-neutral-100 leading-snug">
            {video_invitation.invitation_text}
          </div>
          {video_invitation.filming_guidance && (
            <div className="mt-1 text-[12px] text-neutral-400 leading-snug">
              {video_invitation.filming_guidance}
            </div>
          )}
          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={onAcceptVideoInvitation}
              data-testid="video-invitation-accept"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white text-[13px] font-medium px-3.5 py-1.5 transition-colors"
            >
              Send a clip
            </button>
            {onDeclineVideoInvitation && (
              <button
                type="button"
                onClick={onDeclineVideoInvitation}
                data-testid="video-invitation-decline"
                className="inline-flex items-center rounded-full text-neutral-400 hover:text-neutral-200 text-[13px] px-3 py-1.5 transition-colors"
              >
                Not now
              </button>
            )}
          </div>
        </div>
      )}
      {/* Iter XX.253 — Format-Aware Cricket Coaching chip. Backend
          attaches `format_clarification` when the LLM decides the
          answer materially depends on format AND context doesn't
          establish it. 4 quick options. Tap → POST /format/select
          + persist active_format on session. Dismiss → POST
          /format/dismiss + 30-min silence window. */}
      {!isUser && format_clarification
        && format_clarification.options
        && format_clarification.options.length > 0
        && onSelectFormat && (
        <div
          data-testid="format-clarification-chip"
          data-clarification-reason={format_clarification.reason}
          className="mt-2 max-w-[85%] rounded-2xl border border-sky-800/40 bg-gradient-to-br from-neutral-950 to-neutral-900 px-4 py-3"
        >
          <div className="text-[11px] uppercase tracking-widest text-sky-500/90 font-semibold">
            Which format?
          </div>
          {format_clarification.reason && (
            <div className="mt-1 text-[12px] text-neutral-400 leading-snug">
              {format_clarification.reason}
            </div>
          )}
          <div className="mt-2.5 flex flex-wrap gap-2">
            {format_clarification.options.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onSelectFormat(opt.key)}
                data-testid={`format-option-${opt.key}`}
                className="inline-flex items-center gap-2 rounded-full bg-sky-700 hover:bg-sky-600 active:bg-sky-800 text-white text-[13px] font-medium px-3.5 py-1.5 transition-colors"
              >
                {opt.label}
              </button>
            ))}
            {onDismissFormat && (
              <button
                type="button"
                onClick={onDismissFormat}
                data-testid="format-clarification-dismiss"
                className="inline-flex items-center rounded-full text-neutral-400 hover:text-neutral-200 text-[13px] px-3 py-1.5 transition-colors"
              >
                Not now
              </button>
            )}
          </div>
        </div>
      )}
      {/* Iter 42.1 — Billing intent CTA card. Renders as a prominent
          Upgrade/Manage button instead of leaking a raw Stripe URL into
          the bubble text. cta.url is either a REAL Stripe Checkout URL
          (linked purchase intent) or the host /upgrade page — never
          fabricated.
          Iter XX.214 — On Capacitor native (iOS / Android), we do NOT
          follow `cta.url` (would leak Stripe / external checkout into
          the WebView — Apple 3.1.1 + Google Play policy violation).
          Instead we invoke `beginPlusPurchase()` which routes through
          RevenueCat → StoreKit / Play Billing. Web behaviour unchanged
          — the same URL follows as before. */}
      {!isUser && billing_cta && billing_cta.label && (
        isNative() ? (
          // Iter XX.295 — Native IAP integrity gate: a build that cannot
          // configure RevenueCat shows a truthful update-app state
          // instead of this purchase button.
          <NativePurchaseCta testId="billing-cta-unavailable">
          <button
            type="button"
            onClick={() => {
              // Iter XX.288b — Canonical `journey_offer_tap` also fires
              // on the plain billing_cta bubble (the visible upsell
              // when server 402 arrived without a `your_cricket`
              // payload). XX.288 only wired the emit to the
              // `your-cricket-cta` inside the reveal card, which
              // meant low-evidence users tapped the plain CTA and
              // Muse's `cta_taps` stayed at 0. One tap = one event.
              if (billing_cta.intent === "coaching_arc_used"
                  || billing_cta.intent === "coaching_arc_completed") {
                try {
                  trackFunnel("journey_offer_tap", {
                    surface: "arc_lock_402",
                    variant: "legacy_your_cricket",
                    intent:  billing_cta.intent,
                    cta_source: "billing_cta_native",
                  });
                } catch { /* fire-and-forget */ }
              }
              try {
                beginPlusPurchase({
                  entryPoint: `billing_cta_${billing_cta.intent || "generic"}`,
                });
              } catch { /* fire-and-forget */ }
            }}
            data-testid={`billing-cta-${billing_cta.intent || "generic"}`}
            data-cta-intent={billing_cta.intent}
            data-cta-native="true"
            className="mt-2 max-w-[85%] inline-flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 transition-colors shadow-sm"
          >
            <span className="flex items-center gap-2 text-white">
              <Sparkles size={16} className="opacity-90" />
              <span className="text-[15px] font-medium">{billing_cta.label}</span>
            </span>
            <span aria-hidden className="text-white/80 text-lg leading-none">→</span>
          </button>
          </NativePurchaseCta>
        ) : billing_cta.url ? (
          <a
            href={billing_cta.url}
            target={billing_cta.url.startsWith("http") ? "_blank" : "_self"}
            rel="noopener noreferrer"
            data-testid={`billing-cta-${billing_cta.intent || "generic"}`}
            data-cta-intent={billing_cta.intent}
            data-conversion-context-id={billing_cta.conversion_context_id || undefined}
            onClick={() => {
              // Iter XX.223 — fire the click beacon on the same funnel
              // id so we can attribute purchase → trigger reason.
              try { trackCtaClick(billing_cta); } catch {}
              // Iter XX.288b — Canonical `journey_offer_tap` also on
              // the web variant of the plain billing_cta (arc-lock
              // intent). Mirrors the native branch above. Deduped by
              // sessionStorage inside `trackFunnel`, so a rapid
              // double-tap counts once.
              if (billing_cta.intent === "coaching_arc_used"
                  || billing_cta.intent === "coaching_arc_completed") {
                try {
                  trackFunnel("journey_offer_tap", {
                    surface: "arc_lock_402",
                    variant: "legacy_your_cricket",
                    intent:  billing_cta.intent,
                    cta_source: "billing_cta_web",
                  });
                } catch { /* fire-and-forget */ }
              }
            }}
            className="mt-2 max-w-[85%] inline-flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 transition-colors shadow-sm"
          >
            <span className="flex items-center gap-2 text-white">
              <Sparkles size={16} className="opacity-90" />
              <span className="text-[15px] font-medium">{billing_cta.label}</span>
            </span>
            <span aria-hidden className="text-white/80 text-lg leading-none">→</span>
          </a>
        ) : null
      )}
      {/* Iter 33 — Recovery clarification card. Rendered just below the
          assistant bubble that delivered the question. Disabled once a chip
          has been clicked (the message gets `clarification_resolved=true`). */}
      {isClarification && clarification_chips && onClarifyChip && (
        <ClarificationChips
          kind={clarification_kind}
          chips={clarification_chips}
          onPick={onClarifyChip}
          disabled={!!clarification_resolved}
        />
      )}
      {/* Contextual identity nudge — rendered just below the assistant
          bubble it belongs to. Never a separate screen. */}
      {!isUser && identity_nudge && onNudgeClick && (
        <div className="mt-2 max-w-[85%] surface-1 px-3 py-2.5 rounded-xl border border-emerald-900/50 bg-emerald-950/20"
             data-testid={`identity-nudge-${identity_nudge.trigger}`}>
          <p className="text-xs text-neutral-300 leading-relaxed mb-2">
            {identity_nudge.copy}
          </p>
          <button
            onClick={() => onNudgeClick(identity_nudge.trigger)}
            data-testid="identity-nudge-cta"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-300 hover:text-emerald-200"
          >
            {identity_nudge.cta} →
          </button>
        </div>
      )}
    </div>
  );
}

// ---- ClarificationChips ---------------------------------------------------
// Renders a small chip grid + (for non-cricket) an optional freeform field.
// On chip click, calls onPick(chipId, freeformText) and then locks itself.
function ClarificationChips({ kind, chips, onPick, disabled }) {
  const [freeform, setFreeform] = useState("");
  const [locked, setLocked] = useState(false);
  const isLocked = locked || !!disabled;

  function handlePick(chipId) {
    if (isLocked) return;
    setLocked(true);
    onPick?.(chipId, freeform);
  }

  const wantsFreeformField = kind === "non_cricket";

  return (
    <div
      className="mt-2 max-w-[85%] w-full surface-1 px-3 py-3 rounded-xl border border-emerald-900/50 bg-emerald-950/20"
      data-testid={`clarification-card-${kind}`}
    >
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <button
            key={c.id}
            onClick={() => handlePick(c.id)}
            disabled={isLocked}
            data-testid={`clarification-chip-${kind}-${c.id}`}
            className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
              isLocked
                ? "border-neutral-800 text-neutral-500 bg-neutral-900/40 cursor-not-allowed"
                : "border-emerald-900/60 text-emerald-200 hover:bg-emerald-900/30"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      {wantsFreeformField && !isLocked && (
        <div className="mt-2">
          <input
            value={freeform}
            onChange={(e) => setFreeform(e.target.value)}
            placeholder="Add a quick description (optional)"
            maxLength={300}
            className="w-full bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2 text-sm placeholder-neutral-500 focus:outline-none focus:border-neutral-700"
            data-testid={`clarification-freeform-${kind}`}
          />
        </div>
      )}
      {isLocked && (
        <div className="mt-2 text-xs text-neutral-500" data-testid={`clarification-locked-${kind}`}>
          Thanks — working on it…
        </div>
      )}
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex justify-start" data-testid="chat-typing">
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl px-4 py-3 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: "0ms" }}></span>
        <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: "120ms" }}></span>
        <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: "240ms" }}></span>
      </div>
    </div>
  );
}

// Iter 69 — Session divider.
// Marks the visual boundary of a "new session" (14h+ gap) inside the
// single lifelong conversation. Metadata-styled: centred, small, muted.
// Never a full horizontal rule — subtle so it doesn't interrupt reading.
function _formatSessionDay(dayBucket) {
  // dayBucket is "YYYY-MM-DD" (UTC on the server, treated as a plain
  // calendar date on the client). Fallback to today's local date if
  // missing/invalid.
  const todayLocal = new Date();
  const y = todayLocal.getFullYear();
  const m = String(todayLocal.getMonth() + 1).padStart(2, "0");
  const d = String(todayLocal.getDate()).padStart(2, "0");
  const todayKey = `${y}-${m}-${d}`;
  const key = (dayBucket && /^\d{4}-\d{2}-\d{2}$/.test(dayBucket))
    ? dayBucket : todayKey;
  if (key === todayKey) return "Today";
  // Parse as a local calendar date so "Feb 15" formatting isn't shifted
  // by the browser's timezone.
  const [yy, mm, dd] = key.split("-").map((n) => parseInt(n, 10));
  try {
    const dt = new Date(yy, (mm || 1) - 1, dd || 1);
    return new Intl.DateTimeFormat(undefined, {
      month: "short", day: "numeric",
    }).format(dt);
  } catch {
    return key;
  }
}

function SessionDivider({ dayBucket }) {
  const label = _formatSessionDay(dayBucket);
  return (
    <div
      className="flex items-center justify-center py-3 select-none"
      role="separator"
      aria-label={`${label} · New Session`}
      data-testid="chat-session-divider"
    >
      <span
        className="text-[11px] leading-none tracking-wide text-neutral-500"
        data-testid="chat-session-divider-label"
      >
        {label} <span className="text-neutral-600 mx-1">·</span> New Session
      </span>
    </div>
  );
}

function DebugOverlay() {
  // Fixed pill at top of screen. Shows the values that determine whether
  // the composer fix is actually working on this device. Activate with
  // ?debug=1 in the URL.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((n) => n + 1), 500);
    const vv = window.visualViewport;
    const onVV = () => setTick((n) => n + 1);
    if (vv) {
      vv.addEventListener("resize", onVV);
      vv.addEventListener("scroll", onVV);
    }
    return () => {
      clearInterval(i);
      if (vv) {
        vv.removeEventListener("resize", onVV);
        vv.removeEventListener("scroll", onVV);
      }
    };
  }, []);
  const innerH = typeof window !== "undefined" ? window.innerHeight : 0;
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  const vvH = vv ? Math.round(vv.height) : null;
  const vvT = vv ? Math.round(vv.offsetTop) : null;
  const effective = typeof document !== "undefined"
    ? getComputedStyle(document.documentElement).getPropertyValue("--vv-h").trim()
    : "";
  const composer = typeof document !== "undefined"
    ? document.querySelector('[data-testid="chat-composer"]')
    : null;
  const cRect = composer?.getBoundingClientRect();
  const msgs = typeof document !== "undefined"
    ? document.querySelector('[data-testid="chat-messages"]')
    : null;
  const mRect = msgs?.getBoundingClientRect();
  return (
    <div
      style={{
        position: "fixed", top: 4, left: 4, right: 4, zIndex: 9999,
        background: "rgba(255,200,0,0.92)", color: "#000",
        fontFamily: "monospace", fontSize: 10, padding: "4px 8px",
        borderRadius: 6, lineHeight: 1.3,
      }}
      data-testid="chat-debug-overlay"
    >
      <div>innerH={innerH} vvH={vvH ?? "—"} vvTop={vvT ?? "—"} --vv-h={effective}</div>
      <div>
        composer y=[{cRect ? Math.round(cRect.top) : "—"},{cRect ? Math.round(cRect.bottom) : "—"}]
        {" "}msgs h={mRect ? Math.round(mRect.height) : "—"}
        {" "}t={tick}
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// VideoUploadTips — Iter 43.1
// Pre-upload expectation-setting helper. Founder-approved copy. Rendered
// above the input area when collapsed=false. Goal: lift first-time review
// success rate by telling the player BEFORE upload what kinds of clips
// review reliably. No friction — never blocks upload, never requires
// trimming. The "larger than recommended" warning is rendered separately
// inside UploadingRow once size/duration exceeds the founder thresholds.
// ---------------------------------------------------------------------------
function VideoUploadTips({ onDismiss }) {
  return (
    <div className="max-w-2xl mx-auto px-3 pt-1.5">
      <div
        className="relative rounded-2xl border border-emerald-900/40 bg-emerald-950/15 px-4 py-3 pr-9 text-[12px] leading-relaxed text-emerald-100/85"
        data-testid="chat-upload-tips-card"
      >
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-2 right-2 p-1 rounded-full text-emerald-200/60 hover:text-emerald-200 hover:bg-emerald-900/30 transition-colors"
          aria-label="Dismiss upload tips"
          data-testid="chat-upload-tips-dismiss"
        >
          <X size={14} />
        </button>
        <div className="font-semibold text-emerald-200 mb-1.5">
          To get the best review
        </div>
        <ul className="space-y-1 marker:text-emerald-500 list-disc pl-4">
          <li>Upload a 5–10 second clip</li>
          <li>Make sure you are clearly visible in the frame</li>
          <li>
            If multiple players are visible, tell Gully who you are
            <span className="text-emerald-200/60"> (e.g. &quot;I&apos;m the bowler&quot;)</span>
          </li>
          <li>Shorter clips are reviewed faster and more accurately</li>
          <li>Large files and long videos may not review successfully yet</li>
        </ul>
      </div>
    </div>
  );
}


// Iter XX.124 — Coach-voice progress labels replace the generic
// "Reviewing your clip now". Every phase now sounds like a coach at
// work, not a backend process. Trust > infrastructure. Labels are
// picked up from the poll status endpoint (`/api/video/status`) by
// the parent Chat surface; UploadingRow just renders what it's given.
// Byte-transfer phase (pct < 100) still shows percentage.
//
// Mapping (backend lifecycle → coach label):
//   received | validating       → "Receiving your video…"
//   stored | normalizing        → "Getting it ready…"
//   reviewing                   → "Watching each delivery…"
//   formatting                  → "Putting my coaching together…"
//   completed                   → "Ready."  (transient — bubble replaces)
const _COACH_PHASE_LABELS = {
  received:     "Receiving your video…",
  validating:   "Receiving your video…",
  stored:       "Getting it ready…",
  normalizing:  "Getting it ready…",
  reviewing:    "Watching each delivery…",
  formatting:   "Putting my coaching together…",
  completed:    "Ready.",
};

function UploadingRow({ pct, phase }) {
  // `phase` is the backend lifecycle state (nullable). When missing,
  // fall back to the canonical "watching" label so the user always
  // sees a coach-voice line — never a generic spinner label.
  const isTransferring = pct < 100;
  const coachLabel =
    _COACH_PHASE_LABELS[phase] || "Watching each delivery…";
  const label = isTransferring ? `Uploading ${pct}%` : coachLabel;
  const helper = isTransferring
    ? "can take a moment on mobile"
    : "hang tight — we’re still on the same conversation";
  return (
    <div className="space-y-2" data-testid="chat-uploading">
      {/* Iter XX.124 — Video Promise. Set expectations BEFORE anything
          can fail. The user needs to hear the reassurance the moment
          the upload begins, not after a failure. Short, warm, one
          line. Only rendered during the transfer phase so it doesn't
          repeat once the reviewer takes over. */}
      {isTransferring && (
        <div
          data-testid="chat-video-promise"
          className="rounded-xl border border-emerald-900/30 bg-emerald-950/10 px-3 py-2 text-[12px] leading-snug text-emerald-200/85"
        >
          I’m going to watch this like a coach would. If anything
          goes wrong on my end, we’ll keep coaching and you won’t
          lose today’s conversation.
        </div>
      )}
      <div
        className="rounded-2xl border border-emerald-900/40 bg-emerald-950/20 p-3"
        data-testid="chat-uploading-progress"
        data-phase={phase || (isTransferring ? "uploading" : "reviewing")}
      >
        <div className="flex items-center justify-between text-xs text-neutral-300 mb-2">
          <span data-testid="chat-uploading-label">{label}</span>
          <span className="text-neutral-500">{helper}</span>
        </div>
        <div className="h-1.5 rounded-full bg-neutral-800 overflow-hidden">
          <div className={`h-full bg-emerald-500 transition-all duration-300 ${pct === 100 ? "animate-pulse" : ""}`}
               style={{ width: `${pct === 100 ? 100 : Math.max(2, pct)}%` }} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// IdentitySheet — phone capture for "Save my cricket history"
// Country selector is a searchable picker backed by /app/frontend/src/lib/
// countries.js (all 240+ E.164 dialing codes). Backend validation goes
// through Google libphonenumber so any valid international number works.
// ---------------------------------------------------------------------------
function CountryPicker({ selected, onChange, sessionId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Defer focus until the popover is in the DOM. Mobile keyboards take a
      // beat to open — autofocus on the input feels native.
      setTimeout(() => inputRef.current?.focus(), 40);
    } else {
      setQuery("");
    }
  }, [open]);

  const results = useMemo(() => searchCountries(query), [query]);

  function pick(c) {
    const prevCode = selected.code;
    onChange(c.code);
    setOpen(false);
    if (prevCode !== c.code && sessionId) {
      api.post("/web/track", {
        session_id: sessionId,
        event: "country_selected",
        payload: { from: prevCode, to: c.code, dial: c.dial },
      }).catch(() => {});
    }
  }

  return (
    <div className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 bg-neutral-900 border border-neutral-800 rounded-xl pl-3 pr-2 py-3 text-[16px] text-neutral-100 focus:outline-none focus:border-neutral-600 hover:bg-neutral-800/60 transition-colors"
        data-testid="identity-country-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country: ${selected.name}, dial code ${selected.dial}`}
      >
        <span className="text-base leading-none">{selected.flag}</span>
        <span className="text-sm text-neutral-300">{selected.dial}</span>
        <ChevronDown size={14} className="text-neutral-500" />
      </button>

      {open && (
        <>
          {/* Click-away catcher */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            data-testid="identity-country-backdrop"
          />
          <div
            className="absolute left-0 top-[calc(100%+6px)] z-50 w-[min(88vw,360px)] max-h-[60vh] flex flex-col rounded-2xl border border-neutral-800 bg-[#121212] shadow-2xl shadow-black/60 overflow-hidden"
            data-testid="identity-country-popover"
            role="listbox"
          >
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-neutral-800">
              <Search size={14} className="text-neutral-500" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code"
                className="flex-1 bg-transparent text-[15px] text-neutral-100 placeholder-neutral-500 focus:outline-none"
                data-testid="identity-country-search"
                aria-label="Search country"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="text-neutral-500 hover:text-neutral-300"
                  aria-label="Clear search"
                  data-testid="identity-country-search-clear"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto py-1" data-testid="identity-country-list">
              {results.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-neutral-500" data-testid="identity-country-empty">
                  No countries match &ldquo;{query}&rdquo;.
                </div>
              ) : (
                results.map((c) => {
                  const isSelected = c.code === selected.code;
                  return (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => pick(c)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[15px] hover:bg-neutral-800/70 transition-colors ${
                        isSelected ? "bg-neutral-800/40" : ""
                      }`}
                      data-testid={`identity-country-option-${c.code}`}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <span className="text-base leading-none">{c.flag}</span>
                      <span className="flex-1 truncate text-neutral-100">{c.name}</span>
                      <span className="text-xs text-neutral-500">{c.dial}</span>
                      {isSelected && <Check size={14} className="text-emerald-400" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function IdentitySheet({ onClose, sessionId, onLinked, gated = false }) {
  // XX.294 — Country starts UNSELECTED. Browser-locale inference has
  // been proven unreliable (Toronto Chrome → US, India user → GB).
  // The user must explicitly pick their country before Save fires.
  const [country, setCountry] = useState(guessDefaultCountry); // returns null
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // XX.294 — INLINE error surface. `sonner` toasts alone were the
  // silent-no-op root cause: on mobile Safari at 390px, a bottom-right
  // toast can be clipped behind the fixed bottom nav (z-80) while the
  // sheet remains open (z-60), producing a visible dead tap. Inline
  // errors render INSIDE the sheet's own DOM and cannot be occluded.
  const [error, setError] = useState(null);
  // Card 4 — When the backend returns the XX.294 linked-account 409
  // ("This number is already linked … please sign in with Google"),
  // we surface an ACTIONABLE "Continue with Google" button directly
  // beneath the inline error. Text alone was leaving users stranded —
  // the sheet had no visible sign-in control.
  const [linkedAccount, setLinkedAccount] = useState(false);

  // Selected country — null-safe sentinel when unpicked so the picker
  // renders "🌐 Select country" without crashing.
  const selected = country
    ? (COUNTRY_BY_CODE[country] || ORDERED_COUNTRIES[0])
    : { code: null, dial: "", flag: "🌐", name: "Select country" };
  const phoneMeta = country ? phoneMetaFor(country) : null;

  // Whenever country or phone changes, clear any stale error so the
  // user gets a live sense that the form is responsive.
  useEffect(() => {
    if (error) setError(null);
    // Card 4 — also clear the linked-account CTA when inputs change,
    // so it doesn't linger over an edited (potentially different)
    // number.
    if (linkedAccount) setLinkedAccount(false);
  },
           /* eslint-disable-next-line react-hooks/exhaustive-deps */ [country, phone]);

  // Fire `identity_sheet_opened` once per mount — the funnel's second step.
  // Carries the trigger that opened the sheet so we can split capture rates
  // by entry point (header / nudge / paywall / auto_3msg).
  useEffect(() => {
    if (!sessionId) return;
    let trigger = null;
    try { trigger = localStorage.getItem("gully_last_identity_trigger"); } catch { /* noop */ }
    api.post("/web/track", {
      session_id: sessionId,
      event: "identity_sheet_opened",
      payload: { trigger: trigger || "unknown", default_country: country },
    }).catch(() => {});
  }, [sessionId]);

  // XX.294 — Client-side pre-validation. Fast, actionable, keyed to
  // the selected country. Backend libphonenumber remains authoritative.
  function _validate() {
    if (!phone.trim()) return "Phone number is required.";  // preserved wording (Test 3)
    if (!country) return "Please choose your country first.";
    const digits = phone.replace(/\D/g, "");
    if (digits.length < phoneMeta.min || digits.length > phoneMeta.max) {
      return `${selected.name} numbers should be ${phoneMeta.min === phoneMeta.max
        ? `${phoneMeta.min} digits`
        : `${phoneMeta.min}–${phoneMeta.max} digits`}. Example: ${phoneMeta.example}`;
    }
    return null;
  }

  async function submit() {
    setError(null);
    setLinkedAccount(false);
    const localErr = _validate();
    if (localErr) {
      setError(localErr);
      toast.error(localErr);       // defense in depth
      return;
    }
    setSubmitting(true);
    try {
      // Tag where the identity capture was triggered FROM so the founder
      // dashboard can compute identity_capture_after_review and
      // identity_capture_after_pattern_question rates from audit events.
      let trigger_source = "header";
      try {
        const last = (() => {
          // Look up the last identity-nudge tag we passed in localStorage —
          // set by onNudgeClick in Chat.jsx. Quick + dirty.
          return localStorage.getItem("gully_last_identity_trigger");
        })();
        if (last) trigger_source = last;
      } catch { /* noop */ }
      const { data } = await api.post("/web/identity", {
        session_id: sessionId,
        phone: phone.trim(),
        country,
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        instagram: instagram.trim() || undefined,
        trigger_source,
      });
      // Store JWT so the user is immediately "logged in" for routes that
      // require auth (e.g. /upgrade, /api/billing/checkout). Phone identity
      // is the single source of truth — no email/password needed.
      if (data?.token) {
        try { await setToken(data.token); } catch { /* noop */ }
      }
      // XX.294 — VERIFY PERSISTENCE. Don't trust a 200 alone (past
      // regression: sheet closed but /me still showed "Add phone").
      // We call /auth/me with the new bearer and confirm the server
      // actually authenticates us. If not, fall to the inline error
      // surface — never present false success.
      let verifiedUserId = null;
      try {
        const me = await api.get("/auth/me");
        verifiedUserId = me?.data?.user_id || null;
      } catch { /* verify path failed */ }
      if (!verifiedUserId) {
        const msg = "Save couldn't be confirmed. Please try again in a moment.";
        setError(msg);
        toast.error(msg);
        api.post("/web/track", {
          session_id: sessionId,
          event: "identity_capture_persistence_unverified",
          payload: { country, had_token: !!data?.token },
        }).catch(() => {});
        return;
      }
      try { localStorage.removeItem("gully_last_identity_trigger"); } catch { /* noop */ }
      onLinked?.(true);
    } catch (e) {
      const rawDetail = e?.response?.data?.detail || "";
      const status = e?.response?.status || null;
      // Card 4 — Detect the XX.294 linked-account condition. Backend
      // (web_chat.py L12181) returns HTTP 409 with detail beginning
      // "This number is already linked to an account…". We match on
      // BOTH status + a stable substring so a message rewording later
      // still catches by status. On match, we render an actionable
      // "Continue with Google" CTA directly beneath the inline error
      // so the user can immediately recover their existing identity
      // through the existing Iter 54 Google OAuth flow.
      const isLinkedAccount =
        status === 409 &&
        typeof rawDetail === "string" &&
        rawDetail.toLowerCase().includes("already linked");
      if (isLinkedAccount) setLinkedAccount(true);
      // Iter XX.72.6 — never surface backend internals ("session not
      // found", "not_authenticated", stack traces). Show only messages
      // the backend explicitly authored as user-facing (they end with
      // punctuation or contain a hint like "country code"). Anything
      // else falls back to a friendly generic string.
      const looksUserFacing = rawDetail && (
        /[.!?]$/.test(rawDetail) ||
        rawDetail.toLowerCase().includes("country") ||
        rawDetail.toLowerCase().includes("number")
      );
      const msg = looksUserFacing
        ? rawDetail
        : "Couldn't save that. Check the number?";
      // Funnel failure event — captures country + HTTP status so the founder
      // dashboard can spot bad regions or backend regressions.
      api.post("/web/track", {
        session_id: sessionId,
        event: "identity_capture_failed",
        payload: {
          country,
          status: e?.response?.status || null,
          reason: (rawDetail || "").slice(0, 120),
        },
      }).catch(() => {});
      // XX.294 — Inline error IS the primary surface. Toast is a
      // secondary redundancy (some layouts clip it on mobile Safari).
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center" data-testid="identity-sheet">
      <div className="relative w-full sm:max-w-md bg-[#111] border border-neutral-800 rounded-t-3xl sm:rounded-3xl p-6 max-h-[100dvh] overflow-y-auto sm:max-h-[90dvh]"
           style={{
             // Iter XX.287 — Bottom-sheet modal MUST reserve the persistent
             // <MobileBottomNav>'s measured height (published by the nav as
             // `--gully-bottom-nav-h`, includes iOS safe-area). Previously
             // this padding only accounted for the safe-area inset, so the
             // primary "Save my cricket journey" CTA at the bottom of the
             // sheet was sitting BEHIND the fixed nav (z-80 > sheet z-60)
             // on iPhone Safari at 375–430px. Falls back to the raw
             // safe-area inset when the nav isn't mounted (marketing /
             // desktop / native modal contexts) so the sheet still clears
             // the iOS home indicator.
             paddingBottom: "calc(1.5rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))"
           }}>
        {!gated && (
          <button onClick={onClose} className="absolute top-4 right-4 p-1 text-neutral-500 hover:text-neutral-300"
                  aria-label="Close" data-testid="identity-close">
            <X size={16}/>
          </button>
        )}
        <div className="text-base font-medium mb-1" data-testid="identity-sheet-title">
          {gated ? "Let's save your cricket journey" : "Save your cricket journey"}
        </div>
        <div className="text-sm text-neutral-400 mb-4">
          {gated
            ? "So Gully can remember your matches, connect your videos, and build your season over time."
            : "So Gully remembers your matches, videos and patterns next time you're back."}
        </div>

        {/* Country + phone — single composed input */}
        <div className="flex gap-2 mb-2">
          <CountryPicker selected={selected} onChange={setCountry} sessionId={sessionId} />
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 text-[15px] select-none"
                  data-testid="identity-dial-prefix">
              {selected.dial}
            </span>
            <input
              autoFocus inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={country ? phoneMeta.example : "Pick a country first"}
              className={`w-full bg-neutral-900 border rounded-xl pl-14 pr-4 py-3 text-[16px] placeholder-neutral-500 focus:outline-none focus:border-neutral-600 ${
                error ? "border-red-800" : "border-neutral-800"
              }`}
              aria-invalid={!!error}
              aria-describedby={error ? "identity-error" : undefined}
              data-testid="identity-phone"
            />
          </div>
        </div>
        {/* XX.294 — Inline error surface. Renders inside the sheet DOM,
            cannot be occluded by the fixed bottom nav or off-screen
            toaster on mobile Safari. */}
        {error && (
          <div
            id="identity-error"
            role="alert"
            className="mb-2 px-1 text-[13px] leading-snug text-red-300"
            data-testid="identity-error"
          >
            {error}
          </div>
        )}
        {/* Card 4 — Actionable linked-account recovery. Only rendered
            when the backend returns the XX.294 409 "already linked"
            condition AND we're not inside a Capacitor native shell
            (parity with Login.jsx — Iter XX.181 hides Google on
            native). Reuses `startGoogleLogin` so the OAuth flow is
            identical to the Login page. `/chat` is used as the
            return_to so AuthCallback resolves the user back into
            their existing identity/history path. */}
        {linkedAccount && !isNative() && (
          <button
            type="button"
            onClick={() => {
              try { startGoogleLogin("/chat"); }
              catch { /* redirect can't be recovered locally */ }
            }}
            className="w-full mb-3 flex items-center justify-center gap-2 py-3 rounded-xl border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100 text-sm font-medium transition-colors"
            data-testid="identity-sign-in-google"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".7"/>
              <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity=".5"/>
              <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" opacity=".85"/>
            </svg>
            Continue with Google
          </button>
        )}
        <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
          Include your country code so Gully can match your previous chat history.
        </div>

        <input
          value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
          className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3 text-[16px] placeholder-neutral-500 focus:outline-none focus:border-neutral-600 mb-2"
          data-testid="identity-name"
        />
        <input
          type="email" autoComplete="email" inputMode="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional — for weekly recap)"
          className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3 text-[16px] placeholder-neutral-500 focus:outline-none focus:border-neutral-600 mb-2"
          data-testid="identity-email"
        />
        <input
          value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="Instagram handle (optional)"
          className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3 text-[16px] placeholder-neutral-500 focus:outline-none focus:border-neutral-600 mb-4"
          data-testid="identity-instagram"
        />
        <div className="flex gap-2">
          {!gated && (
            <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-neutral-800 text-neutral-300 hover:bg-neutral-900" data-testid="identity-skip">
              Not now
            </button>
          )}
          <button onClick={submit} disabled={submitting}
                  className={`${gated ? "w-full" : "flex-1"} py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 font-medium`}
                  data-testid="identity-save">
            {submitting ? "Saving…" : (gated ? "Save my cricket journey" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter XX.221 — Connect Your Cricket offer wrapper.
//
// Renders the ConnectCricketCard exactly once when:
//   · user has had ≥3 messages (ConversationDepthPolicy match with XX.81 reveal timing)
//   · no external cricket profile linked yet
//   · card has not been dismissed this browser session
//   · feature flag is enabled server-side (`fetchConnection()` returns null
//     when the /api/cricket routes 404 — production stays silent)
//
// Deliberately BELOW the messages stream + ABOVE the composer, so it can
// never interrupt an in-flight coaching reply. Anonymous users (no JWT)
// skip the fetch entirely — anonymous can't link a profile.
// ---------------------------------------------------------------------------
function ConnectCricketOffer({ messages }) {
  const [shouldShow, setShouldShow] = React.useState(false);
  const [checked, setChecked] = React.useState(false);

  const userTurns = messages.filter((m) => m.role === "user").length;

  React.useEffect(() => {
    if (checked) return;
    if (userTurns < 3) return;
    if (isSessionDismissed()) { setChecked(true); return; }
    if (!hasToken()) { setChecked(true); return; }
    let cancelled = false;
    (async () => {
      const conn = await fetchConnection();
      if (cancelled) return;
      // conn === null → feature disabled server-side; skip permanently.
      if (!conn) { setChecked(true); return; }
      if (conn.connected) { setChecked(true); return; }
      setShouldShow(true);
      setChecked(true);
    })();
    return () => { cancelled = true; };
  }, [userTurns, checked]);

  if (!shouldShow) return null;
  return (
    <div className="px-4 max-w-3xl mx-auto w-full" data-testid="connect-cricket-offer-slot">
      <ConnectCricketCard
        variant="offer" surface="chat_third_turn"
        onDismiss={() => setShouldShow(false)}
        onConnected={() => {
          setShouldShow(false);
          // Iter XX.221 — notify the header pill so it flips to
          // "🏏 My cricket ✓" immediately after a successful confirm,
          // without waiting for a route change.
          window.dispatchEvent(new Event(CRICKET_CONNECTION_CHANGED));
        }}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter XX.221 — My Cricket header pill.
//
// Requirements (founder-locked):
//   · Only appears when genuinely connected (backend `/cricket/connection`
//     returns `{connected: true, profile}`).
//   · Hidden completely when `connect_your_cricket_enabled` is OFF (the API
//     returns 404, `fetchConnection()` yields null).
//   · Clicking opens the lightweight `YourCricketPanel` in a bottom-sheet
//     modal — same management surface as `/me`, but reachable without
//     leaving the chat. `YourCricketPanel` handles disconnect internally.
//   · Anonymous users (no JWT) skip the fetch — anonymous can't be
//     connected.
//   · Refreshes when the ConnectCricketOffer wrapper fires `onConnected`
//     via a shared cross-component event.
//   · Never overcrowds — same pill style as XX.31 Memory/Season/Plus.
// ---------------------------------------------------------------------------
const CRICKET_CONNECTION_CHANGED = "gully_cricket_connection_changed";

function MyCricketPill() {
  const [connected, setConnected] = React.useState(false);
  const [inningsCount, setInningsCount] = React.useState(0);
  const [checked, setChecked] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!hasToken()) { setChecked(true); return; }
    const conn = await fetchConnection();
    if (!conn) { setChecked(true); setConnected(false); setInningsCount(0); return; }
    setConnected(!!conn.connected);
    const rec = await fetchRecordSummary();
    setInningsCount(rec?.innings_count || 0);
    setChecked(true);
  }, []);

  React.useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener(CRICKET_CONNECTION_CHANGED, handler);
    return () => window.removeEventListener(CRICKET_CONNECTION_CHANGED, handler);
  }, [refresh]);

  // Iter XX.222 — semantics changed: pill visible when EITHER a provider
  // profile is linked OR the player has ≥1 confirmed performance from
  // any source (conversation, scorecard OCR, manual). Tick only when
  // ≥1 performance — a bare provider link no longer implies "Gully
  // knows your cricket".
  if (!checked) return null;
  if (!connected && inningsCount === 0) return null;

  const hasRecord = inningsCount > 0;
  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1 ${
          hasRecord
            ? "text-emerald-200/90 hover:text-white bg-emerald-950/60 hover:bg-emerald-900/70 border-emerald-800/70 hover:border-emerald-500/80"
            : "text-neutral-300 hover:text-white bg-neutral-900/50 hover:bg-neutral-800/70 border-neutral-700/60 hover:border-neutral-500"
        }`}
        data-testid="chat-my-cricket-pill"
        data-has-record={hasRecord ? "1" : "0"}
        aria-label={hasRecord ? "My cricket — connected" : "My cricket"}
      >
        <span aria-hidden="true">🏏</span>
        <span className="hidden sm:inline">My cricket</span>
        <span className="sm:hidden">Cricket</span>
        {hasRecord && <span aria-hidden="true" className="text-emerald-400">✓</span>}
      </button>
      {sheetOpen && (
        <MyCricketSheet
          onClose={() => setSheetOpen(false)}
          onDisconnected={() => {
            setConnected(false);
            setSheetOpen(false);
            window.dispatchEvent(new Event(CRICKET_CONNECTION_CHANGED));
          }}
        />
      )}
    </>
  );
}

/** Bottom-sheet modal that reuses MyCricketPanel (reframed XX.222). */
function MyCricketSheet({ onClose, onDisconnected }) {
  return (
    <div
      className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-0 sm:px-4"
      data-testid="my-cricket-sheet"
      onClick={onClose}
    >
      <div
        className="bg-neutral-950 border-t sm:border border-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-4 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{
          // Iter XX.287 — Bottom-sheet modal MUST reserve the persistent
          // <MobileBottomNav>'s measured height so the close/action row
          // at the bottom of MyCricketPanel isn't occluded on mobile
          // web. Falls back to safe-area when nav isn't mounted.
          paddingBottom: "calc(1rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">My Cricket</h3>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-white text-sm"
            data-testid="my-cricket-sheet-close"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <MyCricketPanel />
        <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
          Manage from{" "}
          <Link
            to="/me?tab=knows"
            onClick={onClose}
            className="text-emerald-400 hover:text-emerald-300"
            data-testid="my-cricket-sheet-me-link"
          >
            Me → Knows
          </Link>
          .
        </p>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Iter XX.222 — Performance candidate poller.
//
// After each user turn, polls `/api/performances/pending` once (fires
// after the assistant reply has landed). Renders the FIRST pending
// candidate as a `PerformanceConfirmCard` under the composer area.
// Feature-flagged: `fetchPendingPerformances()` returns null on 404 so
// production stays silent.
// ---------------------------------------------------------------------------
function PerformanceCandidateSlot({ messages }) {
  const [candidate, setCandidate] = React.useState(null);
  const [checkedForMessage, setCheckedForMessage] = React.useState(null);

  const userTurnCount = messages.filter((m) => m.role === "user").length;
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");

  React.useEffect(() => {
    if (!hasToken()) return;
    if (userTurnCount === 0) return;
    const key = `${userTurnCount}:${lastAssistant?.id || ""}`;
    if (checkedForMessage === key) return;
    if (!lastAssistant) return;
    let cancelled = false;
    // Small delay so the extractor + verifier task finishes writing.
    const t = setTimeout(async () => {
      const rows = await fetchPendingPerformances();
      if (cancelled) return;
      setCheckedForMessage(key);
      if (rows && rows.length) setCandidate(rows[0]);
    }, 1200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [userTurnCount, lastAssistant, checkedForMessage]);

  if (!candidate) return null;
  return (
    <div className="px-4 max-w-3xl mx-auto w-full"
          data-testid="performance-candidate-slot">
      <PerformanceConfirmCard
        candidate={candidate}
        onResolved={() => {
          setCandidate(null);
          window.dispatchEvent(new Event(CRICKET_CONNECTION_CHANGED));
        }}
      />
    </div>
  );
}
